import React, { useState, useEffect, useContext, useRef, forwardRef, useImperativeHandle } from "react";
import ReadOnlyExemptButton from "../../components/ReadOnlyExemptButton";
import { useTranslation } from "react-i18next";
import {
  Box, Grid, GridItem, Input, Text, IconButton, Flex, Button, ButtonGroup, Select,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, useDisclosure,
  Radio, RadioGroup, Stack, HStack, FormControl, FormErrorMessage, Textarea,
  Checkbox,
} from "@chakra-ui/react";
import Decimal from 'decimal.js';
import { FaPlusCircle } from "react-icons/fa";
import { FaCircleMinus } from "react-icons/fa6";
import { RiDeleteBin6Fill } from "react-icons/ri";
import HelpIcon from "./HelpIcon";
import { useToast } from "@chakra-ui/react";
import { WebAPIContext } from '../../contexts/WebAPIContext';
import type {
  ProjectWithRdsData, PartNo,
  QuantityAndPriceMonthTable, MoldMonthTable, MoldMonthTableByProject,
  DevelopmentMonthCostTable, DevelopmentMonthCostTableByProject, EquipmentMonthCostTable, EquipmentMonthCostTableByProject,
  YearData, YearPriceData, MonthData, MonthPriceData,
  ProjectContextType, PagePermissions,
  ConfirmorInfo, PriceRows,
} from '../../contexts/types';
import { CustomNumberInput } from "./CustomNumberInput";
import { formatNumber, hasDecimal, getVisibleMonths, sanitizeFileName, resolveDisplayPartNo } from '../../utils/utils';
import { getConfirmor, updateConfirm } from "../../utils/workflow";
import { WorkflowId, Messages, EquipmentUsageType, PurposeType, isSimpleApplicationForm } from "../../contexts/constants";
import { useAppSelector } from "../../store/hooks";
import ConfirmorList from "./ConfirmorList";
import ConfirmorDescription from "./ConfirmorDescription";

import GoogleAPI from "../../utils/GoogleAPI";
import {
  quantityAndPriceToSpreadSheetData,
  spreadSheetDataToQuantityAndPrice,
  validateQuantityAndPriceHeader,
  extendQuantityAndPriceDataForEOL,
  getMaxYearIndexByEOL,
} from '../../hooks';

// コンテキスト
import { ProjectContext, getPermissions } from "./InvestmentScreeningDetail";
import CurrencyList from "./CurrencyList";

// -----------------------------
// インタフェース
// -----------------------------
export interface QuantityAndPriceHandle {
  getSnapshot: () => Record<string, unknown>;
  /** 戻り値: 案件に存在しない PartNo のため読み込みを除外した PartNo 一覧（無ければ空配列） */
  applySnapshot: (data: Record<string, unknown>) => string[];
}

interface QuantityAndPriceProps {
  projectId: string;
  /** 設備用途区分 */
  equipmentUsageType: string;
  /** 親から渡される初期目的（projectData取得前の判定用） */
  initialPurpose?: string;
  /** 親から渡される初期申請書様式（projectData取得前の判定用） */
  initialApplicationFormType?: string;
}

// 数量・単価テーブル(DB登録時)
interface QuantityAndPriceTableRegist {
  id: string;
  partNo: string;
  years: number[];
  data: {
    [key: number]: YearData
  };
  moldRadioValue: string;
  developmentRadioValue: string;
  updateDate?: string | null,
  currencyAndRatio: string,
  newProductRatio?: number | null,
  /** セット販売での単価・数量入力フラグ */
  isSetSalesInput?: boolean;
}

// PartNoInput型を定義
interface PartNoRegisterInput {
  projectId: string;
  currencyId: string;
  quantityAndPrice: string;
  moldSalesByProject: string;
  developmentSalesByProject: string;
  equipmentSalesByProject: string;
  developmentSalesByPartNo: string;
  moldSalesByPartNo: string;
  quantityPriceFreeText?: string;
  businessContinuity?: string;
  updateDate: string | null | undefined;
}

// Confirmor型を定義
interface Confirmor {
  quantityAndPrice?: {
    companyDivision?: string[];
    salesDivision?: string[];
  };
}

// -----------------------------
// 定数
// -----------------------------
// テーブルタイプ
const TABLE_TYPES = {
  PART_NO_TABLES: "partNoTables",  // partNo
  MOLD_TABLES: "moldTables", // 金型
  DEVELOPMENT_COST_TABLES: "developmentCostTables", // 開発費
  EQUIPMENT_COST_TABLES: "equipmentCostTables" // 設備費
} as const;
type TableType = 'partNoTables' | 'moldTables' | 'developmentCostTables' | 'equipmentCostTables';

// PartNo入力ラジオボタン
const RADIO_VALUES = {
  PART_INPUT: "1",
  PART_NOT_INPUT: "0"
} as const;

// 入力項目値の基準幅
const BASE_WIDTH = 166;

// 月数
const MONTH_COUNT = 12;

// 精度を50桁に設定
// 製品売上高(25桁)×PartNo運用MAX(10個) = 35桁＋α
Decimal.set({ precision: 50 });

/**
 * 数量・単価画面
 * @returns
 */
const QuantityAndPrice = forwardRef<QuantityAndPriceHandle, QuantityAndPriceProps>(({ projectId, equipmentUsageType, initialPurpose, initialApplicationFormType }, ref) => {

  const { t } = useTranslation();
  const toast = useToast();
  const WebAPI = useContext(WebAPIContext);

  /** 汎用設備かどうか */
  const isGeneralPurpose = equipmentUsageType === EquipmentUsageType.GeneralPurpose.code;

  /** 画面タイトル */
  const TITLE = isGeneralPurpose
    ? t('features.InvestmentScreening.QuantityAndPrice.titleAssumed')
    : t('features.InvestmentScreening.QuantityAndPrice.titleSales');

  /** 汎用設備用ラベル */
  const LABEL_CURRENCY = isGeneralPurpose
    ? t('features.InvestmentScreening.QuantityAndPrice.label.currencyAssumed')
    : t('features.InvestmentScreening.QuantityAndPrice.label.currencySales');
  const LABEL_QUANTITY = isGeneralPurpose
    ? t('features.InvestmentScreening.QuantityAndPrice.label.quantityAssumed')
    : t('features.InvestmentScreening.QuantityAndPrice.label.quantitySales');
  const LABEL_PRICE = isGeneralPurpose
    ? t('features.InvestmentScreening.QuantityAndPrice.label.priceAssumed')
    : t('features.InvestmentScreening.QuantityAndPrice.label.priceSales');

  /** Googleドライブ 数量・単価スプレッドシートフォルダパス */
  const QUANTITY_AND_PRICE_FOLDER_PATH = "CEAS/SpreadSheet/QuantityAndPrice";

  /** GoogleSpreadボタン制御 */
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());

  /** Google API汎用処理クラス */
  const googleAPI: GoogleAPI = new GoogleAPI();

  /** 入力モード */
  const [inputModes, setInputModes] = useState<{ id: string; mode: "google" | "manual" }[]>([]);

  /** GoogleSpread管理 */
  const [spreadSheetWindows, setSpreadSheetWindows] = useState<Record<string, Window | null>>({});

  // -----------------------------
  // useRef
  // -----------------------------
  const quantityAndPriceTableRef = useRef<(HTMLDivElement | null)[]>([]);
  const partNoMoldPriceChildTableRef = useRef<Record<string, (HTMLDivElement | null)[]>>({});
  const developmentCostChildTableRef = useRef<Record<string, (HTMLDivElement | null)[]>>({});
  const moldTableRef = useRef<(HTMLDivElement | null)[]>([]);
  const developmentCostTableRef = useRef<(HTMLDivElement | null)[]>([]);
  const equipmentCostTableRef = useRef<(HTMLDivElement | null)[]>([]);
  const isFirstRender = useRef(true);
  const refs: Record<string, React.RefObject<HTMLButtonElement>> = {
    companyDivision: useRef<HTMLButtonElement>(null),
    companyHeadDivision: useRef<HTMLButtonElement>(null),
    salesDivision: useRef<HTMLButtonElement>(null),
    procurementDivision: useRef<HTMLButtonElement>(null),
  };

  // -----------------------------
  // useState
  // -----------------------------
  // 単価・数量テーブル
  const [quantityAndPriceTables, setQuantityAndPriceTables] = useState<QuantityAndPriceMonthTable[]>([]);
  // 金型テーブル
  const [moldTables, setMoldTables] = useState<MoldMonthTableByProject[]>([]);
  // 開発費テーブル
  const [developmentCostTables, setDevelopmentCostTables] = useState<DevelopmentMonthCostTableByProject[]>([]);
  // 設備費テーブル
  const [equipmentCostTables, setEquipmentCostTables] = useState<EquipmentMonthCostTableByProject[]>([]);
  // Projectデータ
  const [projectData, setProjectData] = useState<ProjectWithRdsData>();

  // 簡易モード判定（projectData未取得時は親から渡された初期値で判定 → ちらつき防止）
  const isSimpleMode = isSimpleApplicationForm(
    projectData?.purpose ?? initialPurpose,
    projectData?.applicationFormType ?? initialApplicationFormType,
  );
  // PartNoデータ
  const [partNoData, setPartNoData] = useState<PartNo[]>([]);
  // 選択通貨
  const [selectedCurrency, setSelectedCurrency] = useState<{ tableId: string; partNo: string; id: string; name: string }[]>([]);
  // 基準年度
  const [baseStartYear, setBaseStartYear] = useState<number>(0);
  // スクロールバー移動用
  const [updatedTableId, setUpdatedTableId] = useState<string | null>(null);
  // エラー情報
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  // どの部署の確認かを表す
  const [confirmSource, setConfirmSource] = useState<string>("");
  // 確認担当者
  const [confirmor, setConfirmor] = useState<Confirmor | null>(null);
  // 確認ボタン横幅
  const [buttonWidths, setButtonWidths] = useState<Record<string, number>>({});
  // 確認ボタン下担当者名
  const [names, setNames] = useState<Record<string, ConfirmorInfo[]>>({
    companyDivision: [],
    salesDivision: [],
  });
  // 単価行データ
  const [priceRows, setPriceRows] = useState<{ id: string; rows: PriceRows[] }[]>([]);
  // 金型価格行データ
  const [moldPriceRows, setMoldPriceRows] = useState<{ id: string; rows: PriceRows[] }[]>([]);
  // 開発費価格行データ
  const [developmentCostPriceRows, setDevelopmentCostPriceRows] = useState<{ id: string; rows: PriceRows[] }[]>([]);
  // 設備費価格行データ
  const [equipmentCostPriceRows, setEquipmentCostPriceRows] = useState<{ id: string; rows: PriceRows[] }[]>([]);
  // PartNo金型価格行データ
  const [partNoMoldPriceRows, setPartNoMoldPriceRows] = useState<{ parentTableId: string; id: string; rows: PriceRows[] }[]>([]);
  // PartNo開発費価格行データ
  const [partNoDevelopmentCostPriceRows, setPartNoDevelopmentCostPriceRows] = useState<{ parentTableId: string; id: string; rows: PriceRows[] }[]>([]);

  /** 権限用 画面パーミッション */
  const [pagePermissions, setPagePermissions] = useState<PagePermissions>();
  /**自由記入欄 */
  const [quantityPriceFreeText, setQuantityPriceFreeText] = useState<string>();
  // 事業の継続性の有無
  const [businessContinuity, setBusinessContinuity] = useState<string>(RADIO_VALUES.PART_INPUT);
  // -----------------------------
  // 権限情報
  // -----------------------------
  const { userPermissions, reviewDate, currencyData } = useContext(ProjectContext) as ProjectContextType;

  /** スプレッドシートウィンドウ管理（key：スプレッドシートID） */
  const [spreadSheetWindowss, setSpreadSheetWindowss] = useState<Window | null>(null);

  // -----------------------------
  // ダイアログ制御
  // -----------------------------
  const { isOpen, onOpen, onClose } = useDisclosure(); // モーダルの状態
  const [modalTitle, setModalTitle] = useState(""); // モーダルのタイトル

  // 一時保存対象（8項目）: quantityAndPriceTables / moldTables / developmentCostTables /
  //   equipmentCostTables / selectedCurrency / baseStartYear / quantityPriceFreeText / businessContinuity
  //   ※ priceRows 系 6種は useEffect で自動再生成される派生値なので保存しない
  useImperativeHandle(ref, () => ({
    getSnapshot: () => {
      const data: Record<string, unknown> = {};
      if (quantityAndPriceTables.length > 0) data.quantityAndPriceTables = quantityAndPriceTables;
      if (moldTables.length > 0) data.moldTables = moldTables;
      if (developmentCostTables.length > 0) data.developmentCostTables = developmentCostTables;
      if (equipmentCostTables.length > 0) data.equipmentCostTables = equipmentCostTables;
      // priceRows 系は useEffect で自動再生成される派生値なので保存しない
      if (selectedCurrency.length > 0) data.selectedCurrency = selectedCurrency;
      if (baseStartYear !== 0) data.baseStartYear = baseStartYear;
      if (quantityPriceFreeText !== undefined && quantityPriceFreeText !== "") data.quantityPriceFreeText = quantityPriceFreeText;
      if (businessContinuity !== RADIO_VALUES.PART_INPUT) data.businessContinuity = businessContinuity;
      return data;
    },
    applySnapshot: (data: Record<string, unknown>) => {
      // テーブルをスナップショット→現在プロジェクト構造に絞り込む
      // ①一時保存データの行は「id」ではなく「PartNo（＋金型/開発費/設備費名）」で現在データと突き合わせる。
      //   ※ 一時保存後に画面更新すると、quantityAndPriceTables 等は DB の内容で再構築されるため、
      //     未保存の編集中の id は現在の state に存在しない（id は保存のたびに再生成されうる）。
      //     id 一致で突き合わせると、未保存の編集内容が毎回失われてしまう。
      //     PartNo は案件内で意味を持つ安定したキーのため、こちらで一致判定する。
      //   - 案件に存在しない PartNo（削除された等）の一時保存行は読み込まず除外する。
      //   - 同一 PartNo の既存行があれば、その行の id・年度範囲を引き継いで上書きする。
      //   - 同一 PartNo の既存行が無ければ（未保存の新規行）、新たに id を採番して追加する。
      // ②最終年度のみ EOL 後の月の値をゼロ化（月エントリは維持、値のみ 0 に）
      const projectEndDate = projectData?.projectEndDate;
      const eolYear = projectEndDate ? parseInt(projectEndDate.split("-")[0], 10) : null;
      const eolMonth = projectEndDate ? parseInt(projectEndDate.split("-")[1], 10) : null;

      // 年度・月レベルで1テーブル分を絞り込む共通関数
      // ※ 返す years は「実際に data が存在する年度」に限定する（存在しない年度を years に含めると
      //   後続処理（generateRowsFromTable 等）が table.data[year] に対して undefined 参照しクラッシュするため）
      const trimSingleTable = (t: any, validYears: Set<number>): any => {
        const lastYear = validYears.size > 0 ? Math.max(...validYears) : null;
        const trimmedData: Record<number, any> = {};
        for (const yearStr of Object.keys(t.data ?? {})) {
          const y = Number(yearStr);
          if (!validYears.has(y)) continue;
          const yearData = t.data[y];
          if (y === lastYear && yearData?.months && eolYear !== null && eolMonth !== null) {
            const trimmedMonths: Record<number, any> = {};
            for (const [idxStr, monthData] of Object.entries(yearData.months as Record<number, any>)) {
              const calMonth = monthData.month as number;
              const calYear = calMonth >= 4 ? baseStartYear + y - 1 : baseStartYear + y;
              const isAfterEol = calYear > eolYear || (calYear === eolYear && calMonth > eolMonth);
              trimmedMonths[Number(idxStr)] = isAfterEol
                ? Object.fromEntries(Object.entries(monthData).map(([k, v]) => [k, (typeof v === 'number' && k !== 'month') ? 0 : v]))
                : monthData;
            }
            trimmedData[y] = { ...yearData, months: trimmedMonths };
          } else {
            trimmedData[y] = yearData;
          }
        }
        const resultYears = Object.keys(trimmedData).map(Number).sort((a, b) => a - b);
        return { ...t, years: resultYears, data: trimmedData };
      };

      // 現在の state に対応する PartNo が無い（＝一時保存後に追加されまだ DB 未保存の）
      // 新規行用のフォールバック年度。EOL予定日から現時点の年度レンジを再計算する。
      const globalValidYears = new Set(getInitialYearsByEOL());

      // 案件に現在登録されている PartNo 一覧（存在しない PartNo の一時保存行を除外するため）
      // ※ partNoData 未取得（空）の間は誤って全除外してしまわないよう、チェック自体をスキップする
      const validPartNoSet = new Set(
        (partNoData ?? []).map(p => p.partNo).filter((p): p is string => !!p)
      );

      let newIdSeq = 0;
      const generateNewId = (prefix: string) => `${prefix}-${Date.now()}-${newIdSeq++}`;

      // 案件に存在しない PartNo のため除外した一時保存行の PartNo（トースト表示用）
      const skippedPartNos = new Set<string>();

      // snapshot と現在データを PartNo（必要に応じて name フィールドも加味）で突き合わせて絞り込む
      const trimTables = (
        snapshot: any[],
        current: {
          id: string;
          partNo?: string;
          years: number[];
          partNoMoldTables?: any[];
          partNoDevelopmentCostTables?: any[];
        }[],
        opts?: { nameField?: string; idPrefix: string }
      ) => {
        const nameField = opts?.nameField;
        const idPrefix = opts?.idPrefix ?? "table";
        const keyOf = (t: any) => nameField ? `${t?.partNo ?? ""}::${t?.[nameField] ?? ""}` : (t?.partNo ?? "");
        const currentMap = new Map(current.map(t => [keyOf(t), t]));

        const trimNestedList = (list: any[], existingList: any[] | undefined, nameKey: string, idPrefixNested: string) => {
          const existingMap = new Map((existingList ?? []).map((n: any) => [n?.[nameKey] ?? "", n]));
          return (list as any[]).map((nested: any) => {
            const nestedExisting = existingMap.get(nested?.[nameKey] ?? "");
            const nestedValidYears = nestedExisting ? new Set<number>(nestedExisting.years) : globalValidYears;
            const trimmedNested = trimSingleTable(nested, nestedValidYears);
            trimmedNested.id = nestedExisting ? nestedExisting.id : generateNewId(idPrefixNested);
            return trimmedNested;
          });
        };

        const result: any[] = [];
        for (const t of snapshot) {
          // 案件に存在しない PartNo の一時保存行は読み込まず除外する
          if (validPartNoSet.size > 0 && t?.partNo && !validPartNoSet.has(t.partNo)) {
            skippedPartNos.add(t.partNo);
            continue;
          }
          const existing = currentMap.get(keyOf(t));
          const validYears = existing ? new Set(existing.years) : globalValidYears;
          const trimmed = trimSingleTable(t, validYears);
          // 既存行があればその id を維持（画面上の既存行への上書きとして反映させる）
          // 既存行が無い場合（一時保存後に追加されまだ DB 未保存の新規行）は id を採番し直す
          trimmed.id = existing ? existing.id : generateNewId(idPrefix);
          // PartNo 別金型・開発費回収テーブル（QuantityAndPriceMonthTable 内に入れ子）も
          // moldName / developmentCostName で突き合わせて絞り込む
          if (Array.isArray(trimmed.partNoMoldTables)) {
            trimmed.partNoMoldTables = trimNestedList(
              trimmed.partNoMoldTables, existing?.partNoMoldTables, "moldName", "partNoMold"
            );
          }
          if (Array.isArray(trimmed.partNoDevelopmentCostTables)) {
            trimmed.partNoDevelopmentCostTables = trimNestedList(
              trimmed.partNoDevelopmentCostTables, existing?.partNoDevelopmentCostTables, "developmentCostName", "partNoDevelopmentCost"
            );
          }
          result.push(trimmed);
        }
        return result;
      };
      let newQuantityAndPriceTables: QuantityAndPriceMonthTable[] | null = null;
      if (data.quantityAndPriceTables !== undefined) {
        newQuantityAndPriceTables = trimTables(data.quantityAndPriceTables as any[], quantityAndPriceTables, { idPrefix: "quantityAndPrice" }) as QuantityAndPriceMonthTable[];
        setQuantityAndPriceTables(newQuantityAndPriceTables);
      }
      if (data.moldTables !== undefined)
        setMoldTables(trimTables(data.moldTables as any[], moldTables, { nameField: "moldName", idPrefix: "mold" }) as MoldMonthTableByProject[]);
      if (data.developmentCostTables !== undefined)
        setDevelopmentCostTables(trimTables(data.developmentCostTables as any[], developmentCostTables, { nameField: "developmentCostName", idPrefix: "developmentCost" }) as DevelopmentMonthCostTableByProject[]);
      if (data.equipmentCostTables !== undefined)
        setEquipmentCostTables(trimTables(data.equipmentCostTables as any[], equipmentCostTables, { nameField: "equipmentCostName", idPrefix: "equipmentCost" }) as EquipmentMonthCostTableByProject[]);
      // priceRows 系は set しない（テーブル set 後に useEffect が自動再生成する）
      if (data.selectedCurrency !== undefined) {
        // selectedCurrency の tableId はスナップショット時点の id のままなので、
        // 直前に id を振り直した quantityAndPriceTables に合わせて PartNo 経由で付け替える
        const partNoToNewId = new Map((newQuantityAndPriceTables ?? []).map(t => [t.partNo, t.id]));
        const remappedSelectedCurrency = (data.selectedCurrency as { tableId: string; partNo: string; id: string; name: string }[])
          .map(item => {
            const newId = partNoToNewId.get(item.partNo);
            return newId ? { ...item, tableId: newId } : item;
          });
        setSelectedCurrency(remappedSelectedCurrency);
      }
      if (data.baseStartYear !== undefined) setBaseStartYear(data.baseStartYear as number);
      if (data.quantityPriceFreeText !== undefined) setQuantityPriceFreeText(data.quantityPriceFreeText as string);
      if (data.businessContinuity !== undefined) setBusinessContinuity(data.businessContinuity as string);

      // 案件に存在しない PartNo のため除外した行があれば、ユーザーに通知する
      if (skippedPartNos.size > 0) {
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.QuantityAndPrice.message.tempLoadPartNoNotFound', {
            partNos: Array.from(skippedPartNos).join(", "),
          }),
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
      return Array.from(skippedPartNos);
    },
  }));

  const handleModalOpen = (title: string, e: React.MouseEvent<HTMLButtonElement>) => {
    // ボタンのフォーカスを外す
    e.currentTarget.blur();
    // 部署名保存
    const { name } = e.target as HTMLButtonElement;
    setConfirmSource(name);
    if (validateForm()) {

      // 年度データを更新
      // PartNoテーブルのデータ更新
      setQuantityAndPriceTables((prevTables) => {
        return prevTables.map((table) => {
          const updatedData = { ...table.data };
          // 数量、売上合計
          updatePartNoTotals(updatedData);
          // PartNoに紐づく金型
          const updatedPartNoMoldTables = updateMoldTables(table.partNoMoldTables);
          // PartNoに紐づく開発費
          const updatedPartNoDevelopmentCostTables = updateDevelopmentCostTables(table.partNoDevelopmentCostTables);

          return {
            ...table,
            data: updatedData,
            partNoMoldTables: updatedPartNoMoldTables,
            partNoDevelopmentCostTables: updatedPartNoDevelopmentCostTables,
          };
        });
      });

      // 金型テーブルのデータ更新
      setMoldTables((prevTables) => {
        return updateTablesData(prevTables, updatePriceTotals);
      });

      // 開発費テーブルのデータ更新
      setDevelopmentCostTables((prevTables) => {
        return updateTablesData(prevTables, updatePriceTotals);
      });

      // 設備費テーブルのデータ更新
      setEquipmentCostTables((prevTables) => {
        return updateTablesData(prevTables, updatePriceTotals);
      });

      setModalTitle(title);
      onOpen();
    }
  };

  // -----------------------------
  // テーブル操作関連処理
  // -----------------------------
  // 年度を計算
  const getFiscalYear = (baseYear: number, yearIndex: number) => {
    return baseYear + yearIndex;
  };

  const employeeId = useAppSelector((state) => state.employee.employeeId);
  const divisionId = useAppSelector((state) => state.employee.divisionId);

  /**
   * 深い階層のフィールドをイミュータブルに更新する
   *
   * @param obj 更新対象のオブジェクト
   * @param path 更新する階層のパス（例: ['data', year, 'months', month, field]）
   * @param value 更新する値
   */
  const updateNestedField = (obj: any, path: (string | number)[], value: any): any => {
    if (path.length === 0) return value;

    const [key, ...restPath] = path;

    if (Array.isArray(obj)) {
      // IDに基づいて更新対象を特定
      return obj.map((item) =>
        item.id === key ? updateNestedField(item, restPath, value) : item
      );
    }

    return {
      ...obj,
      [key]: updateNestedField(obj[key] || {}, restPath, value),
    };
  };

  /**
   * PartNo 月毎の数量、単価の初期化
   *
   * @param startMonth 開始月(省略時は4月から)
   * @returns 4～3月の配列データ
   */
  const initializePartNoMonths = (startMonth: number = 4, isFirstYear: boolean = false): { [key: number]: MonthData } => {
    const months: { [key: number]: MonthData } = {};

    // 初年度の場合はプロジェクトの開始月を設定
    if (isFirstYear && projectData?.projectStartDate) {
      // 初年度の場合、プロジェクトの開始月と終了月で処理
      const startDateMonth = parseInt(projectData.projectStartDate.split("-")[1], 10); // 開始月

      // 月次データを2月から3月まで動的に生成
      let index = 1; // 年度中の連番として使用
      for (let month = startDateMonth; month <= 3; month++) {
        months[index] = {
          month: month, // 実際の月番号
          quantity: 0,  // 初期化
          price: 0,     // 初期化
        };
        index++;
      }
    } else {
      // 通常年度（4月から翌年3月まで）
      let index = 1;
      for (let month = startMonth; month <= MONTH_COUNT; month++) {
        months[index] = {
          month: month,
          quantity: 0,
          price: 0,
        };
        index++;
      }
      for (let month = 1; month < startMonth; month++) {
        months[index] = {
          month: month,
          quantity: 0,
          price: 0,
        };
        index++;
      }
    }

    return months;
  };

  // 金型、開発費 価格の初期化
  const initializePriceMonths = (startMonth: number = 4, isFirstYear: boolean = false): { [key: number]: MonthPriceData } => {
    const months: { [key: number]: MonthPriceData } = {};
    // 初年度の場合はプロジェクトの開始月を設定
    if (isFirstYear && projectData?.projectStartDate) {
      // 初年度の場合、プロジェクトの開始月と終了月で処理
      const startDateMonth = parseInt(projectData.projectStartDate.split("-")[1], 10); // 開始月

      // 月次データを2月から3月まで動的に生成
      let index = 1; // 年度中の連番として使用
      for (let month = startDateMonth; month <= 3; month++) {
        months[index] = {
          month: month, // 実際の月番号
          price: 0,     // 初期化
        };
        index++;
      }
    } else {
      // 通常年度（4月から翌年3月まで）
      let index = 1;
      for (let month = startMonth; month <= MONTH_COUNT; month++) {
        months[index] = {
          month: month,
          price: 0,
        };
        index++;
      }
      for (let month = 1; month < startMonth; month++) {
        months[index] = {
          month: month,
          price: 0,
        };
        index++;
      }
    }
    return months;
  };

  /**
   * EOL予定日から初期年次配列を生成する
   * @returns EOL予定日までの年次配列（EOL未設定の場合は[1,2,3,4,5]）
   */
  const getInitialYearsByEOL = (): number[] => {
    const defaultYears = [1, 2, 3, 4, 5];

    if (!projectData?.projectEndDate || !projectData?.projectStartDate) {
      return defaultYears;
    }

    const projectEndDate = projectData.projectEndDate;
    const eolYear = parseInt(projectEndDate.split("-")[0], 10);
    const eolMonth = parseInt(projectEndDate.split("-")[1], 10);

    // 基準年度を計算
    const startDate = new Date(projectData.projectStartDate);
    const startYear = startDate.getFullYear();
    const startDateMonth = startDate.getMonth() + 1;
    const fiscalYearBase = startDateMonth >= 4 ? startYear : startYear - 1;

    // EOL予定日の年度を計算（4月～翌3月が1年度）
    const eolFiscalYear = eolMonth >= 4 ? eolYear : eolYear - 1;
    // 基準年度からの差分 + 1（1年目から始まるため）
    const maxYear = eolFiscalYear - fiscalYearBase + 1;

    if (maxYear <= 0) {
      return [1]; // 最低1年は表示
    }

    // maxYearまでの年次配列を生成（最大10年）
    const years: number[] = [];
    for (let i = 1; i <= Math.min(maxYear, 10); i++) {
      years.push(i);
    }
    return years;
  };

  /**
   * EOL予定日から初期データを生成する（パーツNo用）
   */
  const getInitialDataByEOL = (): { [key: number]: YearData } => {
    const years = getInitialYearsByEOL();
    const data: { [key: number]: YearData } = {};
    years.forEach(year => {
      data[year] = { quantity: 0, unitPrice: 0, salesPrice: 0, months: initializePartNoMonths() };
    });
    return data;
  };

  /**
   * EOL予定日から初期データを生成する（単価のみ用）
   */
  const getInitialPriceDataByEOL = (): { [key: number]: YearPriceData } => {
    const years = getInitialYearsByEOL();
    const data: { [key: number]: YearPriceData } = {};
    years.forEach(year => {
      data[year] = { price: 0, months: initializePriceMonths() };
    });
    return data;
  };

  // 数量・単価テーブル追加
  const addQuantityAndPriceTable: React.MouseEventHandler<HTMLButtonElement> = (event) => {

    event.currentTarget.blur();

    const nextId = `quantityAndPrice-${Date.now()}`;

    const startMonth = projectData?.projectStartDate
      ? parseInt(projectData.projectStartDate.split("-")[1], 10)
      : 4;

    // EOL予定日を考慮した年次配列とデータを取得
    const initialYears = getInitialYearsByEOL();
    const initialData = getInitialDataByEOL();

    // 新製品比率のデフォルト値（目的が新製品の場合は100、それ以外は0）
    const defaultNewProductRatio = projectData?.purpose === PurposeType.NewProduct.code ? 100 : 0;

    const newTable: QuantityAndPriceMonthTable = {
      id: nextId,
      partNo: "",
      years: initialYears,
      data: initialData,
      moldRadioValue: RADIO_VALUES.PART_NOT_INPUT,
      developmentRadioValue: RADIO_VALUES.PART_NOT_INPUT,
      partNoMoldTables: [],
      partNoDevelopmentCostTables: [],
      currencyAndRatio: [{ id: "", ratio: 0 }],
      newProductRatio: defaultNewProductRatio,
    };
    setQuantityAndPriceTables((prevTables) => [...prevTables, newTable]);
  };

  // パーツNoテーブル 金型追加
  const addPartNoMoldTable = (tableId: string): React.MouseEventHandler<HTMLButtonElement> => (event) => {
    event.currentTarget.blur();

    setQuantityAndPriceTables((prevTables) =>
      prevTables.map((table) => {
        if (table.id !== tableId) {
          return table;
        }

        const nextId = `partNoMold-${Date.now()}`;
        const startMonth = projectData?.projectStartDate
          ? parseInt(projectData.projectStartDate.split("-")[1], 10)
          : 4;
        const newMoldTable: MoldMonthTable = {
          id: nextId,
          partNo: table.partNo,
          moldName: "",
          years: table.years,
          // 各年度データを初期化
          data: table.years.reduce((acc, year) => {
            acc[year] = {
              price: 0,
              months: initializePriceMonths(),
            };
            return acc;
          }, {} as { [key: number]: YearPriceData }),
        };

        return {
          ...table,
          partNoMoldTables: [...table.partNoMoldTables, newMoldTable],
        };
      })
    );
  };

  // パーツNoテーブル 開発費追加
  const addPartNoDevelopmentCostTable = (tableId: string): React.MouseEventHandler<HTMLButtonElement> => (event) => {
    event.currentTarget.blur();

    setQuantityAndPriceTables((prevTables) =>
      prevTables.map((table) => {
        if (table.id !== tableId) {
          return table;
        }

        const nextId = `partNoDevelopmentCost-${Date.now()}`;
        const startMonth = projectData?.projectStartDate
          ? parseInt(projectData.projectStartDate.split("-")[1], 10)
          : 4;
        const newDevelopmentCostTable: DevelopmentMonthCostTable = {
          id: nextId,
          partNo: table.partNo,
          developmentCostName: "",
          years: table.years,
          // 各年度のデータを初期化
          data: table.years.reduce((acc, year) => {
            acc[year] = {
              price: 0,
              months: initializePriceMonths(),
            };
            return acc;
          }, {} as { [key: number]: YearPriceData }),
        };

        return {
          ...table,
          partNoDevelopmentCostTables: [...table.partNoDevelopmentCostTables, newDevelopmentCostTable],
        };
      })
    );
  };

  // 金型回収テーブル追加
  const addMoldTable: React.MouseEventHandler<HTMLButtonElement> = (event) => {

    event.currentTarget.blur();

    const nextId = `mold-${Date.now()}`;
    const startMonth = projectData?.projectStartDate
      ? parseInt(projectData.projectStartDate.split("-")[1], 10)
      : 4;

    // EOL予定日を考慮した年次配列とデータを取得
    const initialYears = getInitialYearsByEOL();
    const initialData = getInitialPriceDataByEOL();

    setMoldTables([
      ...moldTables,
      {
        id: nextId,
        partNo: "",
        moldName: "", // 金型名
        years: initialYears,
        data: initialData,
        currencyId: "",
      },
    ]);
  };

  // 開発費回収テーブル追加
  const addDevelopmentCostTable: React.MouseEventHandler<HTMLButtonElement> = (event) => {

    event.currentTarget.blur();

    const nextId = `developmentCost-${Date.now()}`;
    const startMonth = projectData?.projectStartDate
      ? parseInt(projectData.projectStartDate.split("-")[1], 10)
      : 4;

    // EOL予定日を考慮した年次配列とデータを取得
    const initialYears = getInitialYearsByEOL();
    const initialData = getInitialPriceDataByEOL();

    setDevelopmentCostTables([
      ...developmentCostTables,
      {
        id: nextId,
        partNo: "",
        developmentCostName: "",
        years: initialYears,
        data: initialData,
        currencyId: "",
      },
    ]);
  };

  // 設備費回収テーブル追加
  const addEquipmentCostTable: React.MouseEventHandler<HTMLButtonElement> = (event) => {

    event.currentTarget.blur();

    const nextId = `equipmentCost-${Date.now()}`;

    // EOL予定日を考慮した年次配列とデータを取得
    const initialYears = getInitialYearsByEOL();
    const initialData = getInitialPriceDataByEOL();

    setEquipmentCostTables([
      ...equipmentCostTables,
      {
        id: nextId,
        partNo: "",
        equipmentCostName: "",
        years: initialYears,
        data: initialData,
        currencyId: "",
      },
    ]);
  };

  // 年追加ボタン処理
  const addYear = (tableId: string, type: TableType) => {
    const newYearDataPartNo: YearData = { quantity: 0, unitPrice: 0, salesPrice: 0, months: initializePartNoMonths() };
    const newYearDataMold: YearPriceData = { price: 0, months: initializePriceMonths() };
    const newYearDataDevCost: YearPriceData = { price: 0, months: initializePriceMonths() };

    // スクロールバー制御を行うテーブルID格納
    setUpdatedTableId(tableId);
    switch (type) {
      case TABLE_TYPES.PART_NO_TABLES:
        setQuantityAndPriceTables((prevTables: QuantityAndPriceMonthTable[]) => {
          return prevTables.map((table) => {

            if (table.id !== tableId) { return table; }

            // IDが一致するものを更新
            return {
              ...table,
              years: [...table.years, table.years.length + 1],
              data: { ...table.data, [table.years.length + 1]: newYearDataPartNo },
              partNoMoldTables: table.partNoMoldTables.map((moldTable) => ({
                ...moldTable,
                years: [...moldTable.years, moldTable.years.length + 1],
                data: { ...moldTable.data, [moldTable.years.length + 1]: newYearDataMold },
              })),
              partNoDevelopmentCostTables: table.partNoDevelopmentCostTables.map((developmentCostTable) => ({
                ...developmentCostTable,
                years: [...developmentCostTable.years, developmentCostTable.years.length + 1],
                data: { ...developmentCostTable.data, [developmentCostTable.years.length + 1]: newYearDataDevCost },
              })),
            };
          });
        });
        break;
      case TABLE_TYPES.MOLD_TABLES:
        setMoldTables((prevTables: MoldMonthTableByProject[]) => changeTableAddYear(prevTables, tableId, newYearDataMold));
        break;
      case TABLE_TYPES.DEVELOPMENT_COST_TABLES:
        setDevelopmentCostTables((prevTables: DevelopmentMonthCostTableByProject[]) => changeTableAddYear(prevTables, tableId, newYearDataDevCost));
        break;
      case TABLE_TYPES.EQUIPMENT_COST_TABLES:
        setEquipmentCostTables((prevTables: EquipmentMonthCostTableByProject[]) => changeTableAddYear(prevTables, tableId, newYearDataDevCost));
        break;
      default:
        console.error("addYear Unknown table type: " + type);
    }
    setTimeout(() => setUpdatedTableId(null), 0);
  };
  // 年追加時のテーブル情報更新
  const changeTableAddYear = <T extends { id: string; years: number[]; data: { [key: number]: any } }>(
    prevTables: T[],
    tableId: string,
    newYearData: any
  ): T[] => {
    return prevTables.map((table) => {

      if (table.id !== tableId) { return table; }

      return {
        ...table,
        years: [...table.years, table.years.length + 1],
        data: {
          ...table.data,
          [table.years.length + 1]: newYearData,
        },
      };
    });
  };

  // 年削除ボタン処理
  const removeYear = (tableId: string, type: TableType) => {

    switch (type) {
      case TABLE_TYPES.PART_NO_TABLES:
        setQuantityAndPriceTables((prevTables) => {
          return prevTables.map((table) => {

            if (table.id !== tableId) { return table; }

            return {
              ...table,
              years: table.years.slice(0, -1),
              data: Object.fromEntries(Object.entries(table.data).filter(([year]) => Number(year) < table.years.length)),
              partNoMoldTables: table.partNoMoldTables.map((moldTable) => ({
                ...moldTable,
                years: moldTable.years.slice(0, -1),
                data: Object.fromEntries(Object.entries(moldTable.data).filter(([year]) => Number(year) < moldTable.years.length)),
              })),
              partNoDevelopmentCostTables: table.partNoDevelopmentCostTables.map((developmentCostTable) => ({
                ...developmentCostTable,
                years: developmentCostTable.years.slice(0, -1),
                data: Object.fromEntries(Object.entries(developmentCostTable.data).filter(([year]) => Number(year) < developmentCostTable.years.length)),
              })),
            };
          });
        });

        break;
      case TABLE_TYPES.MOLD_TABLES:
        setMoldTables((prevTables) => changeTableRemoveYear(prevTables, tableId));
        break;
      case TABLE_TYPES.DEVELOPMENT_COST_TABLES:
        setDevelopmentCostTables((prevTables) => changeTableRemoveYear(prevTables, tableId));
        break;
      case TABLE_TYPES.EQUIPMENT_COST_TABLES:
        setEquipmentCostTables((prevTables) => changeTableRemoveYear(prevTables, tableId));
        break;
      default:
        console.error("removeYear Unknown table type: " + type);
    }
  };
  // 年削除時のテーブル情報更新
  const changeTableRemoveYear = <T extends { id: string; years: number[]; data: { [key: number]: any } }>(
    prevTables: T[],
    tableId: string
  ): T[] => {
    return prevTables.map((table) => {

      if (table.id !== tableId) { return table; }

      return {
        ...table,
        years: table.years.slice(0, -1),
        data: Object.fromEntries(
          Object.entries(table.data).filter(([year]) => Number(year) < table.years.length)
        ),
      };
    });
  };

  // テーブル削除ボタン処理
  const deleteTable = (id: string, type: TableType) => {
    // エラーメッセージリセット
    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      Object.keys(newErrors).forEach((key) => {
        if (key.startsWith(`${type}-${id}`)) {
          delete newErrors[key];
        }
      });
      return newErrors;
    });

    switch (type) {
      case TABLE_TYPES.PART_NO_TABLES:
        setQuantityAndPriceTables((prev) => prev.filter((table) => table.id !== id));
        setSelectedCurrency((prev) => prev.filter((item) => item.tableId !== id));
        break;
      case TABLE_TYPES.MOLD_TABLES:
        setMoldTables((prev) => prev.filter((table) => table.id !== id));
        break;
      case TABLE_TYPES.DEVELOPMENT_COST_TABLES:
        setDevelopmentCostTables((prev) => prev.filter((table) => table.id !== id));
        break;
      case TABLE_TYPES.EQUIPMENT_COST_TABLES:
        setEquipmentCostTables((prev) => prev.filter((table) => table.id !== id));
        break;
      default:
        console.error("deleteTable Unknown table type: " + type);
    }
  };

  // モーダル用の各合計計算
  const calculateTotal = () => {
    if (!quantityAndPriceTables || quantityAndPriceTables.length === 0) {
      return { partNoTotals: [], moldTotals: [], developmentCostTotals: [], equipmentCostTotals: [] };
    }

    // PartNoごとの年間売上の合計を計算
    const partNoTotals = quantityAndPriceTables.map((table) => {
      const partNoMoldTables = Array.isArray(table.partNoMoldTables) ? table.partNoMoldTables : [];
      const partNoDevelopmentCostTables = Array.isArray(table.partNoDevelopmentCostTables) ? table.partNoDevelopmentCostTables : [];

      // PartNoに紐づく金型回収の合計を計算
      const partNoMoldTotal = partNoMoldTables.reduce(
        (sum, moldTable) =>
          sum.plus(
            moldTable.years.reduce((tableSum, year) => {
              const yearData = moldTable.data[year];
              const yearTotal = Object.values(yearData?.months || {}).reduce(
                (monthSum, monthData) => monthSum.plus(new Decimal(monthData.price || 0)),
                new Decimal(0)
              );
              return tableSum.plus(yearTotal);
            }, new Decimal(0))
          ),
        new Decimal(0)
      );

      // PartNoに紐づく開発費回収の合計を計算
      const partNoDevelopmentCostTotal = partNoDevelopmentCostTables.reduce(
        (sum, developmentCostTable) =>
          sum.plus(
            developmentCostTable.years.reduce((tableSum, year) => {
              const yearData = developmentCostTable.data[year];
              const yearTotal = Object.values(yearData?.months || {}).reduce(
                (monthSum, monthData) => monthSum.plus(new Decimal(monthData.price || 0)), // 月の金額合計
                new Decimal(0)
              );
              return tableSum.plus(yearTotal);
            }, new Decimal(0))
          ),
        new Decimal(0)
      );

      // PartNoごとの年間売上の合計を計算
      const partNoSalesTotal = table.years.reduce((sum, year) => {
        const yearData = table.data[year];
        const yearSales = Object.values(yearData?.months || {}).reduce(
          (monthSum, monthData) =>
            monthSum.plus(
              new Decimal(monthData.quantity || 0).times(new Decimal(monthData.price || 0))
            ), // 月単位の売上 (数量 * 金額)
          new Decimal(0)
        );
        return sum.plus(yearSales);
      }, new Decimal(0));

      return {
        partNo: table.partNo,
        salesTotal: partNoSalesTotal,
        moldTotal: partNoMoldTotal,
        developmentCostTotal: partNoDevelopmentCostTotal,
      };
    });

    // 各金型回収合計を計算
    const moldTotals = moldTables.map((table) => {
      const total = table.years.reduce((tableSum, year) => {
        const yearData = table.data[year];
        const yearTotal = Object.values(yearData?.months || {}).reduce(
          (monthSum, monthData) => monthSum.plus(new Decimal(monthData.price || 0)),
          new Decimal(0)
        );
        return tableSum.plus(yearTotal);
      }, new Decimal(0));

      return {
        id: table.id,
        name: table.moldName,
        currencyId: table.currencyId,
        total,
      };
    });

    // 各開発費回収合計を計算
    const developmentCostTotals = developmentCostTables.map((table) => {
      const total = table.years.reduce((tableSum, year) => {
        const yearData = table.data[year];
        const yearTotal = Object.values(yearData?.months || {}).reduce(
          (monthSum, monthData) => monthSum.plus(new Decimal(monthData.price || 0)),
          new Decimal(0)
        );
        return tableSum.plus(yearTotal);
      }, new Decimal(0));

      return {
        id: table.id,
        name: table.developmentCostName,
        currencyId: table.currencyId,
        total,
      };
    });

    // 各設備費回収合計を計算
    const equipmentCostTotals = equipmentCostTables.map((table) => {
      const total = table.years.reduce((tableSum, year) => {
        const yearData = table.data[year];
        const yearTotal = Object.values(yearData?.months || {}).reduce(
          (monthSum, monthData) => monthSum.plus(new Decimal(monthData.price || 0)),
          new Decimal(0)
        );
        return tableSum.plus(yearTotal);
      }, new Decimal(0));

      return {
        id: table.id,
        name: table.equipmentCostName,
        currencyId: table.currencyId,
        total,
      };
    });

    return { partNoTotals, moldTotals, developmentCostTotals, equipmentCostTotals };
  };

  const { partNoTotals, moldTotals, developmentCostTotals, equipmentCostTotals } = calculateTotal();


  /**
   * 年次データを更新して合計情報を計算する
   * @param prevTables テーブルのデータ
   * @param updateTotalFunc 合計計算を行う関数
   * @returns 更新後のテーブルデータ
   */
  const updateTablesData = <T extends { years: number[]; data: { [key: number]: any } }>(
    prevTables: T[],
    updateTotalFunc: (data: { [key: number]: any }) => void
  ): T[] => {
    return prevTables.map((table) => {
      const updatedData = { ...table.data }; // データのコピー
      updateTotalFunc(updatedData); // 年次データの合計を更新
      return {
        ...table,
        data: updatedData, // 更新後のデータをセット
      };
    });
  };

  /**
   * PartNoテーブルの年次データ更新処理
   */
  const updatePartNoTotals = (data: { [key: number]: YearData }) => {
    Object.keys(data).forEach((yearKey) => {
      const year = Number(yearKey);
      const yearData = data[year];

      if (!yearData?.months) return;

      const totalQuantity = calculateTotalQuantity([year], data);
      const { totalSales, weightedAveragePrice } = calculateTotalSales([year], data);

      data[year].quantity = totalQuantity.toNumber();
      data[year].unitPrice = weightedAveragePrice.toNumber();
      data[year].salesPrice = totalSales.toNumber();
    });
  };

  /**
   * 金型テーブルまたは開発費テーブルの年次データ更新処理
   */
  const updatePriceTotals = (data: { [key: number]: YearPriceData }) => {
    Object.keys(data).forEach((yearKey) => {
      const year = Number(yearKey);
      const yearData = data[year];

      if (!yearData?.months) return;

      const yearTotal = calculateTotalPrice([year], data);
      data[year].price = yearTotal.toNumber();
    });
  };

  /**
   * PartNoに紐づく金型テーブルの年度データを更新
   * @param relatedTables MoldMonthTable[]
   * @returns MoldMonthTable[]
   */
  const updateMoldTables = (relatedTables: MoldMonthTable[]): MoldMonthTable[] => {
    return relatedTables.map((relatedTable) => {
      const updatedData = { ...relatedTable.data };
      updatePriceTotals(updatedData);
      return {
        ...relatedTable,
        data: updatedData,
      };
    });
  };

  /**
   * PartNoに紐づく開発費テーブルの年度データを更新
   * @param relatedTables DevelopmentMonthCostTable[]
   * @returns DevelopmentMonthCostTable[]
   */
  const updateDevelopmentCostTables = (relatedTables: DevelopmentMonthCostTable[]): DevelopmentMonthCostTable[] => {
    return relatedTables.map((relatedTable) => {
      const updatedData = { ...relatedTable.data };
      updatePriceTotals(updatedData);
      return {
        ...relatedTable,
        data: updatedData,
      };
    });
  };

  // -----------------------------
  // 入力項目イベント
  // -----------------------------
  // 数量・単価テキスト入力変更時のイベント
  // 保持しているテーブルの値を更新
  const handleInputChange = <T extends QuantityAndPriceMonthTable | MoldMonthTableByProject | DevelopmentMonthCostTableByProject | EquipmentMonthCostTableByProject>
    (tableId: string, year: number, month: number, field: "quantity" | "price", value: number | null, setter: React.Dispatch<React.SetStateAction<T[]>>) => {
    setter((prevTables) =>
      prevTables.map((table) => {
        if (table.id !== tableId) { return table; }

        // ネストデータの値を更新
        const updatedData = updateNestedField(table.data, [year, "months", month, field], value);

        return {
          ...table,
          data: updatedData,
        };
      })
    );
  };

  // PartNo入力変更時のイベント
  const handlePartNoChange = (tableId: string, value: string) => {
    setSelectedCurrency((prev) => {
      const index = prev.findIndex((item) => item.tableId === tableId);

      if (index !== -1) {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          tableId,
          partNo: value
        };
        return updated;
      }

      return [
        ...prev,
      ];
    });

    setQuantityAndPriceTables((prevTables) => changeTablePartNo(prevTables, tableId, value));
  };

  // 保持しているテーブルの値を更新
  const changeTablePartNo = <T extends { id: string; partNo: string }>(prevTables: T[], tableId: string, value: string) => {
    return prevTables.map((table) =>
      table.id === tableId ? { ...table, partNo: value } : table
    );
  };

  /**
   * セット販売入力チェックボックスの状態変更
   * @param tableId 対象PartNoテーブルのID
   * @param checked チェック状態
   */
  const handleSetSalesInputChange = (tableId: string, checked: boolean) => {
    setQuantityAndPriceTables((prevTables) =>
      prevTables.map((table) =>
        table.id === tableId ? { ...table, isSetSalesInput: checked } : table
      )
    );
  };

  // PartNoに紐づく金型テーブル 金型名入力変更時のイベント
  const handlePartNoMoldNameChange = (tableId: string, moldTableId: string, value: string) => {
    setQuantityAndPriceTables((prevTables) =>
      prevTables.map((table) => {
        if (table.id !== tableId) { return table; }

        // テーブル-moldName(金型名)を更新
        const updatedPartNoMoldTables = updateNestedField(table.partNoMoldTables, [moldTableId, "moldName"], value);

        return {
          ...table,
          partNoMoldTables: updatedPartNoMoldTables,
        };
      })
    );
  };

  // PartNoに紐づく金型テーブル 金額入力更新イベント
  const handlePartNoMoldInputChange = (tableId: string, moldTableId: string, year: number, month: number, value: number | null) => {
    setQuantityAndPriceTables((prevTables) =>
      prevTables.map((table) => {
        if (table.id !== tableId) { return table; }

        // moldTableId-data-year-months-month-priceを更新する
        const updatedPartNoMoldTables = updateNestedField(
          table.partNoMoldTables,
          [moldTableId, "data", year, "months", month, "price"],
          value ?? 0
        );

        return {
          ...table,
          partNoMoldTables: updatedPartNoMoldTables,
        };
      })
    );
  };

  // PartNoに紐づく開発費テーブル 開発費名入力変更時のイベント
  const handlePartNoDevelopmentCostNameChange = (tableId: string, developmentCostTableId: string, value: string) => {
    setQuantityAndPriceTables((prevTables) =>
      prevTables.map((table) => {
        if (table.id !== tableId) { return table; }

        // テーブル-開発費名を更新
        const updatedPartNoDevelopmentCostTables = updateNestedField(
          table.partNoDevelopmentCostTables,
          [developmentCostTableId, "developmentCostName"],
          value
        );

        return {
          ...table,
          partNoDevelopmentCostTables: updatedPartNoDevelopmentCostTables,
        };
      })
    );
  };

  // PartNoに紐づく開発費テーブル 金額入力更新イベント
  const handlePartNoDevelopmentCostInputChange = (tableId: string, devCostTableId: string, year: number, month: number, value: number | null) => {
    setQuantityAndPriceTables((prevTables) =>
      prevTables.map((table) => {
        if (table.id !== tableId) { return table; }

        const updatedPartNoDevelopmentCostTables = updateNestedField(
          table.partNoDevelopmentCostTables,
          [devCostTableId, "data", year, "months", month, "price"],
          value ?? 0
        );

        return {
          ...table,
          partNoDevelopmentCostTables: updatedPartNoDevelopmentCostTables,
        };
      })
    );
  };

  // PartNoに紐づく金型テーブル削除ボタン処理
  const deletePartNoMoldTable = (tableId: string, moldTableId: string) => {
    setQuantityAndPriceTables((prevTables) =>
      prevTables.map((table) => {
        if (table.id !== tableId) { return table; }
        return {
          ...table,
          partNoMoldTables: table.partNoMoldTables.filter(
            (moldTable) => moldTable.id !== moldTableId
          ),
        };
      })
    );
  };

  // PartNoに紐づく開発費テーブル削除ボタン処理
  const deletePartNoDevelopmentCostTable = (tableId: string, developmentCostTableId: string) => {
    setQuantityAndPriceTables((prevTables) =>
      prevTables.map((table) => {
        if (table.id !== tableId) { return table; }
        return {
          ...table,
          partNoDevelopmentCostTables: table.partNoDevelopmentCostTables.filter(
            (developmentCostTable) => developmentCostTable.id !== developmentCostTableId
          ),
        };
      })
    );
  };

  // 金型名入力変更時のイベント
  const handleMoldNameChange = (tableId: string, value: string) => {
    // 保持しているテーブルの値を更新
    setMoldTables((prevTables) =>
      prevTables.map((table) =>
        table.id === tableId ? { ...table, moldName: value } : table
      )
    );
  };

  // 開発費名入力変更時のイベント
  const handleDevelopmentCostNameChange = (tableId: string, value: string) => {
    // 保持しているテーブルの値を更新
    setDevelopmentCostTables((prevTables) =>
      prevTables.map((table) =>
        table.id === tableId ? { ...table, developmentCostName: value } : table
      )
    );
  };

  // 設備費名入力変更時のイベント
  const handleEquipmentCostNameChange = (tableId: string, value: string) => {
    // 保持しているテーブルの値を更新
    setEquipmentCostTables((prevTables) =>
      prevTables.map((table) =>
        table.id === tableId ? { ...table, equipmentCostName: value } : table
      )
    );
  };

  // ラジオボタン変更時のイベント
  const handleRadioChange = (tableId: string, value: string, type: TableType) => {

    switch (type) {
      case TABLE_TYPES.MOLD_TABLES:
        setQuantityAndPriceTables((prevTables) =>
          prevTables.map((table) =>
            table.id === tableId ? { ...table, moldRadioValue: value } : table
          )
        );

        // 金型名の入力なしが選択された場合、partNoテキストを初期化
        if (value === RADIO_VALUES.PART_NOT_INPUT) {
          setQuantityAndPriceTables((prevTables) =>
            prevTables.map((table) =>
              table.id === tableId ? { ...table, partNoMoldTables: [] } : table
            )
          );
        }
        break;
      case TABLE_TYPES.DEVELOPMENT_COST_TABLES:
        setQuantityAndPriceTables((prevTables) =>
          prevTables.map((table) =>
            table.id === tableId ? { ...table, developmentRadioValue: value } : table
          )
        );

        if (value === RADIO_VALUES.PART_NOT_INPUT) {
          setQuantityAndPriceTables((prevTables) =>
            prevTables.map((table) =>
              table.id === tableId ? { ...table, partNoDevelopmentCostTables: [] } : table
            )
          );
        }
        break;
      default:
    }
  }

  // 月算出処理
  const getMonth = (month: string, num: number): string => {
    if (!month) return "";
    const [y, m] = month.split("-").map(Number);
    const date = new Date(y, m - 1 + num, 1);
    const year = date.getFullYear();
    const newMonth = String(date.getMonth() + 1).padStart(2, "0");
    return `${year}-${newMonth}`;
  };

  // 行情報生成処理
  const generateRowsFromTable = (table: any, baseStartYear: number, startMonth: number) => {
    const rows: PriceRows[] = [];
    let id = 1;
    let prevValue: number | null = null;
    let start: { y: number; m: number } | null = null;

    // EOL予定日を取得
    const projectEndDate = projectData?.projectEndDate;
    let eolYear: number | null = null;
    let eolMonth: number | null = null;
    if (projectEndDate) {
      eolYear = parseInt(projectEndDate.split("-")[0], 10);
      eolMonth = parseInt(projectEndDate.split("-")[1], 10);
    }

    table.years.forEach((y: number) => {
      const months = table.data[y].months ? Object.values(table.data[y].months) : [];
      months.forEach((m: any) => {

        // 年度表示の計算ロジック
        const fiscalYear =
          m.month >= 4 && m.month <= 12
            ? baseStartYear + y - 1
            : baseStartYear + y;

        const startYear =
          startMonth >= 4 && startMonth <= 12
            ? baseStartYear
            : baseStartYear + 1;

        if (fiscalYear < startYear ||
          fiscalYear === startYear && m.month < startMonth) {
          return;
        }

        const value = m["price"];

        if (prevValue === null) {
          start = { y: fiscalYear, m: m.month };
          prevValue = value;
        } else if (value !== prevValue) {
          rows.push({
            id: id++,
            displayStartMonth: `${start!.y}-${String(start!.m).padStart(2, "0")}`,
            displayEndMonth: `${m.month === 1 ? fiscalYear - 1 : fiscalYear}-${String(m.month === 1 ? 12 : m.month - 1).padStart(2, "0")}`,
            startMonth: `${start!.y}-${String(start!.m).padStart(2, "0")}`,
            endMonth: `${m.month === 1 ? fiscalYear - 1 : fiscalYear}-${String(m.month === 1 ? 12 : m.month - 1).padStart(2, "0")}`,
            value: prevValue,
          });
          start = { y: fiscalYear, m: m.month };
          prevValue = value;
        }

        // 最後の月の処理
        if (y === table.years[table.years.length - 1] && m.month === 3) {
          if (id === 1 || value !== 0) {
            // 終了年月の決定：EOL予定日と量産開始日から10年後のうち早い方を使用
            let endYear = fiscalYear;
            let endMonth = m.month;

            // 量産開始日から10年間（年度末の3月）を計算
            const projectStartDate = projectData?.projectStartDate;
            let maxEndYear: number | null = null;
            let maxEndMonth: number | null = null;
            if (projectStartDate) {
              const startY = parseInt(projectStartDate.split("-")[0], 10);
              const startM = parseInt(projectStartDate.split("-")[1], 10);
              // 10年間の年度末を上限とする（例：2025/05開始なら2035/03まで）
              // 年度は4月始まりなので、4月以降開始なら+10年の3月、1-3月開始なら+9年の3月
              maxEndYear = startM >= 4 ? startY + 10 : startY + 9;
              maxEndMonth = 3;
            }

            if (eolYear !== null && eolMonth !== null) {
              // EOL予定日と10年間の年度末を比較して早い方を使用
              if (maxEndYear !== null && maxEndMonth !== null) {
                const eolDate = new Date(eolYear, eolMonth - 1);
                const maxDate = new Date(maxEndYear, maxEndMonth - 1);
                if (eolDate <= maxDate) {
                  endYear = eolYear;
                  endMonth = eolMonth;
                } else {
                  endYear = maxEndYear;
                  endMonth = maxEndMonth;
                }
              } else {
                endYear = eolYear;
                endMonth = eolMonth;
              }
            } else if (maxEndYear !== null && maxEndMonth !== null) {
              // EOL予定日が未設定の場合は10年後を使用
              endYear = maxEndYear;
              endMonth = maxEndMonth;
            }

            rows.push({
              id: id++,
              displayStartMonth: `${start!.y}-${String(start!.m).padStart(2, "0")}`,
              displayEndMonth: `${endYear}-${String(endMonth).padStart(2, "0")}`,
              startMonth: `${start!.y}-${String(start!.m).padStart(2, "0")}`,
              endMonth: `${endYear}-${String(endMonth).padStart(2, "0")}`,
              value,
            });
          }
        }
      });
    });

    return rows;
  };

  /**
   * 「画面で入力」ボタン押下
   */
  const handleMaualClick = (tableId: string, e: React.MouseEvent<HTMLButtonElement>) => {
    // フォーカスを外す
    e.currentTarget.blur()

    // スプレッドシートClose
    const win = spreadSheetWindows[tableId];
    if (win) {
      win.close();
      setSpreadSheetWindows(prev => ({ ...prev, [tableId]: null }));
    }
  }

  /**
   * 「GoogleSpread」ボタン押下
   */
  const handleGoogleSpreadClick = async (e: React.MouseEvent<HTMLButtonElement>,
    table: QuantityAndPriceMonthTable | MoldMonthTable | DevelopmentMonthCostTable) => {

    // フォーカスを外す
    e.currentTarget.blur()

    if (!table.partNo) {
      console.error("PartNo has not been selected.");
      return;
    }

    if (loadingIds.has(table.id)) {
      return;
    }

    // エラー時に今回開いたタブを閉じるため保持
    let openedWindow: Window | null = null;

    try {
      setLoadingIds(prev => new Set(prev).add(table.id));

      // 同じテーブルで開いている既存タブは閉じる（再オープン時にタブを重複させない）
      const prevWindow = spreadSheetWindowsRef.current[table.id];
      if (prevWindow && !prevWindow.closed) {
        prevWindow.close();
      }

      // ポップアップブロック回避：非同期処理より前にユーザー操作起因でウィンドウを確保
      const newWindow = window.open("", "_blank");
      openedWindow = newWindow;
      setSpreadSheetWindows(prev => ({ ...prev, [table.id]: newWindow }));

      // Googleアカウント認証
      if (! await googleAPI.signIn()) {
        throw new Error("signIn error");
      }

      // 対象ファイル名でスプレッドシートを生成する（既に存在する場合は作成しない）
      // ファイル名に使用できない文字（スラッシュ等）を全角に置換
      const partNoItem = partNoData.find(p => p.partNo === table.partNo);
      const fileName = sanitizeFileName(resolveDisplayPartNo(partNoItem));
      const folderPath = QUANTITY_AND_PRICE_FOLDER_PATH;
      let folderId = await googleAPI.createFolderPath(folderPath, null);
      if (!folderId) { folderId = ""; }

      const activeSpreadsheetId = await googleAPI.createSpreadSheet(fileName, folderId, null);

      // 事前確保したウィンドウをスプレッドシートに遷移
      if (newWindow) {
        newWindow.location.href = googleAPI.getOpenSpradSheetUrl(activeSpreadsheetId);
        if (activeSpreadsheetId) {
          // スプレッドシートウィンドウを保持
          setSpreadSheetWindowss(newWindow);
        }

        // GoogleSpreadシートウィンドウを手動で閉じられた場合
        const interval = setInterval(() => {
          if (newWindow.closed) {
            clearInterval(interval); // 監視を停止
            // 入力モードをmanualに戻す
            setInputModes(prev => {
              if (prev.find(item => item.id === table.id)) {
                return prev.map(item =>
                  item.id === table.id ? { ...item, mode: "manual" } : item
                );
              } else {
                return [...prev, { id: table.id, mode: "manual" }];
              }
            });
          }
        }, 500); // 500ms ごとに監視
      } else {
        console.error("GoogleSpreadシートがブロックされました");
      }

      // 共通関数でSpreadSheet書き込みデータを生成
      const sheetData = quantityAndPriceToSpreadSheetData(
        table as QuantityAndPriceMonthTable,
        baseStartYear,
        projectData?.projectStartDate,
        isGeneralPurpose
      );

      // シート名を動的に取得（ロケール対応）
      const sheetName = await googleAPI.getFirstSheetName(activeSpreadsheetId) ?? GoogleAPI.DEFAULT_SHEET_NAME;

      // スプレッドシートから値を取得
      const result = await googleAPI.spreadSheetGetValues(activeSpreadsheetId, sheetName);
      const { values } = result;

      // 既存データがあれば、初期化
      if (values) {
        const emptyValues = Array.from({ length: 3 }, () => Array(values[0].length).fill(""));
        await googleAPI.spreadSheetSetValue(activeSpreadsheetId, sheetName, 1, 1, emptyValues);
      }

      // Spreadシートにデータを反映
      await googleAPI.spreadSheetSetValue(activeSpreadsheetId, sheetName, 1, 1, sheetData);

    } catch (error) {
      console.error("Change GoogleSpread Error.", error);
      // 今回開いたタブを閉じ、対象テーブルを画面入力に戻す（他テーブルのタブは閉じない）
      openedWindow?.close();
      setSpreadSheetWindows(prev => ({ ...prev, [table.id]: null }));
      setInputModes(prev => prev.map(item => item.id === table.id ? { ...item, mode: "manual" } : item));

      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.QuantityAndPrice.message.googleSpreadUnavailable'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoadingIds(prev => {
        const copy = new Set(prev);
        copy.delete(table.id);
        return copy;
      });
    }
  };

  /**
   * 「データ連携」ボタン押下
   * @returns
   */
  const handleSyncData = async (e: React.MouseEvent<HTMLButtonElement>,
    table: QuantityAndPriceMonthTable | MoldMonthTable | DevelopmentMonthCostTable) => {

    // フォーカスを外す
    e.currentTarget.blur();

    if (!table.partNo) {
      console.error("PartNo has not been selected.");
      return;
    }

    if (loadingIds.has(table.id)) {
      return;
    }

    try {
      setLoadingIds(prev => new Set(prev).add(table.id));

      // Googleアカウント認証
      if (! await googleAPI.signIn()) {
        throw new Error("signIn error");
      }

      // ファイル名に使用できない文字（スラッシュ等）を全角に置換
      // displayPartNoが登録済みならそちらをファイル名に使用（No.435対応、GoogleSpreadボタンと同一ロジック）
      const partNoItem = partNoData.find(p => p.partNo === table.partNo);
      const fileName = sanitizeFileName(resolveDisplayPartNo(partNoItem));
      const folderPath = QUANTITY_AND_PRICE_FOLDER_PATH;
      let folderId = await googleAPI.createFolderPath(folderPath, null);

      if (!folderId) { folderId = ""; }
      const activeSpreadsheetId = await googleAPI.createSpreadSheet(fileName, folderId, null);

      if (!activeSpreadsheetId) {
        console.error("Spreadsheet ID is missing.");
        return;
      }

      // シート名を動的に取得（ロケール対応）
      const sheetName = await googleAPI.getFirstSheetName(activeSpreadsheetId) ?? GoogleAPI.DEFAULT_SHEET_NAME;

      // スプレッドシートから値を取得
      const result = await googleAPI.spreadSheetGetValues(activeSpreadsheetId, sheetName);
      const { values } = result;

      if (values) {
        // ヘッダーバリデーション（共通関数）
        if (!validateQuantityAndPriceHeader(values[0].slice(1))) {
          toast({
            title: TITLE,
            description: t('features.InvestmentScreening.QuantityAndPrice.message.headerInvalid'),
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });

          return;
        }

        // データ変換（共通関数）
        const { data: updatedData, maxYearByEOL, errors: conversionErrors, warnings: conversionWarnings } = spreadSheetDataToQuantityAndPrice(
          values,
          baseStartYear,
          { projectStartDate: projectData?.projectStartDate, projectEndDate: projectData?.projectEndDate },
          undefined,
          undefined,
          isGeneralPurpose,
        );

        // 変換時の警告をトースト表示
        for (const warn of conversionWarnings) {
          toast({
            title: TITLE,
            description: warn,
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
        }

        // 変換時のエラーをトースト表示
        for (const err of conversionErrors) {
          toast({
            title: TITLE,
            description: err,
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }

        const targetYearCount = Math.max(Object.keys(updatedData).length, maxYearByEOL);

        // 現在のテーブルの年次数を目標の年次数に合わせる
        for (let i = table.years.length; i < targetYearCount; i++) {
          addYear(table.id, TABLE_TYPES.PART_NO_TABLES);
        }

        // 年次を削除しない（EOL予定日までの年次は維持する）
        // EOL予定日を超える年次のみ削除
        for (let i = table.years.length; i > targetYearCount; i--) {
          removeYear(table.id, TABLE_TYPES.PART_NO_TABLES);
        }

        updateTables(table.id, updatedData);
      }
    } catch (error) {
      console.error("データ連携エラー:", error);
    } finally {
      setLoadingIds(prev => {
        const copy = new Set(prev);
        copy.delete(table.id);
        return copy;
      });
    }
  };

  // -----------------------------
  // 入力項目イベント
  // -----------------------------
  // 数量・単価テキスト入力変更時のイベント
  // 保持しているテーブルの値を更新
  const updateTables = (tableId: String, updatedData: any) => {
    setQuantityAndPriceTables((prevTables) =>
      prevTables.map((table) => {
        if (table.id !== tableId) { return table; }
        return {
          ...table,
          data: updatedData,
        };
      })
    );
  };

  // 年月比較処理
  function isFutureMonth(from: string, to: string): boolean {
    const [fromYear, fromMonth] = from.split("-").map(Number);
    const [toYear, toMonth] = to.split("-").map(Number);

    if (toYear > fromYear) return true;
    if (toYear < fromYear) return false;

    return toMonth > fromMonth;
  }

  // 通貨行追加
  const addCurrencyRow = (tableId: string) => {
    setQuantityAndPriceTables((prevTables) =>
      prevTables.map((table) => {
        if (table.id !== tableId) { return table; }

        return {
          ...table,
          currencyAndRatio: [...table.currencyAndRatio, { id: "", ratio: 0 }],
        };
      })
    );
  };

  // 通貨行削除
  const removeCurrencyRow = (index: number, tableId: string, currencyId: string) => {
    setQuantityAndPriceTables((prevTables) =>
      prevTables.map((table) => {
        if (table.id !== tableId) { return table; }

        // indexを使用して削除（currencyIdが空文字の場合に複数行が削除される問題を回避）
        const currencyAndRatio = table.currencyAndRatio.filter((_, i) => i !== index);
        updateSelectedCurrency(table.id, table.partNo, currencyAndRatio);

        return {
          ...table,
          currencyAndRatio: currencyAndRatio,
        };
      })
    );
  };

  // 通貨・比率の更新
  const updateCurrencyRow = (index: number, tableId: string, field: "id" | "ratio", value: any) => {
    setQuantityAndPriceTables((prevTables) =>
      prevTables.map((table) => {
        if (table.id !== tableId) { return table; }

        const updatedCurrencyAndRatio = table.currencyAndRatio.map((c, i) =>
          i === index ? { ...c, [field]: value } : c
        );

        updateSelectedCurrency(table.id, table.partNo, updatedCurrencyAndRatio);

        return {
          ...table,
          currencyAndRatio: updatedCurrencyAndRatio
        };
      })
    );
  };

  // 新製品比率の更新（空の場合は0を設定）
  const updateNewProductRatio = (tableId: string, value: number | null) => {
    setQuantityAndPriceTables((prevTables) =>
      prevTables.map((table) => {
        if (table.id !== tableId) { return table; }
        return {
          ...table,
          newProductRatio: value ?? 0
        };
      })
    );
  };

  // 基準通貨設定処理
  const updateSelectedCurrency = (tableId: string, partNo: string, currencyAndRatio: { id: string; ratio: number }[]) => {
    // 基準通貨を決定（空配列または有効な通貨がない場合は空文字）
    let id = "";
    let name = "";

    const hasValidCurrency = currencyAndRatio.length > 0 && currencyAndRatio.some(c => c.id);
    if (hasValidCurrency) {
      // 有効な比率のみを対象にする（null/undefined/NaNを除外）
      const validRatios = currencyAndRatio.filter(c => c.ratio != null && !isNaN(c.ratio));
      if (validRatios.length > 0) {
        const maxRatio = Math.max(...validRatios.map((c) => c.ratio));
        const firstMaxIndex = currencyAndRatio.findIndex((c) => c.ratio === maxRatio);
        if (firstMaxIndex !== -1 && currencyAndRatio[firstMaxIndex]) {
          const selected = currencyData.find(currency => currency.currencyId === currencyAndRatio[firstMaxIndex].id);
          id = selected?.currencyId ?? "";
          name = selected?.currencyName ?? "";
        }
      }
    }

    // selectedCurrencyを更新
    setSelectedCurrency((prev) => {
      const index = prev.findIndex((item) => item.tableId === tableId);
      const newEntry = { tableId, partNo, id, name };

      if (index !== -1) {
        const updated = [...prev];
        updated[index] = newEntry;
        return updated;
      }
      return [...prev, newEntry];
    });
  };

  // 更新行設定処理
  const setUpdatedRows = (rowId: number, field: string, value: string | number | null, tableRows: { id: string; rows: PriceRows[] }) => {
    const targetRows = tableRows.rows.map((row) =>
      row.id === rowId ? { ...row, [field]: value } : row
    );
    const row = targetRows.find((r) => r.id === rowId);
    const index = targetRows.findIndex(r => r.id === rowId)

    const updatedRows = row ? [row] : [];

    if (row && field === "endMonth" && targetRows.length !== index + 1) {
      const nextRow = targetRows[index + 1];
      const month = getMonth(row.endMonth, 1);
      if (isFutureMonth(month, nextRow.startMonth)) {
        nextRow.startMonth = month;
        updatedRows.push(nextRow);
      }
    }

    if (row && field === "startMonth" && row.value !== 0) {
      const rowInfo = tableRows.rows.find((r) => r.id === rowId);

      if (rowInfo) {
        const prevRow: PriceRows = {
          id: 0,
          displayStartMonth: rowInfo.startMonth,
          displayEndMonth: getMonth(row.startMonth, -1),
          startMonth: rowInfo.startMonth,
          endMonth: getMonth(row.startMonth, -1),
          value: 0,
        };
        updatedRows.push(prevRow);
      }
    }

    if (row && field === "endMonth" && row.value !== 0 && targetRows.length === index + 1) {
      const rowInfo = tableRows.rows.find((r) => r.id === rowId);

      if (rowInfo) {
        const nextRow: PriceRows = {
          id: 0,
          displayStartMonth: getMonth(row.endMonth, 1),
          displayEndMonth: rowInfo.endMonth,
          startMonth: getMonth(row.endMonth, 1),
          endMonth: rowInfo.endMonth,
          value: 0,
        };
        updatedRows.push(nextRow);
      }
    }

    return updatedRows;
  };

  // -----------------------------
  // useEffect
  // -----------------------------
  /**
   * 権限情報
   */
  useEffect(() => {
    const pagePermissions = getPermissions(userPermissions, WorkflowId.QuantityAndPrice, projectData?.purpose, employeeId, divisionId, projectData?.registrantDivisionId);
    setPagePermissions(pagePermissions);
  }, [userPermissions, employeeId, divisionId, projectData]);

  /**
   * 画面遷移時にGoogleSpreadタブを閉じる
   */
  // 依存配列に spreadSheetWindows を指定すると、別テーブルでGoogleSpreadを開くたびに
  // 前回のcleanupが走り既存のタブまで閉じてしまうため、refで最新値を保持しアンマウント時のみ閉じる
  const spreadSheetWindowsRef = useRef<Record<string, Window | null>>({});
  useEffect(() => {
    spreadSheetWindowsRef.current = spreadSheetWindows;
  }, [spreadSheetWindows]);
  useEffect(() => {
    return () => {
      // コンポーネントのアンマウント時にGoogleSpreadタブを閉じる
      Object.values(spreadSheetWindowsRef.current).forEach((win) => {
        if (win && !win.closed) {
          win.close();
        }
      });
    };
  }, []);

  // 画面初期表示時のデータ取得
  useEffect(() => {
    if (isFirstRender.current) {
      console.log("QuantityAndPrice start");
      getDispData();
      isFirstRender.current = false;
    }
  }, []);

  // API実行
  const getDispData = async () => {
    try {
      if (projectId && projectId !== "") {
        const projectResult = await getProjectWithRds();
        if (projectResult) {
          await getPartNo();
        }
      } else {
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.QuantityAndPrice.message.registerFromBasicInfo'),
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('getDispData:', error);
      toast({
        title: t('common.label.failed'),
        description: t('common.api.commonError'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    const widths: Record<string, number> = Object.keys(refs).reduce((acc, key) => {
      acc[key] = refs[key]?.current?.offsetWidth || 0;
      return acc;
    }, {} as Record<string, number>);
    setButtonWidths(widths);
  }, []);

  // テーブルスクロールバー操作
  useEffect(() => {
    if (!updatedTableId) return;

    //DOMの更新完了を待って実行
    requestAnimationFrame(() => {
      if (updatedTableId.startsWith("quantityAndPrice")) {
        scrollToRight(quantityAndPriceTableRef.current, updatedTableId);
        scrollChildRef(partNoMoldPriceChildTableRef.current, updatedTableId)
        scrollChildRef(developmentCostChildTableRef.current, updatedTableId)
      } else if (updatedTableId.startsWith("mold")) {
        scrollToRight(moldTableRef.current, updatedTableId);
      } else if (updatedTableId.startsWith("developmentCost")) {
        scrollToRight(developmentCostTableRef.current, updatedTableId);
      } else if (updatedTableId.startsWith("equipmentCost")) {
        scrollToRight(equipmentCostTableRef.current, updatedTableId);
      }
    });

  }, [updatedTableId]);
  // addYearでスクロールバー右端移動
  const scrollToRight = (refs: (HTMLDivElement | null)[], tableId: string) => {
    const index = refs.findIndex(ref => ref && ref.id === tableId);
    const ref = refs[index];
    if (ref) { ref.scrollLeft = ref.scrollWidth; }
  };
  // PartNoに紐づく子要素のスクロールバーを右端移動
  const scrollChildRef = (refs: Record<string, (HTMLDivElement | null)[]>, tableId: string) => {
    const childArray = refs[tableId];
    if (!childArray) return;

    childArray.forEach(el => {
      if (el) el.scrollLeft = el.scrollWidth;
    });
  }

  useEffect(() => {
    // データがない場合、または開始日がない場合は処理終了
    if (partNoData.length === 0 || !projectData?.projectStartDate) {
      return;
    }

    const startMonth = parseInt(projectData.projectStartDate.split("-")[1], 10);

    // EOL予定日の情報を取得
    const projectEndDate = projectData.projectEndDate;
    const eolYear = projectEndDate ? parseInt(projectEndDate.split("-")[0], 10) : null;
    const eolMonth = projectEndDate ? parseInt(projectEndDate.split("-")[1], 10) : null;

    // 基準年度を計算
    const startDate = new Date(projectData.projectStartDate);
    const startYear = startDate.getFullYear();
    const startDateMonth = startDate.getMonth() + 1;
    const fiscalYearBase = startDateMonth >= 4 ? startYear : startYear - 1;

    /**
     * 指定された年・月がEOL予定日を超えているかを判定（データ初期化用）
     */
    const isAfterEOLForInit = (year: number, month: number): boolean => {
      if (!eolYear || !eolMonth) return false;

      // 現在の年度を計算
      let actualYear: number;
      if (year === 1) {
        if (startMonth >= 4 && startMonth <= 12) {
          actualYear = month >= 4 ? fiscalYearBase : fiscalYearBase + 1;
        } else {
          actualYear = fiscalYearBase + 1;
        }
      } else {
        actualYear = month >= 4 ? fiscalYearBase + year - 1 : fiscalYearBase + year;
      }

      const currentYearMonth = actualYear * 100 + month;
      const eolYearMonth = eolYear * 100 + eolMonth;

      return currentYearMonth > eolYearMonth;
    };

    /**
     * EOL予定日の年度を計算し、表示すべき最大年次を返す
     */
    const getMaxYearIndex = (): number | null => {
      if (!eolYear || !eolMonth) return null;

      // EOL予定日の年度を計算（4月～翌3月が1年度）
      const eolFiscalYear = eolMonth >= 4 ? eolYear : eolYear - 1;
      // 基準年度からの差分 + 1（1年目から始まるため）
      const maxYear = eolFiscalYear - fiscalYearBase + 1;
      return maxYear > 0 ? maxYear : 1;
    };

    const maxYearIndex = getMaxYearIndex();

    // 初期化処理
    const initializeMonthlyData = (data: any, years: number[], startMonth: number, shouldClearQuantity: boolean = false) => {
      const updatedData = { ...data };
      years.forEach((year) => {
        if (updatedData[year]?.months) {
          const months = updatedData[year].months;
          const visibleMonthKeys = getVisibleMonths(year, months, startMonth);

          // フィルタ条件に一致しない月を初期化
          Object.keys(months).forEach((monthKey) => {
            const monthData = months[Number(monthKey)];
            const actualMonth = monthData?.month || Number(monthKey);

            // プロジェクト開始日以前の月、またはEOL予定日以降の月を初期化
            if (!visibleMonthKeys.includes(Number(monthKey)) || isAfterEOLForInit(year, actualMonth)) {
              if (shouldClearQuantity) {
                updatedData[year].months[monthKey].quantity = 0;
              }
              updatedData[year].months[monthKey].price = 0;
            }
          });
        }
      });
      return updatedData;
    };

    /**
     * EOL予定日の年度までの年次に拡張する（不足分を追加）
     */
    const extendYearsToEOL = (years: number[]): number[] => {
      if (maxYearIndex === null) return years;
      // 既存の年次をコピー（EOL予定日以内のもののみ）
      const extendedYears = years.filter(year => year <= maxYearIndex);
      // 不足している年次を追加
      for (let year = 1; year <= maxYearIndex; year++) {
        if (!extendedYears.includes(year)) {
          extendedYears.push(year);
        }
      }
      // ソートして返す
      return extendedYears.sort((a, b) => a - b);
    };

    /**
     * EOL予定日の年度までのデータに拡張する（不足分を追加）
     */
    const extendDataToEOL = (data: any, years: number[], isPartNoData: boolean = false): any => {
      if (maxYearIndex === null) return data;
      const extendedData: any = {};
      // 既存のデータをコピー（EOL予定日以内のもののみ）
      years.forEach(year => {
        if (year <= maxYearIndex && data[year]) {
          extendedData[year] = data[year];
        }
      });
      // 不足している年次のデータを追加
      for (let year = 1; year <= maxYearIndex; year++) {
        if (!extendedData[year]) {
          if (isPartNoData) {
            // パーツNo用の空データを作成
            extendedData[year] = {
              quantity: 0,
              unitPrice: 0,
              salesPrice: 0,
              months: createEmptyPartNoMonths()
            };
          } else {
            // 金型・開発費用の空データを作成
            extendedData[year] = {
              price: 0,
              months: createEmptyPriceMonths()
            };
          }
        }
      }
      return extendedData;
    };

    /**
     * パーツNo用の空月データを作成
     */
    const createEmptyPartNoMonths = (): { [key: number]: MonthData } => {
      const months: { [key: number]: MonthData } = {};
      const monthOrder = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
      monthOrder.forEach((month, index) => {
        months[index + 1] = { month, quantity: 0, price: 0 };
      });
      return months;
    };

    /**
     * 金型・開発費用の空月データを作成
     */
    const createEmptyPriceMonths = (): { [key: number]: MonthPriceData } => {
      const months: { [key: number]: MonthPriceData } = {};
      const monthOrder = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
      monthOrder.forEach((month, index) => {
        months[index + 1] = { month, price: 0 };
      });
      return months;
    };

    // PartNoテーブル設定
    const parsedTables: QuantityAndPriceMonthTable[] = partNoData.map(item => {
      if (!item.quantityAndPrice) { return null; }
      const quantityAndPriceJson = JSON.parse(item.quantityAndPrice);

      // EOL予定日の年度までの年次に拡張する
      const extendedYears = extendYearsToEOL(quantityAndPriceJson.years);

      let parsedMoldSales = [];
      let parsedDevelopmentSales = [];
      let selectedCurrencies = [{ id: "", ratio: 0 }];
      if (item.moldSalesByPartNo) {
        try {
          parsedMoldSales = JSON.parse(item.moldSalesByPartNo);
          // 配列でない場合は配列に変換
          if (!Array.isArray(parsedMoldSales)) { parsedMoldSales = [parsedMoldSales]; }

          // 金型セールデータの初期化 (フィルタ条件に該当しない月を0で初期化、EOL予定日以降も0)
          // EOL予定日の年度まで年次を拡張する
          parsedMoldSales = parsedMoldSales.map((mold) => {
            const moldExtendedYears = extendYearsToEOL(mold.years);
            const moldInitializedData = initializeMonthlyData(mold.data, mold.years, startMonth, false);
            const moldExtendedData = extendDataToEOL(moldInitializedData, mold.years, false);
            return {
              ...mold,
              years: moldExtendedYears,
              data: moldExtendedData
            };
          });
        } catch (error) {
          console.error("Failed to parse JSON:", error);
        }
      }

      if (item.developmentSalesByPartNo) {
        try {
          parsedDevelopmentSales = JSON.parse(item.developmentSalesByPartNo);
          // 配列でない場合は配列に変換
          if (!Array.isArray(parsedDevelopmentSales)) { parsedDevelopmentSales = [parsedDevelopmentSales]; }

          // 開発費データの初期化 (フィルタ条件に該当しない月を0で初期化、EOL予定日以降も0)
          // EOL予定日の年度まで年次を拡張する
          parsedDevelopmentSales = parsedDevelopmentSales.map((devCost) => {
            const devCostExtendedYears = extendYearsToEOL(devCost.years);
            const devCostInitializedData = initializeMonthlyData(devCost.data, devCost.years, startMonth, false);
            const devCostExtendedData = extendDataToEOL(devCostInitializedData, devCost.years, false);
            return {
              ...devCost,
              years: devCostExtendedYears,
              data: devCostExtendedData
            };
          });
        } catch (error) {
          console.error("Failed to parse JSON:", error);
        }
      }

      if (quantityAndPriceJson.currencyAndRatio) {
        try {
          selectedCurrencies = JSON.parse(quantityAndPriceJson.currencyAndRatio);
          // 配列でない場合は配列に変換
          if (!Array.isArray(selectedCurrencies)) { selectedCurrencies = [selectedCurrencies]; }
        } catch (error) {
          console.error("Failed to parse JSON:", error);
        }

        updateSelectedCurrency(quantityAndPriceJson.id, quantityAndPriceJson.partNo, selectedCurrencies);
      }

      // 初年度のprojectStartDate以前の月、およびEOL予定日以降の月を0で初期化
      const updatedData = initializeMonthlyData(
        quantityAndPriceJson.data,
        quantityAndPriceJson.years,
        startMonth,
        true // quantityも初期化する
      );

      // EOL予定日の年度までのデータを拡張する
      const extendedData = extendDataToEOL(updatedData, quantityAndPriceJson.years, true);

      return {
        id: quantityAndPriceJson.id,
        partNo: quantityAndPriceJson.partNo,
        years: extendedYears,
        data: extendedData,
        moldRadioValue: quantityAndPriceJson.moldRadioValue,
        developmentRadioValue: quantityAndPriceJson.developmentRadioValue,
        partNoMoldTables: parsedMoldSales,
        partNoDevelopmentCostTables: parsedDevelopmentSales,
        updateDate: item.updateDate,
        currencyAndRatio: selectedCurrencies,
        newProductRatio: quantityAndPriceJson.newProductRatio ?? (projectData?.purpose === PurposeType.NewProduct.code ? 100 : 0),
        isSetSalesInput: item.isSetSalesInput ?? false,
      };
    }).filter(item => item !== null);

    setQuantityAndPriceTables(parsedTables);
  }, [partNoData, projectData?.projectStartDate, projectData?.projectEndDate]);


  useEffect(() => {
    if (!projectData?.projectStartDate) return;

    const startMonth = parseInt(projectData.projectStartDate.split("-")[1], 10);

    // EOL予定日の情報を取得
    const projectEndDate = projectData.projectEndDate;
    const eolYear = projectEndDate ? parseInt(projectEndDate.split("-")[0], 10) : null;
    const eolMonth = projectEndDate ? parseInt(projectEndDate.split("-")[1], 10) : null;

    // 基準年度を計算
    const startDate = new Date(projectData.projectStartDate);
    const startYear = startDate.getFullYear();
    const startDateMonth = startDate.getMonth() + 1;
    const fiscalYearBase = startDateMonth >= 4 ? startYear : startYear - 1;

    /**
     * 指定された年・月がEOL予定日を超えているかを判定（データ初期化用）
     */
    const isAfterEOLForInit = (year: number, month: number): boolean => {
      if (!eolYear || !eolMonth) return false;

      // 現在の年度を計算
      let actualYear: number;
      if (year === 1) {
        if (startMonth >= 4 && startMonth <= 12) {
          actualYear = month >= 4 ? fiscalYearBase : fiscalYearBase + 1;
        } else {
          actualYear = fiscalYearBase + 1;
        }
      } else {
        actualYear = month >= 4 ? fiscalYearBase + year - 1 : fiscalYearBase + year;
      }

      const currentYearMonth = actualYear * 100 + month;
      const eolYearMonth = eolYear * 100 + eolMonth;

      return currentYearMonth > eolYearMonth;
    };

    /**
     * EOL予定日の年度を計算し、表示すべき最大年次を返す
     */
    const getMaxYearIndex = (): number | null => {
      if (!eolYear || !eolMonth) return null;

      // EOL予定日の年度を計算（4月～翌3月が1年度）
      const eolFiscalYear = eolMonth >= 4 ? eolYear : eolYear - 1;
      // 基準年度からの差分 + 1（1年目から始まるため）
      const maxYear = eolFiscalYear - fiscalYearBase + 1;
      return maxYear > 0 ? maxYear : 1;
    };

    const maxYearIndex = getMaxYearIndex();

    /**
     * EOL予定日の年度までの年次に拡張する（不足分を追加）
     */
    const extendYearsToEOL = (years: number[]): number[] => {
      if (maxYearIndex === null) return years;
      // 既存の年次をコピー（EOL予定日以内のもののみ）
      const extendedYears = years.filter(year => year <= maxYearIndex);
      // 不足している年次を追加
      for (let year = 1; year <= maxYearIndex; year++) {
        if (!extendedYears.includes(year)) {
          extendedYears.push(year);
        }
      }
      // ソートして返す
      return extendedYears.sort((a, b) => a - b);
    };

    /**
     * EOL予定日の年度までのデータに拡張する（不足分を追加）
     */
    const extendDataToEOL = (data: any, years: number[]): any => {
      if (maxYearIndex === null) return data;
      const extendedData: any = {};
      // 既存のデータをコピー（EOL予定日以内のもののみ）
      years.forEach(year => {
        if (year <= maxYearIndex && data[year]) {
          extendedData[year] = data[year];
        }
      });
      // 不足している年次のデータを追加
      for (let year = 1; year <= maxYearIndex; year++) {
        if (!extendedData[year]) {
          // 金型・開発費・設備費用の空データを作成
          extendedData[year] = {
            price: 0,
            months: createEmptyPriceMonths()
          };
        }
      }
      return extendedData;
    };

    /**
     * 金型・開発費・設備費用の空月データを作成
     */
    const createEmptyPriceMonths = (): { [key: number]: MonthPriceData } => {
      const months: { [key: number]: MonthPriceData } = {};
      const monthOrder = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
      monthOrder.forEach((month, index) => {
        months[index + 1] = { month, price: 0 };
      });
      return months;
    };

    // 初期化処理
    const initializeMonthlyData = (tableArray: any[], startMonth: number, getDataSetter: (data: any[]) => void) => {
      const updatedTables = tableArray.map((table) => {
        const updatedData = { ...table.data };
        // yearsがundefinedの場合、dataのキーから年次を復元する（振り返りモード対応）
        const years = table.years || Object.keys(updatedData).map(Number).sort((a, b) => a - b);
        if (!years.length) return { ...table, years, data: updatedData };
        years.forEach((year: number) => {
          const months = updatedData[year]?.months || {};
          const visibleMonthKeys = getVisibleMonths(year, months, startMonth);

          // 表示すべきでない月のデータを初期化（プロジェクト開始日以前、EOL予定日以降）
          Object.keys(months).forEach((monthKey) => {
            const monthData = months[Number(monthKey)];
            const actualMonth = monthData?.month || Number(monthKey);

            if (!visibleMonthKeys.includes(Number(monthKey)) || isAfterEOLForInit(year, actualMonth)) {
              updatedData[year].months[monthKey].price = 0;
            }
          });
        });

        // EOL予定日の年度まで年次を拡張する
        const extendedYears = extendYearsToEOL(years);
        const extendedData = extendDataToEOL(updatedData, years);

        return { ...table, years: extendedYears, data: extendedData };
      });
      getDataSetter(updatedTables);
    };

    // 金型回収テーブル設定
    if (projectData?.moldSalesByProject) {
      let moldSalesByProject = projectData.moldSalesByProject;
      try {
        moldSalesByProject = JSON.parse(moldSalesByProject);
      } catch (error) {
        console.error("Failed to parse moldSalesByProject:", error);
        return;
      }
      if (Array.isArray(moldSalesByProject)) {
        initializeMonthlyData(moldSalesByProject, startMonth, setMoldTables);
      } else {
        console.error("moldSalesByProject is not an array");
      }
    }

    // 開発費回収テーブル設定
    if (projectData?.developmentSalesByProject) {
      let developmentSalesByProject = projectData.developmentSalesByProject;
      try {
        developmentSalesByProject = JSON.parse(developmentSalesByProject);
      } catch (error) {
        console.error("Failed to parse developmentSalesByProject:", error);
        return;
      }
      if (Array.isArray(developmentSalesByProject)) {
        initializeMonthlyData(developmentSalesByProject, startMonth, setDevelopmentCostTables);
      } else {
        console.error("developmentSalesByProject is not an array");
      }
    }

    // 設備費回収テーブル設定
    if (projectData?.equipmentSalesByProject) {
      let equipmentSalesByProject = projectData.equipmentSalesByProject;
      try {
        equipmentSalesByProject = JSON.parse(equipmentSalesByProject);
      } catch (error) {
        console.error("Failed to parse developmentSalesByProject:", error);
        return;
      }
      if (Array.isArray(equipmentSalesByProject)) {
        initializeMonthlyData(equipmentSalesByProject, startMonth, setEquipmentCostTables);
      } else {
        console.error("equipmentSalesByProject is not an array");
      }
    }

    const year = startDate.getFullYear();
    const month = startDate.getMonth() + 1;
    const fiscalYear = month >= 4 ? year : year - 1;
    setBaseStartYear(fiscalYear);
  }, [projectData]);

  useEffect(() => {
    const fetchConfirmor = async () => {
      try {
        // 確認担当者JSONを取得
        const confirmorData = await getConfirmor(
          WebAPI,
          projectId,
          reviewDate,
        );
        setConfirmor(confirmorData);
      } catch (error) {
        // TODO 処理失敗時は暫定でスルーする
      }
    };
    if (projectId) {
      fetchConfirmor();
    }
  }, [projectId, reviewDate]);

  useEffect(() => {
    if (confirmor?.quantityAndPrice) {
      const updatedNames = Object.keys(names).reduce((acc, division) => {
        const divisionData = confirmor.quantityAndPrice?.[division as keyof Confirmor['quantityAndPrice']];
        if (Array.isArray(divisionData)) {
          acc[division] = divisionData;
        } else {
          acc[division] = [];
        }
        return acc;
      }, {} as Record<string, ConfirmorInfo[]>);
      setNames(updatedNames);
    }
  }, [confirmor]);

  useEffect(() => {
    if (!quantityAndPriceTables.length || !projectData?.projectStartDate) return;

    const projectStartDate = projectData?.projectStartDate;
    const startMonth = projectStartDate ? parseInt(projectStartDate.split("-")[1], 10) : 4;

    const result = quantityAndPriceTables.map((table) => ({
      id: table.id,
      rows: generateRowsFromTable(table, baseStartYear, startMonth),
    }));

    setPriceRows(result);
  }, [quantityAndPriceTables, projectData]);

  useEffect(() => {
    if (!moldTables.length || !projectData?.projectStartDate) return;

    const projectStartDate = projectData?.projectStartDate;
    const startMonth = projectStartDate ? parseInt(projectStartDate.split("-")[1], 10) : 4;

    const result = moldTables.map((table) => ({
      id: table.id,
      rows: generateRowsFromTable(table, baseStartYear, startMonth),
    }));

    setMoldPriceRows(result);
  }, [moldTables, projectData]);

  useEffect(() => {
    if (!developmentCostTables.length || !projectData?.projectStartDate) return;

    const projectStartDate = projectData?.projectStartDate;
    const startMonth = projectStartDate ? parseInt(projectStartDate.split("-")[1], 10) : 4;

    const result = developmentCostTables.map((table) => ({
      id: table.id,
      rows: generateRowsFromTable(table, baseStartYear, startMonth),
    }));

    setDevelopmentCostPriceRows(result);
  }, [developmentCostTables, projectData]);

  useEffect(() => {
    if (!equipmentCostTables.length || !projectData?.projectStartDate) return;

    const projectStartDate = projectData?.projectStartDate;
    const startMonth = projectStartDate ? parseInt(projectStartDate.split("-")[1], 10) : 4;

    const result = equipmentCostTables.map((table) => ({
      id: table.id,
      rows: generateRowsFromTable(table, baseStartYear, startMonth),
    }));

    setEquipmentCostPriceRows(result);
  }, [equipmentCostTables, projectData]);

  useEffect(() => {
    if (!quantityAndPriceTables.length || !projectData?.projectStartDate) return;

    const projectStartDate = projectData?.projectStartDate;
    const startMonth = projectStartDate ? parseInt(projectStartDate.split("-")[1], 10) : 4;

    const result: { parentTableId: string; id: string; rows: PriceRows[] }[] = [];

    quantityAndPriceTables.forEach((partNoTable) => {
      if (partNoTable.partNoMoldTables && Array.isArray(partNoTable.partNoMoldTables)) {
        partNoTable.partNoMoldTables.forEach((moldTable) => {
          result.push({
            parentTableId: partNoTable.id,
            id: moldTable.id,
            rows: generateRowsFromTable(moldTable, baseStartYear, startMonth),
          });
        });
      }
    });

    setPartNoMoldPriceRows(result);
  }, [quantityAndPriceTables, projectData]);

  useEffect(() => {
    if (!quantityAndPriceTables.length || !projectData?.projectStartDate) return;

    const projectStartDate = projectData?.projectStartDate;
    const startMonth = projectStartDate ? parseInt(projectStartDate.split("-")[1], 10) : 4;

    const result: { parentTableId: string; id: string; rows: PriceRows[] }[] = [];

    quantityAndPriceTables.forEach((partNoTable) => {
      if (partNoTable.partNoDevelopmentCostTables && Array.isArray(partNoTable.partNoDevelopmentCostTables)) {
        partNoTable.partNoDevelopmentCostTables.forEach((devCostTable) => {
          result.push({
            parentTableId: partNoTable.id,
            id: devCostTable.id,
            rows: generateRowsFromTable(devCostTable, baseStartYear, startMonth),
          });
        });
      }
    });

    setPartNoDevelopmentCostPriceRows(result);
  }, [quantityAndPriceTables, projectData]);

  useEffect(() => {
    // 自由記入欄初期設定
    if (projectData?.quantityPriceFreeText) {
      setQuantityPriceFreeText(projectData?.quantityPriceFreeText || "")
    }
    // 事業の継続性の有無: 保存値があれば復元、なければデフォルト「あり」
    setBusinessContinuity(projectData?.businessContinuity || RADIO_VALUES.PART_INPUT);
  }, [projectData])


  /**
   * 初期表示
   */
  useEffect(() => {
    /**
     * GAPIロードとサインインを行う。
     */
    const initGapi = async () => {
      try {
        // Google API 初期化
        if (!await googleAPI.initGapi()) {
          throw new Error("initGapi error");
        }

        // Googleアカウント認証
        if (! await googleAPI.signIn()) {
          throw new Error("signIn error");
        }
      } catch (error) {
        console.error("Google API 初期化エラー:", error);
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.QuantityAndPrice.message.googleApiInitFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    };

    if (projectId) {
      // GAPIロードとサインイン
      initGapi();
    }
  }, [toast]);

  // -----------------------------
  // エラーチェック
  // -----------------------------
  /**
   * 年次データ内の月次データに対するチェックを実行
   *
   * @param months 月次データ ({ month: price })
   * @param errors エラーオブジェクト
   * @param errorKeyPrefix エラーのキーのプレフィックス
   * @param decimalCurrency 整数入力のみ必要な場合のフラグ
   */
  const validateMonthsPrice = (
    months: { [key: number]: { price: number } },
    errors: { [key: string]: string },
    errorKeyPrefix: string,
    decimalCurrency: boolean
  ) => {
    Object.values(months).forEach((monthData, monthIndex) => {
      const price = monthData?.price || 0;
      if (!decimalCurrency && price % 1 !== 0) {
        const monthKey = Object.keys(months).find(
          (key) => months[Number(key)] === monthData
        );
        errors[`${errorKeyPrefix}-${monthKey}-price`] = t('features.InvestmentScreening.QuantityAndPrice.validation.integerOnly');
      }
    });
  };

  const validateForm = () => {

    const newErrors: { [key: string]: string } = {};
    // PartNoの重複チェック用セット
    const partNoSet = new Set<string>();

    if (!projectId || projectId == "") {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.QuantityAndPrice.message.registerFromBasicInfo'),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // 簡易モード時は事業の継続性の有無のみが入力対象。他のバリデーションはスキップ。
    if (isSimpleMode) {
      setErrors({});
      return true;
    }

    // 入力がない場合
    if (quantityAndPriceTables.length === 0) {

      toast({
        title: t('common.label.inputError'),
        description: t('features.InvestmentScreening.QuantityAndPrice.validation.partNoRequired'),
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return false;
    }

    // PartNoテーブル
    quantityAndPriceTables.forEach((table) => {
      if (!table.partNo) {
        newErrors[`partNoTables-${table.id}-partNo`] = t('features.InvestmentScreening.QuantityAndPrice.validation.partNoRequired');
      }

      if (partNoSet.has(table.partNo)) {
        newErrors[`partNoTables-${table.id}-partNo`] = t('features.InvestmentScreening.QuantityAndPrice.validation.partNoDuplicate');
      } else {
        // マップにセット
        partNoSet.add(table.partNo);
      }

      // 通貨が未選択の場合
      if (table.currencyAndRatio.length === 0 ||
        table.currencyAndRatio.some(item => !item.id)) {
        newErrors[`currencyData-${table.id}`] = t('features.InvestmentScreening.QuantityAndPrice.validation.currencyRequired');
      } else {
        // 通貨の比率合計が100でない場合
        const totalRatio = table.currencyAndRatio.reduce((sum, item) => sum + item.ratio, 0);
        if (totalRatio !== 100) {
          newErrors[`ratio-${table.id}`] = t('features.InvestmentScreening.QuantityAndPrice.validation.ratioMustBe100');
        }
      }

      // 汎用設備の場合、新製品/既存製品比率は必須
      if (isGeneralPurpose && (table.newProductRatio === null || table.newProductRatio === undefined)) {
        newErrors[`newProductRatio-${table.id}`] = t('features.InvestmentScreening.QuantityAndPrice.validation.newProductRatioRequired');
      }

      const decimalCurrency = hasDecimal(selectedCurrency.find((item) => item.tableId === table.id)?.name ?? "");

      table.years.forEach((year, yearIndex) => {
        const yearMonths = table.data[year]?.months || {};
        validateMonthsPrice(yearMonths, newErrors, `partNoTables-${table.id}-${year}`, decimalCurrency);
      });

      if (table.moldRadioValue === RADIO_VALUES.PART_INPUT) {
        table.partNoMoldTables.forEach((moldTable) => {
          if (!moldTable.moldName) {
            newErrors[`partNoTables-${table.id}-${moldTable.id}-moldName`] = t('features.InvestmentScreening.QuantityAndPrice.validation.moldNameRequired');
          }

          // 月毎のデータを取得
          moldTable.years.forEach((year, yearIndex) => {
            const yearMonths = moldTable.data[year]?.months || {};

            Object.keys(yearMonths).forEach((monthKey, monthIndex) => {
              const monthData = yearMonths[Number(monthKey)];
              const price = monthData?.price || 0;
              const errorKey = `partNoTables-${table.id}-${moldTable.id}-${year}-${monthKey}-price`;

              if (!decimalCurrency && price % 1 !== 0) {
                newErrors[errorKey] = t('features.InvestmentScreening.QuantityAndPrice.validation.integerOnly');
              }
            });
          });
        });
      }

      if (table.developmentRadioValue === RADIO_VALUES.PART_INPUT) {
        table.partNoDevelopmentCostTables.forEach((developmentCostTable) => {
          if (!developmentCostTable.developmentCostName) {
            newErrors[`partNoTables-${table.id}-${developmentCostTable.id}-developmentCostName`] = t('features.InvestmentScreening.QuantityAndPrice.validation.devCostNameRequired');
          }

          developmentCostTable.years.forEach((year, yearIndex) => {
            const yearMonths = developmentCostTable.data[year]?.months || {};

            Object.keys(yearMonths).forEach((monthKey, monthIndex) => {
              const monthData = yearMonths[Number(monthKey)];
              const price = monthData?.price || 0;
              const errorKey = `partNoTables-${table.id}-${developmentCostTable.id}-${year}-${monthKey}-price`;

              if (!decimalCurrency && price % 1 !== 0) {
                newErrors[errorKey] = t('features.InvestmentScreening.QuantityAndPrice.validation.integerOnly');
              }
            });
          });
        });
      }
    });

    // partNoOptionsに設定されているPartNoがすべて設定されているかチェック
    const partNoOptionsSet = new Set(partNoOptions.map(option => option.value).filter((value): value is string => value !== undefined));
    const missingPartNos = Array.from(partNoOptionsSet).filter(partNo => !partNoSet.has(partNo));
    if (missingPartNos.length > 0) {
      newErrors[`partNoTables-missingPartNos`] = t('features.InvestmentScreening.QuantityAndPrice.validation.missingPartNos', { partNos: missingPartNos.join(", ") });
      toast({
        title: t('common.label.inputError'),
        description: t('features.InvestmentScreening.QuantityAndPrice.message.missingPartNos', { partNos: missingPartNos.join(", ") }),
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    // 金型回収テーブル
    moldTables.forEach((table) => {
      if (!table.moldName) {
        newErrors[`moldTables-${table.id}-moldName`] = t('features.InvestmentScreening.QuantityAndPrice.validation.moldNameRequired');
      }

      // 通貨が未選択の場合
      if (!table.currencyId) {
        newErrors[`moldCurrencyData-${table.id}`] = t('features.InvestmentScreening.QuantityAndPrice.validation.currencyRequired');
      }
      const decimalCurrency = hasDecimal(currencyData.find(currency => currency.currencyId === table.currencyId)?.currencyName!);

      table.years.forEach((year, yearIndex) => {
        const yearMonths = table.data[year]?.months || {};

        Object.keys(yearMonths).forEach((monthKey, monthIndex) => {
          const monthData = yearMonths[Number(monthKey)];
          const price = monthData?.price || 0;
          const errorKey = `moldTables-${table.id}-${year}-${monthKey}-price`;

          if (!decimalCurrency && price % 1 !== 0) {
            newErrors[errorKey] = t('features.InvestmentScreening.QuantityAndPrice.validation.integerOnly');
          }
        });
      });
    });

    // 開発費回収テーブル
    developmentCostTables.forEach((table) => {
      if (!table.developmentCostName) {
        newErrors[`developmentCostTables-${table.id}-developmentCostName`] = t('features.InvestmentScreening.QuantityAndPrice.validation.devCostNameRequired');
      }

      // 通貨が未選択の場合
      if (!table.currencyId) {
        newErrors[`developmentCostCurrencyData-${table.id}`] = t('features.InvestmentScreening.QuantityAndPrice.validation.currencyRequired');
      }
      const decimalCurrency = hasDecimal(currencyData.find(currency => currency.currencyId === table.currencyId)?.currencyName!);

      table.years.forEach((year, yearIndex) => {
        const yearMonths = table.data[year]?.months || {};

        Object.keys(yearMonths).forEach((monthKey, monthIndex) => {
          const monthData = yearMonths[Number(monthKey)];
          const price = monthData?.price || 0;
          const errorKey = `developmentCostTables-${table.id}-${year}-${monthKey}-price`;

          if (!decimalCurrency && price % 1 !== 0) {
            newErrors[errorKey] = t('features.InvestmentScreening.QuantityAndPrice.validation.integerOnly');
          }
        });
      });
    });

    // 設備費回収テーブル
    equipmentCostTables.forEach((table) => {
      if (!table.equipmentCostName) {
        newErrors[`equipmentCostTables-${table.id}-equipmentCostName`] = t('features.InvestmentScreening.QuantityAndPrice.validation.equipCostNameRequired');
      }

      // 通貨が未選択の場合
      if (!table.currencyId) {
        newErrors[`equipmentCostCurrencyData-${table.id}`] = t('features.InvestmentScreening.QuantityAndPrice.validation.currencyRequired');
      }
      const decimalCurrency = hasDecimal(currencyData.find(currency => currency.currencyId === table.currencyId)?.currencyName!);

      table.years.forEach((year, yearIndex) => {
        const yearMonths = table.data[year]?.months || {};

        Object.keys(yearMonths).forEach((monthKey, monthIndex) => {
          const monthData = yearMonths[Number(monthKey)];
          const price = monthData?.price || 0;
          const errorKey = `equipmentCostTables-${table.id}-${year}-${monthKey}-price`;

          if (!decimalCurrency && price % 1 !== 0) {
            newErrors[errorKey] = t('features.InvestmentScreening.QuantityAndPrice.validation.integerOnly');
          }
        });
      });
    });

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      toast({
        title: t('common.label.inputError'),
        description: t('features.InvestmentScreening.QuantityAndPrice.message.inputError'),
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
    }

    return Object.keys(newErrors).length === 0;
  };

  // -----------------------------
  // API
  // -----------------------------
  /**
   * 確認ボタン押下時処理
   */
  const handleConfirm = async (e: React.MouseEvent<HTMLButtonElement>) => {
    // ボタンのフォーカスを外す
    e.currentTarget.blur();
    const partNoTableResult: QuantityAndPriceTableRegist[] = [];
    const partNoMoldTableResult: MoldMonthTable[] = [];
    const partNoDevelopmentCostTableResult: DevelopmentMonthCostTable[] = [];

    quantityAndPriceTables.forEach((table) => {
      const partNo = table.partNo;
      if (!partNo) { return; }

      // partNoMoldTablesがない場合、moldRadioValueをPART_NOT_INPUTに変更
      const moldRadioValue = table.partNoMoldTables.length === 0 ? RADIO_VALUES.PART_NOT_INPUT : table.moldRadioValue;
      const developmentRadioValue = table.partNoDevelopmentCostTables.length === 0 ? RADIO_VALUES.PART_NOT_INPUT : table.developmentRadioValue;

      const partNoItem = partNoData.find(p => p.partNo === partNo);

      const currencyAndRatioJson = JSON.stringify(table.currencyAndRatio.filter(item => item.ratio !== 0));
      console.log("currencyAndRatioJson:", currencyAndRatioJson);

      // partNoTableのデータを整形
      partNoTableResult.push({
        id: table.id,
        partNo: table.partNo,
        years: table.years,
        data: table.data,
        moldRadioValue: moldRadioValue,
        developmentRadioValue: developmentRadioValue,
        updateDate: partNoItem?.updateDate,
        currencyAndRatio: currencyAndRatioJson,
        newProductRatio: table.newProductRatio,
        isSetSalesInput: table.isSetSalesInput,
        //        partNoMoldTables: table.partNoMoldTables,
        //        partNoDevelopmentCostTables: table.partNoDevelopmentCostTables,
      });

      // partNoMoldTablesのデータを整形
      table.partNoMoldTables.forEach((moldTable) => {
        partNoMoldTableResult.push({
          id: moldTable.id,
          partNo: moldTable.partNo,
          years: moldTable.years,
          moldName: moldTable.moldName,
          data: moldTable.data,
        });
      });

      // partNoDevelopmentCostTablesのデータを整形
      table.partNoDevelopmentCostTables.forEach((developmentCostTable) => {
        partNoDevelopmentCostTableResult.push({
          id: developmentCostTable.id,
          partNo: developmentCostTable.partNo,
          years: developmentCostTable.years,
          developmentCostName: developmentCostTable.developmentCostName,
          data: developmentCostTable.data,
        });
      });
    });

    const quantityAndPriceJson = JSON.stringify(partNoTableResult);
    console.log("quantityAndPriceJson:", quantityAndPriceJson);

    const partNoMoldJson = JSON.stringify(partNoMoldTableResult);
    console.log("partNoMoldJson:", partNoMoldJson);

    const partNoDevelopmentCostJson = JSON.stringify(partNoDevelopmentCostTableResult);
    console.log("partNoDevelopmentCostJson:", partNoDevelopmentCostJson);


    const moldTableProjectResult: MoldMonthTableByProject[] = [];
    // moldテーブルのデータをprojectに紐づくデータ、partNoに紐づくデータに分けて整形
    // {"id":"mold-1739172828523","years":[1,2,3,4,5],"moldName":"test2","data":{"1":{"price":1},・・・]
    moldTables.forEach((table) => {
      const result = {
        id: table.id,
        partNo: "",
        years: table.years,
        moldName: table.moldName,
        data: table.data,
        currencyId: table.currencyId
      };
      moldTableProjectResult.push(result);
    });
    const moldProjectJson = JSON.stringify(moldTableProjectResult);
    console.log("moldProjectJson:", moldProjectJson);

    const devCostTableProjectResult: DevelopmentMonthCostTableByProject[] = [];
    // developmentCostテーブルのデータをprojectに紐づくデータ、partNoに紐づくデータに分けて整形
    // {"id":"developmentCost-1739176394411","years":[1,2,3,4,5],"developmentCostName":"開発費１","data":{"1":{"price":1},・・・
    developmentCostTables.forEach((table) => {
      const result = {
        id: table.id,
        partNo: "",
        years: table.years,
        developmentCostName: table.developmentCostName,
        data: table.data,
        currencyId: table.currencyId
      };
      devCostTableProjectResult.push(result);
    });
    const devCostProjectJson = JSON.stringify(devCostTableProjectResult);
    console.log("devCostProjectJson:", devCostProjectJson);

    const equipmentCostTableProjectResult: EquipmentMonthCostTableByProject[] = [];
    // equipmentCostテーブルのデータをprojectに紐づくデータ、partNoに紐づくデータに分けて整形
    // {"id":"equipmentCost-1739176394411","years":[1,2,3,4,5],"equipmentCostName":"設備費１","data":{"1":{"price":1},・・・
    equipmentCostTables.forEach((table) => {
      const result = {
        id: table.id,
        partNo: "",
        years: table.years,
        equipmentCostName: table.equipmentCostName,
        data: table.data,
        currencyId: table.currencyId
      };
      equipmentCostTableProjectResult.push(result);
    });
    const equipmentCostProjectJson = JSON.stringify(equipmentCostTableProjectResult);
    console.log("equipmentCostProjectJson:", equipmentCostProjectJson);

    // 入力データをオブジェクトにまとめる
    // 簡易モード時はテーブルがないため currencyId は空文字で保存。詳細モードは従来通り。
    const input: PartNoRegisterInput = {
      projectId: projectId,
      currencyId: isSimpleMode
        ? ""
        : selectedCurrency[0].id,
      quantityAndPrice: quantityAndPriceJson,
      moldSalesByProject: moldProjectJson,
      developmentSalesByProject: devCostProjectJson,
      equipmentSalesByProject: equipmentCostProjectJson,
      developmentSalesByPartNo: partNoDevelopmentCostJson,
      moldSalesByPartNo: partNoMoldJson,
      quantityPriceFreeText: !quantityPriceFreeText?.trim() ? "" : quantityPriceFreeText,
      businessContinuity: businessContinuity,
      updateDate: projectData?.updateDate,
      ...(reviewDate && { reviewDate }),
    };

    try {
      await updatePartNo(input);

      try {
        // ワークフローテーブルを更新
        await updateConfirm(
          WebAPI,
          projectId,
          WorkflowId.QuantityAndPrice,
          employeeId,
          confirmSource,
          "true",
          reviewDate,
        );
        // 確認担当者JSONを取得
        const confirmor = await getConfirmor(
          WebAPI,
          projectId,
          reviewDate,
        );
        setConfirmor(confirmor);
      } catch (error) {
        // TODO 処理失敗時は暫定でスルーする
      }

    } catch (error) {
      console.error('handleConfirm:', error);
    } finally {
      onClose();
    }
  };

  // プロジェクト情報取得API
  const getProjectWithRds = async () => {
    try {
      console.log(`getProjectWithRds():start`);
      // プロジェクト情報にRDSのデータを含める(useRDSData:true)
      const response = await WebAPI.request({
        query: `
          query getProjectWithRds($projectId: String, $useRDSData: Boolean, $reviewDate: String) {
            getProjectById(projectId: $projectId, useRDSData: $useRDSData, reviewDate: $reviewDate) {
              projectId
              projectName
              revisedOpportunityNameByRevision
              moldSalesByProject
              developmentSalesByProject
              equipmentSalesByProject
              projectStartDate
              projectEndDate
              purpose
              applicationFormType
              createDate
              updateDate
              currencyId
              quantityPriceFreeText
              businessContinuity
              # RDS Data
              opportunityName
              registrantDivisionId
              updateDateRDS
            }
          }`,
        variables: {
          projectId: projectId,
          useRDSData: true,
          ...(reviewDate && { reviewDate })
        }
      });
      // データ取得に成功した場合
      if (response && response.data && response.data.getProjectById) {
        setProjectData(response.data.getProjectById);
        return true;
      }

      // データ取得失敗の場合
      toast({
        title: t('features.InvestmentScreening.QuantityAndPrice.message.dataFetchErrorTitle'),
        description: t('features.InvestmentScreening.QuantityAndPrice.message.projectFetchFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return false;
    } catch (error) {
      console.error("Error in getProjectWithRds:", error);
      return false;
    }
  };

  // PartNo取得API
  const getPartNo = async () => {
    try {
      console.log(`getPartNo():start`);
      const response = await WebAPI.request({
        query: `
          query getPartNo($projectId: String, $reviewDate: String) {
            getPartNo(projectId: $projectId, reviewDate: $reviewDate) {
              partNo
              displayPartNo
              isSetSalesInput
              quantityAndPrice
              moldSalesByPartNo
              developmentSalesByPartNo
              createDate
              updateDate
            }
          }`,
        variables: {
          projectId: projectId,
          ...(reviewDate && { reviewDate })
        }
      });
      if (response && response.data && response.data.getPartNo) {
        setPartNoData(response.data.getPartNo);
        return true;
      }
      // データ取得失敗の場合
      toast({
        title: t('features.InvestmentScreening.QuantityAndPrice.message.dataFetchErrorTitle'),
        description: t('features.InvestmentScreening.QuantityAndPrice.message.partNoFetchFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return false;
    } catch (error) {
      console.error("Error in getPartNo:", error);
      return false;
    }
  };

  // PartNo登録API
  const updatePartNo = async (input: PartNoRegisterInput) => {
    try {
      console.log("updatePartNo:start");

      // GraphQLクエリ定義
      const query = `
        mutation updatePartNo ($input: PartNoInput!) {
          updatePartNo(input: $input) {status}
        }
      `;

      // APIリクエスト実行
      const result = await WebAPI.request({
        query: query,
        variables: { input }
      });

      if (result?.data?.updatePartNo.status === 'success') {
        toast({
          title: t('common.label.success'),
          description: t('features.InvestmentScreening.QuantityAndPrice.message.registerSuccess', { title: TITLE }),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        // 再取得
        await getDispData();

      } else if (result?.data?.updatePartNo.status === 'expired') {
        toast({
          title: TITLE,
          description: Messages.expiredMessage,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        console.warn(`Conflict error. updatePartNo.`);
      } else {
        throw new Error(result);
      }
    } catch (error) {
      toast({
        title: t('common.label.failed'),
        description: t('common.api.commonError'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      console.error('updatePartNo:', error);
    }
  };

  // -----------------------------
  // リストボックス
  // -----------------------------
  // partNoリスト
  const partNoOptions = partNoData.map((partNoItem) => ({
    value: partNoItem.partNo,
    label: resolveDisplayPartNo(partNoItem),
  }));

  // 通貨リスト
  const currencyOptions = currencyData.map((currencyItem) => ({
    value: currencyItem.currencyId,
    label: currencyItem.currencyName,
  }));

  // -----------------------------
  // ロジック
  // -----------------------------
  /**
   * PartNoごとの月次数量 (quantity) の合計計算
   * @param years 年度の配列 (例: [1, 2, 3])
   * @param data 年度ごとのデータ構造 ({ year: YearData })
   * @returns Decimal型の数量合計
   * 
   */
  const calculateTotalQuantity = (years: number[], data: { [key: number]: YearData }): Decimal => {
    return years.reduce((total, year) => {
      const yearMonths = data[year]?.months || {};
      const yearQuantity = Object.values(yearMonths).reduce(
        (sum, monthData) => sum.plus(monthData.quantity || 0),
        new Decimal(0)
      );
      return total.plus(yearQuantity);
    }, new Decimal(0));
  };

  /**
   * PartNoごとの売上金額 (quantity * price) の合計計算と加重平均単価計算
   * @param years 年度の配列 (例: [1, 2, 3])
   * @param data 年度ごとのデータ構造 ({ year: YearData })
   * @returns 売上合計金額 (`totalSales`) と加重平均単価 (`weightedAveragePrice`)
   */
  const calculateTotalSales = (years: number[], data: { [key: number]: YearData }): { totalSales: Decimal; weightedAveragePrice: Decimal } => {
    let totalQuantity = new Decimal(0); // 全年度の数量合計
    let weightedSum = new Decimal(0);   // 数量 * 単価の合計

    // 年度ごとの売上合計を計算
    const totalSales = years.reduce((total, year) => {
      const yearMonths = data[year]?.months || {};
      return Object.values(yearMonths).reduce(
        (sum, monthData) => {
          const quantity = new Decimal(monthData.quantity || 0);
          const price = new Decimal(monthData.price || 0);
          const monthlySales = quantity.times(price);

          // 加重平均計算用の値を集計
          // 数量合計
          totalQuantity = totalQuantity.plus(quantity);
          // 数量*単価合計
          weightedSum = weightedSum.plus(quantity.times(price));

          // 売上合計の累積
          return sum.plus(monthlySales);
        },
        total
      );
    }, new Decimal(0));

    // 売上合計を小数点以下3桁に丸める
    const roundedTotalSales = totalSales.toDecimalPlaces(3);

    // 加重平均単価の計算（総数量が0の場合は単価を0とする）
    const weightedAveragePrice = totalQuantity.equals(0)
      ? new Decimal(0)
      : weightedSum.dividedBy(totalQuantity).toDecimalPlaces(3);

    return { totalSales: roundedTotalSales, weightedAveragePrice };
  };

  /**
   * 金型または開発費の金額合計計算
   * @param years 年度の配列 (例: [1, 2, 3])
   * @param data 年度ごとのデータ構造 ({ year: YearPriceData })
   * @returns Decimal型の金額の合計
   */
  const calculateTotalPrice = (years: number[], data: { [key: number]: YearPriceData }): Decimal => {
    return years.reduce((total, year) => {
      const yearMonths = data[year]?.months || {};
      const yearSales = Object.values(yearMonths).reduce(
        (sum, monthData) =>
          sum.plus(new Decimal(monthData.price || 0)),
        new Decimal(0)
      );
      return total.plus(yearSales);
    }, new Decimal(0));
  };

  /**
   * 総合計金額を計算
   *
   * @param table 対象のテーブルデータ
   * @returns Decimal型の総合計金額
   */
  const calculateGrandTotal = (table: QuantityAndPriceMonthTable): Decimal => {
    // 製品売上高の合計、単価(加重平均)
    const { totalSales, weightedAveragePrice } = calculateTotalSales(table.years, table.data);

    // 金型回収合計
    const moldTotal = Array.isArray(table.partNoMoldTables)
      ? table.partNoMoldTables.reduce((sum, moldTable) =>
        sum.plus(calculateTotalPrice(moldTable.years, moldTable.data)),
        new Decimal(0)
      )
      : new Decimal(0);

    // 開発費回収合計
    const developmentCostTotal = Array.isArray(table.partNoDevelopmentCostTables)
      ? table.partNoDevelopmentCostTables.reduce((sum, developmentCostTable) =>
        sum.plus(calculateTotalPrice(developmentCostTable.years, developmentCostTable.data)),
        new Decimal(0)
      )
      : new Decimal(0);

    // 総合計を計算
    return new Decimal(0).plus(totalSales).plus(moldTotal).plus(developmentCostTotal);
  };

  // -----------------------------
  // 画面
  // -----------------------------
  // /**
  //  * 月次のフィルタリングを行う関数
  //  * @param year 対象年度
  //  * @param months 月次データ ({ month: ... })
  //  * @param startMonth プロジェクト開始月
  //  * @returns 表示すべき月のキー配列
  //  */
  // const getVisibleMonths = (year: number, months: { [key: number]: { month: number } }, startMonth: number): number[] => {
  //   return Object.keys(months).map((key) => Number(key)).filter((monthKey) => {
  //     const month = months[monthKey]?.month;
  //     if (year === 1) {
  //       if (startMonth >= 1 && startMonth <= 3) {
  //         // プロジェクト開始月が1～3月の場合
  //         return month >= startMonth && month <= 3;
  //       } else if (startMonth >= 4 && startMonth <= 12) {
  //         // プロジェクト開始月が4～12月の場合
  //         return (month >= startMonth && month <= 12) || month <= 3;
  //       }
  //     }
  //     // 2年目以降は全て表示
  //     return true;
  //   });
  // };

  /**
   * テーブル内の列数を計算する関数
   * @param table テーブルデータ
   * @param projectStartDate プロジェクト開始日
   * @returns 動的な列数
   */
  const getDynamicColumnCount = (
    table: QuantityAndPriceMonthTable | MoldMonthTable | DevelopmentMonthCostTable | EquipmentMonthCostTable,
    projectStartDate: string | null | undefined
  ): number => {
    const startMonth = projectStartDate
      ? parseInt(projectStartDate.split("-")[1], 10)
      : 4; // デフォルトで4月を設定

    return table.years.reduce((totalMonths, year) => {
      const months = table.data[year]?.months || {};

      const visibleMonthKeys = Object.keys(months)
        .map((key) => Number(key)) // キーを数値型に変換
        .filter((monthKey) => {
          const month = months[monthKey]?.month; // 月番号を取得
          let isVisible = false;

          if (year === 1) {
            // 初年度の場合のフィルター条件
            if (startMonth >= 1 && startMonth <= 3) {
              // プロジェクト開始月が1～3月の場合は、その月以降を含む (翌年の1～3月も含める)
              isVisible = month >= startMonth && month <= 3;
            } else if (startMonth >= 4 && startMonth <= 12) {
              // プロジェクト開始月が4～12月の場合は、開始月以降と翌年度1–3月も含める
              isVisible = month >= startMonth || month <= 3;
            }
          } else {
            // 通常年度（2年目以降）は全て表示
            isVisible = true;
          }

          return isVisible;
        });

      return totalMonths + visibleMonthKeys.length;
    }, 0);
  };

  //  /**
  //   * テーブル内の列数を計算する関数
  //   * @param table 表データ
  //   * @returns 動的な列数
  //   */
  //  const getDynamicColumnCount = (table: QuantityAndPriceMonthTable | MoldMonthTable | DevelopmentMonthCostTable): number => {
  //    return table.years.reduce((totalMonths, year, yearIndex) => {
  //      const months = table.data[year]?.months || {}; // 年度ごとのデータ
  //
  //      // フィルタリング処理：初年度のみ表示件数を動的に制御
  //      const visibleMonthKeys = year === 1 && projectData?.projectStartDate
  //        ? Object.keys(months).filter((key) => {
  //          // プロジェクト開始月
  //          const startMonth = parseInt(projectData?.projectStartDate?.split("-")[1] || "0", 10);
  //          const month = months[Number(key)]?.month; // 月番号
  //
  //          if (startMonth >= 1 && startMonth <= 3) {
  //            // 開始月が1～3月の場合：翌年3月までをカウント
  //            return month >= startMonth && month <= 3;
  //          } else {
  //            // 開始月が4～12月の場合：その年の12月までをカウント
  //            return month >= startMonth && month <= 12;
  //          }
  //        }).map(Number) // フィルタリング結果を数値列に変換
  //      // 通常年度は全データをカウント
  //      : Object.keys(months).map(Number);
  //      // 初年度分の件数を加算
  //      return totalMonths + visibleMonthKeys.length;
  //    // 初期値0
  //    }, 0);
  //  };

  /**
   * 年月表示処理関数
   * @param table テーブルデータ
   * @param projectStartDate プロジェクト開始日
   * @param baseStartYear ベースになる年度
   * @returns JSX 要素の配列
   */
  const generateYearMonthCells = (
    table: QuantityAndPriceMonthTable | MoldMonthTable | DevelopmentMonthCostTable | EquipmentMonthCostTable,
    projectStartDate: string | null | undefined,
    baseStartYear: number
  ): JSX.Element[] => {
    const startMonth = projectStartDate ? parseInt(projectStartDate.split("-")[1], 10) : 4;

    return table.years.flatMap((year) => {
      const months = table.data[year]?.months || {};
      const visibleMonthKeys = getVisibleMonths(year, months, startMonth);

      return visibleMonthKeys.map((monthKey) => {
        const month = months[monthKey]?.month;

        // 年度表示の計算ロジック
        const fiscalYear =
          month >= 4 && month <= 12
            ? baseStartYear + year - 1
            : baseStartYear + year;

        return (
          <Text textAlign="center" fontWeight="bold" key={`header-${year}-${monthKey}`}>
            {t('common.dateFormat.yearMonth', { year: fiscalYear, month })}
          </Text>
        );
      });
    });
  };

  //  /**
  //   * 年月表示処理関数
  //   * @param table テーブルデータ
  //   * @param baseStartYear ベースになる年度
  //   * @returns JSX 要素の配列
  //   */
  //  const generateYearMonthCells = (
  //    table: QuantityAndPriceMonthTable | MoldMonthTable | DevelopmentMonthCostTable,
  //    baseStartYear: number
  //  ): JSX.Element[] => {
  //    return table.years.flatMap((year, yearIndex) => {
  //      const months = table.data[year]?.months || {};
  //
  //      // フィルタリング: 初年度の条件を分岐させる
  //      const visibleMonthKeys = Object.keys(months).filter((key) => {
  //        const month = months[Number(key)]?.month; // 月番号
  //        if (year === 1 && projectData?.projectStartDate) {
  //          // プロジェクト開始月
  //          const startMonth = parseInt(projectData.projectStartDate.split("-")[1], 10);
  //
  //          if (startMonth >= 1 && startMonth <= 3) {
  //            // 開始月が1～3月の場合：翌年3月までを表示
  //            return month >= startMonth && month <= 3;
  //          } else {
  //            // 開始月が4～12月の場合：その年の12月までを表示
  //            return month >= startMonth && month <= 12;
  //          }
  //        }
  //        return true; // 通常年度の場合はすべて表示
  //      }).map(Number); // フィルタリング後キーを数値化
  //
  //      return visibleMonthKeys.map((monthKey) => {
  //        const month = months[monthKey]?.month;
  //
  //        // 年度表示の計算（初年度の翌年分を考慮）
  //        const fiscalYear =
  //          year === 1 && month < 4
  //            ? baseStartYear + year // 翌年 (例: 2025年)
  //            : baseStartYear + year - 1; // 通常計算
  //
  //        return (
  //          <Text textAlign="center" fontWeight="bold">{t('common.dateFormat.yearMonth', { year: fiscalYear, month })}</Text>
  //        );
  //      });
  //    });
  //  };

  /**
   * 指定された年・月がEOL予定日を超えているかを判定
   * @param year 年次（1年目、2年目など）
   * @param month 月次（1～12の数字）
   * @param baseYear 基準年度
   * @param startMonth プロジェクト開始月
   * @param projectEndDate EOL予定日（YYYY-MM-DD形式）
   * @returns EOL予定日を超えている場合はtrue
   */
  const isAfterEOL = (
    year: number,
    month: number,
    baseYear: number,
    startMonth: number,
    projectEndDate: string | null | undefined
  ): boolean => {
    // EOL予定日が設定されていない場合は非活性にしない
    if (!projectEndDate) {
      return false;
    }

    // EOL予定日の年月を取得
    const eolYear = parseInt(projectEndDate.split("-")[0], 10);
    const eolMonth = parseInt(projectEndDate.split("-")[1], 10);

    // 現在の年度を計算
    // 4月～12月は当年度、1月～3月は前年度
    let actualYear: number;
    if (year === 1) {
      // 初年度の場合
      if (startMonth >= 4 && startMonth <= 12) {
        // 開始月が4～12月の場合
        actualYear = month >= 4 ? baseYear : baseYear + 1;
      } else {
        // 開始月が1～3月の場合
        actualYear = baseYear + 1;
      }
    } else {
      // 2年目以降
      actualYear = month >= 4 ? baseYear + year - 1 : baseYear + year;
    }

    // 年月を比較（YYYYMM形式で比較）
    const currentYearMonth = actualYear * 100 + month;
    const eolYearMonth = eolYear * 100 + eolMonth;

    return currentYearMonth > eolYearMonth;
  };

  /**
   * EOL予定日の年度を計算し、表示すべき最大年次を返す（ボタン制御用）
   * @returns EOL予定日の最大年次（EOL予定日が未設定の場合はnull）
   */
  const getMaxYearIndexByEOL = (): number | null => {
    if (!projectData?.projectEndDate || !projectData?.projectStartDate) {
      return null;
    }

    const projectEndDate = projectData.projectEndDate;
    const eolYear = parseInt(projectEndDate.split("-")[0], 10);
    const eolMonth = parseInt(projectEndDate.split("-")[1], 10);

    // EOL予定日の年度を計算（4月～翌3月が1年度）
    const eolFiscalYear = eolMonth >= 4 ? eolYear : eolYear - 1;
    // 基準年度からの差分 + 1（1年目から始まるため）
    const maxYear = eolFiscalYear - baseStartYear + 1;
    return maxYear > 0 ? maxYear : 1;
  };

  /**
   * GridItems生成関数
   * @param table テーブルデータ
   * @param projectStartDate プロジェクト開始日
   * @param renderCell セルを描画するコールバック関数（isDisabledを追加）
   * @returns JSX.Element[] フィルタリングされた月次で描画したセルの配列
   */
  const generateGridItems = (
    table: QuantityAndPriceMonthTable | MoldMonthTable | DevelopmentMonthCostTable | EquipmentMonthCostTable,
    projectStartDate: string | null | undefined,
    renderCell: (monthData: any, year: number, monthKey: number, index: number, isDisabled: boolean) => JSX.Element
  ): JSX.Element[] => {
    const startMonth = projectStartDate ? parseInt(projectStartDate.split("-")[1], 10) : 4;

    return table.years.flatMap((year) => {
      const months = table.data[year]?.months || {};
      const visibleMonthKeys = getVisibleMonths(year, months, startMonth);

      return visibleMonthKeys.map((monthKey, monthIndex) => {
        const monthData = months[monthKey];
        const actualMonth = monthData?.month || monthKey;
        // EOL予定日を超えているかを判定
        const isDisabled = isAfterEOL(year, actualMonth, baseStartYear, startMonth, projectData?.projectEndDate);
        return renderCell(monthData, year, monthKey, monthIndex, isDisabled);
      });
    });
  };

  //  /**
  //   * 共通コンポーネント生成
  //   * @param table テーブルデータ
  //   * @param projectStartDate プロジェクト開始日
  //   * @param renderCell セルを描画するコールバック
  //   * @returns JSX.Element[] フィルタリングされた月次を元に描画したセルの配列
  //   */
  //  const generateGridItems = (
  //    table: QuantityAndPriceMonthTable | MoldMonthTable | DevelopmentMonthCostTable,
  //    projectStartDate: string | null | undefined,
  //    renderCell: (monthData: any, year: number, monthKey: number, index: number) => JSX.Element
  //  ): JSX.Element[] => {
  //    return table.years.flatMap((year, yearIndex) => {
  //      const yearMonths = table.data[year]?.months || {};
  //
  //      // 初年度と通常年度のフィルタリング
  //      const visibleMonthKeys = year === 1 && projectStartDate
  //        ? Object.keys(yearMonths).filter((key) => {
  //          const startMonth = parseInt(projectStartDate.split("-")[1], 10);
  //          const month = yearMonths[Number(key)]?.month;
  //
  //          if (startMonth >= 1 && startMonth <= 3) {
  //            return month >= startMonth && month <= 3; // 初年度1～3月で検索
  //          } else {
  //            return month >= startMonth && month <= 12; // 初年度4～12月に限定
  //          }
  //        }).map(Number)
  //      : Object.keys(yearMonths).map(Number); // 通常年度は全件表示
  //
  //      // フィルタリングされた月次でセルを生成
  //      return visibleMonthKeys.map((monthKey, monthIndex) => {
  //        const monthData = yearMonths[Number(monthKey)];
  //        // 渡されたコールバックで描画
  //        return renderCell(monthData, year, Number(monthKey), monthIndex);
  //      });
  //    });
  //  };

  /**
   * 年次・月次をフォーマットして表示するテキストコンポーネント
   *
   * @param year 年次（1年目、2年目など）
   * @param baseStartYear 基準年度
   * @param month 月次（1～12の数字を指定）
   */
  const formatYearAndMonthDisplay = (year: number, baseStartYear: number, month: number, isFirstYearDynamic: boolean = false) => {

    const startDate = projectData?.projectStartDate;
    const startMonth = startDate ? parseInt(startDate.split("-")[1], 10) : 4;

    // 年度計算
    const fiscalYear =
      isFirstYearDynamic && year === 1
        ? month >= startMonth // プロジェクト開始月の設定
          ? getFiscalYear(baseStartYear, year - 1)
          : getFiscalYear(baseStartYear, year - 1) + 1
        : month >= 4
          ? getFiscalYear(baseStartYear, year - 1)
          : getFiscalYear(baseStartYear, year - 1) + 1;

    return (
      <>
        {/* 年度と月次 */}
        <Text textAlign="center" fontWeight="bold">{t('common.dateFormat.yearMonth', { year: fiscalYear, month })}</Text>
      </>
    );
  };

  /**
   * 開始年月～終了年月 設定値の入力コンポーネント
   *
   * @param table テーブル情報
   */
  const renderSection = <T extends QuantityAndPriceMonthTable | MoldMonthTableByProject | DevelopmentMonthCostTableByProject | EquipmentMonthCostTableByProject>(
    title: string,
    setRows: React.Dispatch<React.SetStateAction<{ id: string; rows: PriceRows[] }[]>>,
    setTables: React.Dispatch<React.SetStateAction<T[]>>,
    targetRows: { id: string; rows: PriceRows[] }[],
    table: QuantityAndPriceMonthTable | MoldMonthTableByProject | DevelopmentMonthCostTableByProject | EquipmentMonthCostTableByProject,
    currencyName?: string
  ) => {
    const matched = targetRows.find((item) => item.id === table.id);
    const rows = matched ? matched.rows : [];

    const projectStartDate = projectData?.projectStartDate;
    const startMonth = projectStartDate ? parseInt(projectStartDate.split("-")[1], 10) : 4;
    const fiscalYear =
      startMonth >= 4 && startMonth <= 12
        ? baseStartYear
        : baseStartYear + 1;

    const startYearMonth = `${fiscalYear}-${String(startMonth).padStart(2, "0")}`;

    // 終了年月の上限：EOL予定日と量産開始日から10年後のうち早い方を使用
    const projectEndDate = projectData?.projectEndDate;
    let endYearMonth: string;

    // 量産開始日から10年間（年度末の3月）を計算
    let maxEndYear: number | null = null;
    let maxEndMonth: number | null = null;
    if (projectStartDate) {
      const startY = parseInt(projectStartDate.split("-")[0], 10);
      const startM = parseInt(projectStartDate.split("-")[1], 10);
      // 10年間の年度末を上限とする（例：2025/05開始なら2035/03まで）
      // 年度は4月始まりなので、4月以降開始なら+10年の3月、1-3月開始なら+9年の3月
      maxEndYear = startM >= 4 ? startY + 10 : startY + 9;
      maxEndMonth = 3;
    }

    if (projectEndDate) {
      const eolYear = parseInt(projectEndDate.split("-")[0], 10);
      const eolMonth = parseInt(projectEndDate.split("-")[1], 10);
      // EOL予定日と10年間の年度末を比較して早い方を使用
      if (maxEndYear !== null && maxEndMonth !== null) {
        const eolDate = new Date(eolYear, eolMonth - 1);
        const maxDate = new Date(maxEndYear, maxEndMonth - 1);
        if (eolDate <= maxDate) {
          endYearMonth = `${eolYear}-${String(eolMonth).padStart(2, "0")}`;
        } else {
          endYearMonth = `${maxEndYear}-${String(maxEndMonth).padStart(2, "0")}`;
        }
      } else {
        endYearMonth = `${eolYear}-${String(eolMonth).padStart(2, "0")}`;
      }
    } else if (maxEndYear !== null && maxEndMonth !== null) {
      // EOL予定日が未設定の場合は10年間の年度末を使用
      endYearMonth = `${maxEndYear}-${String(maxEndMonth).padStart(2, "0")}`;
    } else {
      endYearMonth = `${baseStartYear + table.years.length}-${"03"}`;
    }

    const addRow = (rowId: number) => {
      setRows((prev) => {
        const target = prev.find((t) => t.id === table.id);
        if (!target) return prev;

        const last = target.rows[target.rows.length - 1];
        if (!last?.endMonth) return prev;
        const nextStart = getMonth(last.endMonth, 1);

        const newRow: PriceRows = {
          id: rowId + 1,
          displayStartMonth: nextStart,
          displayEndMonth: "",
          startMonth: nextStart,
          endMonth: "",
          value: 0,
        };

        const updatedRows = [...target.rows, newRow];

        return prev.map((t) =>
          t.id === table.id ? { ...t, rows: updatedRows } : t
        );
      });
    };

    const removeRow = (rowId: number) => {
      setRows((prev) => {
        const target = prev.find((t) => t.id === table.id);
        if (!target) return prev;

        updateRow(rowId, "value", 0);

        const updatedRows = target.rows.filter((row) => row.id !== rowId);

        return prev.map((t) =>
          t.id === table.id ? { ...t, rows: updatedRows } : t
        );
      });
    };

    // 表示用年月を更新
    const handleDisplayMonthChange = (
      rowId: number,
      field: "startMonth" | "endMonth",
      value: string
    ) => {
      const displayField = field === "startMonth" ? "displayStartMonth" : "displayEndMonth";

      setRows(prev =>
        prev.map(tableItem =>
          tableItem.id === table.id
            ? {
              ...tableItem,
              rows: tableItem.rows.map(r =>
                r.id === rowId
                  ? { ...r, [displayField]: value }
                  : r
              ),
            }
            : tableItem
        )
      );
    };

    const updateRow = (
      rowId: number,
      field: "startMonth" | "endMonth" | "value",
      value: string | number | null
    ) => {
      setRows((prev) =>
        prev.map((tableRows) => {
          if (tableRows.id !== table.id) return tableRows;

          const targetRows = tableRows.rows.map((row) =>
            row.id === rowId ? { ...row, [field]: value } : row
          );

          const updatedRows = setUpdatedRows(rowId, field, value, tableRows);

          for (const updatedRow of updatedRows) {
            if (
              updatedRow?.startMonth &&
              updatedRow?.endMonth &&
              !(field === "endMonth" &&
                updatedRow?.value === 0 &&
                updatedRows.indexOf(updatedRow) === 0 &&
                targetRows[targetRows.length - 1].id === rowId) &&
              !(field === "startMonth" &&
                updatedRow?.value === 0 &&
                updatedRows.indexOf(updatedRow) === 0)
            ) {
              const [startY, startM] = updatedRow.startMonth.split("-").map(Number);
              const [endY, endM] = updatedRow.endMonth.split("-").map(Number);

              let fiscalY =
                startM >= 4 && startM <= 12
                  ? startY - baseStartYear + 1
                  : startY - baseStartYear;

              let endFiscalY =
                endM >= 4 && endM <= 12
                  ? endY - baseStartYear + 1
                  : endY - baseStartYear;

              let m = startM;

              while (fiscalY <= endFiscalY) {
                const months = table.data[fiscalY]?.months;
                if (months) {
                  const monthKey = Object.keys(months).find(
                    (key) => months[Number(key)]?.month === m
                  );
                  const endMnthKey = Object.keys(months).find(
                    (key) => months[Number(key)]?.month === endM
                  );
                  if (fiscalY < endFiscalY || Number(monthKey) <= Number(endMnthKey)) {
                    handleInputChange(table.id, fiscalY, Number(monthKey), "price", updatedRow.value, setTables);
                  }
                }

                if (m === 3) {
                  fiscalY++;
                }
                m++;

                if (m > 12) {
                  m = 1;
                }
              }
            }
          }
          return { ...tableRows, rows: targetRows };
        })
      );
    };

    return (
      rows.length > 0 && (
        <>
          <Box mb={4}>
            <Text fontWeight="bold" mb={2}>
              {title}
            </Text>
            {/* ヘッダ部分 */}
            <Grid templateColumns="150px 20px 150px 150px 40px 80px" mb={2} alignItems="center" gap={3}>
              <GridItem>
                <Text>{t('features.InvestmentScreening.QuantityAndPrice.field.startMonth')}</Text>
              </GridItem>
              <GridItem />
              <GridItem>
                <Text>{t('features.InvestmentScreening.QuantityAndPrice.field.endMonth')}</Text>
              </GridItem>
              <GridItem>
                <Text >{t('features.InvestmentScreening.QuantityAndPrice.field.amount')}</Text>
              </GridItem>
              <GridItem />
            </Grid>
            {rows.map((row, index) => (
              <Grid
                key={row.id}
                templateColumns="150px 20px 150px 150px 40px 80px"
                alignItems="center"
                mb={3}
                gap={3}
              >
                <GridItem>
                  <Input
                    type="month"
                    width="150px"
                    min={startYearMonth}
                    max={row.endMonth || endYearMonth}
                    value={row.displayStartMonth}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleDisplayMonthChange(row.id, "startMonth", value);
                      if (row.startMonth !== value && value >= startYearMonth && value <= (row.endMonth || endYearMonth)) {
                        updateRow(row.id, "startMonth", value);
                      } else {
                        handleDisplayMonthChange(row.id, "startMonth", row.startMonth);
                      }
                    }}
                    isDisabled={index > 0}
                  />
                </GridItem>
                <GridItem justifySelf="center"><Text>～</Text></GridItem>
                <GridItem>
                  <Input
                    type="month"
                    width="150px"
                    min={row.startMonth || startYearMonth}
                    max={endYearMonth}
                    value={row.displayEndMonth}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleDisplayMonthChange(row.id, "endMonth", value);
                      if (row.endMonth !== value && value >= (row.startMonth || startYearMonth) && value <= endYearMonth) {
                        updateRow(row.id, "endMonth", value);
                      } else {
                        handleDisplayMonthChange(row.id, "endMonth", row.endMonth);
                      }
                    }}
                  />
                </GridItem>

                <GridItem>
                  <CustomNumberInput
                    value={row.value || 0}
                    onChange={() => { }}
                    onBlur={(value) => updateRow(row.id, "value", value)}
                    max={9999999999}
                    min={0}
                    decimalPlaces={4}
                  />
                </GridItem>
                <Text>{currencyName || "--"}</Text>
                <GridItem>
                  {index === rows.length - 1 && (
                    <IconButton
                      aria-label="Add"
                      icon={<FaPlusCircle />}
                      colorScheme="blue"
                      size="sm"
                      mr={2}
                      onClick={() => addRow(row.id)}
                      isDisabled={!row.endMonth}
                    />
                  )}
                  {index === rows.length - 1 && index !== 0 && (
                    <IconButton
                      aria-label="Remove"
                      icon={<FaCircleMinus />}
                      colorScheme="red"
                      size="sm"
                      onClick={() => removeRow(row.id)}
                    />
                  )}
                </GridItem>
              </Grid>
            ))}
          </Box>
        </>
      )
    );
  };

  /**
   * PartNo金型・開発費の開始年月～終了年月 設定値の入力コンポーネント
   *
   * @param parentTableId PartNoテーブルID
   * @param devCostTable 開発費テーブル情報
   */
  const renderPartNoSection = (
    setRows: React.Dispatch<React.SetStateAction<{ parentTableId: string; id: string; rows: PriceRows[] }[]>>,
    targetRows: { parentTableId: string; id: string; rows: PriceRows[] }[],
    parentTableId: string,
    table: MoldMonthTable | DevelopmentMonthCostTable,
    currencyName?: string
  ) => {
    const matched = targetRows.find(
      (item) => item.parentTableId === parentTableId && item.id === table.id
    );
    const rows = matched ? matched.rows : [];

    const projectStartDate = projectData?.projectStartDate;
    const startMonth = projectStartDate ? parseInt(projectStartDate.split("-")[1], 10) : 4;
    const fiscalYear =
      startMonth >= 4 && startMonth <= 12
        ? baseStartYear
        : baseStartYear + 1;

    const startYearMonth = `${fiscalYear}-${String(startMonth).padStart(2, "0")}`;

    // 終了年月の上限：EOL予定日と量産開始日から10年後のうち早い方を使用
    const projectEndDate = projectData?.projectEndDate;
    let endYearMonth: string;

    // 量産開始日から10年間（年度末の3月）を計算
    let maxEndYear: number | null = null;
    let maxEndMonth: number | null = null;
    if (projectStartDate) {
      const startY = parseInt(projectStartDate.split("-")[0], 10);
      const startM = parseInt(projectStartDate.split("-")[1], 10);
      // 10年間の年度末を上限とする（例：2025/05開始なら2035/03まで）
      // 年度は4月始まりなので、4月以降開始なら+10年の3月、1-3月開始なら+9年の3月
      maxEndYear = startM >= 4 ? startY + 10 : startY + 9;
      maxEndMonth = 3;
    }

    if (projectEndDate) {
      const eolYear = parseInt(projectEndDate.split("-")[0], 10);
      const eolMonth = parseInt(projectEndDate.split("-")[1], 10);
      // EOL予定日と10年間の年度末を比較して早い方を使用
      if (maxEndYear !== null && maxEndMonth !== null) {
        const eolDate = new Date(eolYear, eolMonth - 1);
        const maxDate = new Date(maxEndYear, maxEndMonth - 1);
        if (eolDate <= maxDate) {
          endYearMonth = `${eolYear}-${String(eolMonth).padStart(2, "0")}`;
        } else {
          endYearMonth = `${maxEndYear}-${String(maxEndMonth).padStart(2, "0")}`;
        }
      } else {
        endYearMonth = `${eolYear}-${String(eolMonth).padStart(2, "0")}`;
      }
    } else if (maxEndYear !== null && maxEndMonth !== null) {
      // EOL予定日が未設定の場合は10年間の年度末を使用
      endYearMonth = `${maxEndYear}-${String(maxEndMonth).padStart(2, "0")}`;
    } else {
      endYearMonth = `${baseStartYear + table.years.length}-${"03"}`;
    }

    const addRow = (rowId: number) => {
      setRows((prev) => {
        const target = prev.find(
          (t) => t.parentTableId === parentTableId && t.id === table.id
        );
        if (!target) return prev;

        const last = target.rows[target.rows.length - 1];
        if (!last?.endMonth) return prev;
        const nextStart = getMonth(last.endMonth, 1);

        const newRow: PriceRows = {
          id: rowId + 1,
          displayStartMonth: nextStart,
          displayEndMonth: "",
          startMonth: nextStart,
          endMonth: "",
          value: 0,
        };

        const updatedRows = [...target.rows, newRow];

        return prev.map((t) =>
          t.parentTableId === parentTableId && t.id === table.id
            ? { ...t, rows: updatedRows }
            : t
        );
      });
    };

    const removeRow = (rowId: number) => {
      setRows((prev) => {
        const target = prev.find(
          (t) => t.parentTableId === parentTableId && t.id === table.id
        );
        if (!target) return prev;

        updateRow(rowId, "value", 0);

        const updatedRows = target.rows.filter((row) => row.id !== rowId);

        return prev.map((t) =>
          t.parentTableId === parentTableId && t.id === table.id
            ? { ...t, rows: updatedRows }
            : t
        );
      });
    };

    const handleDisplayMonthChange = (
      rowId: number,
      field: "startMonth" | "endMonth",
      value: string
    ) => {
      const displayField = field === "startMonth" ? "displayStartMonth" : "displayEndMonth";

      setRows(prev =>
        prev.map(tableItem =>
          tableItem.parentTableId === parentTableId && tableItem.id === table.id
            ? {
              ...tableItem,
              rows: tableItem.rows.map(r =>
                r.id === rowId
                  ? { ...r, [displayField]: value }
                  : r
              ),
            }
            : tableItem
        )
      );
    };

    const updateRow = (
      rowId: number,
      field: "startMonth" | "endMonth" | "value",
      value: string | number | null
    ) => {
      setRows((prev) =>
        prev.map((tableRows) => {
          if (tableRows.parentTableId !== parentTableId || tableRows.id !== table.id) {
            return tableRows;
          }

          const targetRows = tableRows.rows.map((row) =>
            row.id === rowId ? { ...row, [field]: value } : row
          );

          const updatedRows = setUpdatedRows(rowId, field, value, tableRows);

          for (const updatedRow of updatedRows) {
            if (
              updatedRow?.startMonth &&
              updatedRow?.endMonth &&
              !(field === "endMonth" &&
                updatedRow?.value === 0 &&
                updatedRows.indexOf(updatedRow) === 0 &&
                targetRows[targetRows.length - 1].id === rowId) &&
              !(field === "startMonth" &&
                updatedRow?.value === 0 &&
                updatedRows.indexOf(updatedRow) === 0)
            ) {
              const [startY, startM] = updatedRow.startMonth.split("-").map(Number);
              const [endY, endM] = updatedRow.endMonth.split("-").map(Number);

              let fiscalY =
                startM >= 4 && startM <= 12
                  ? startY - baseStartYear + 1
                  : startY - baseStartYear;

              let endFiscalY =
                endM >= 4 && endM <= 12
                  ? endY - baseStartYear + 1
                  : endY - baseStartYear;

              let m = startM;

              while (fiscalY <= endFiscalY) {
                const months = table.data[fiscalY]?.months;
                if (months) {
                  const monthKey = Object.keys(months).find(
                    (key) => months[Number(key)]?.month === m
                  );
                  const endMnthKey = Object.keys(months).find(
                    (key) => months[Number(key)]?.month === endM
                  );
                  if (fiscalY < endFiscalY || Number(monthKey) <= Number(endMnthKey)) {
                    if ("moldName" in table) {
                      handlePartNoMoldInputChange(parentTableId, table.id, fiscalY, Number(monthKey), updatedRow.value);
                    } else {
                      handlePartNoDevelopmentCostInputChange(parentTableId, table.id, fiscalY, Number(monthKey), updatedRow.value);
                    }
                  }
                }

                if (m === 3) {
                  fiscalY++;
                }
                m++;

                if (m > 12) {
                  m = 1;
                }
              }
            }
          }
          return { ...tableRows, rows: targetRows };
        })
      );
    };

    return (
      rows.length > 0 && (
        <>
          <Box mb={4}>
            <Text fontWeight="bold" mb={2}>
              {t('features.InvestmentScreening.QuantityAndPrice.field.amount')}
            </Text>
            {/* ヘッダ部分 */}
            <Grid templateColumns="150px 20px 150px 150px 40px 80px" mb={2} alignItems="center" gap={3}>
              <GridItem>
                <Text>{t('features.InvestmentScreening.QuantityAndPrice.field.startMonth')}</Text>
              </GridItem>
              <GridItem />
              <GridItem>
                <Text>{t('features.InvestmentScreening.QuantityAndPrice.field.endMonth')}</Text>
              </GridItem>
              <GridItem>
                <Text >{t('features.InvestmentScreening.QuantityAndPrice.field.amount')}</Text>
              </GridItem>
              <GridItem />
            </Grid>
            {rows.map((row, index) => (
              <Grid
                key={row.id}
                templateColumns="150px 20px 150px 150px 40px 80px"
                alignItems="center"
                mb={3}
                gap={3}
              >
                <GridItem>
                  <Input
                    type="month"
                    width="150px"
                    min={startYearMonth}
                    max={row.endMonth || endYearMonth}
                    value={row.displayStartMonth}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleDisplayMonthChange(row.id, "startMonth", value);
                      if (row.startMonth !== value && value >= startYearMonth && value <= (row.endMonth || endYearMonth)) {
                        updateRow(row.id, "startMonth", value);
                      } else {
                        handleDisplayMonthChange(row.id, "startMonth", row.startMonth);
                      }
                    }}
                    isDisabled={index > 0}
                  />
                </GridItem>
                <GridItem justifySelf="center"><Text>～</Text></GridItem>
                <GridItem>
                  <Input
                    type="month"
                    width="150px"
                    min={row.startMonth || startYearMonth}
                    max={endYearMonth}
                    value={row.displayEndMonth}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleDisplayMonthChange(row.id, "endMonth", value);
                      if (row.endMonth !== value && value >= (row.startMonth || startYearMonth) && value <= endYearMonth) {
                        updateRow(row.id, "endMonth", value);
                      } else {
                        handleDisplayMonthChange(row.id, "endMonth", row.endMonth);
                      }
                    }}
                  />
                </GridItem>

                <GridItem>
                  <CustomNumberInput
                    value={row.value || 0}
                    onChange={() => { }}
                    onBlur={(value) => updateRow(row.id, "value", value)}
                    max={9999999999}
                    min={0}
                    decimalPlaces={4}
                  />
                </GridItem>
                <Text>{currencyName || "--"}</Text>
                <GridItem>
                  {index === rows.length - 1 && (
                    <IconButton
                      aria-label="Add"
                      icon={<FaPlusCircle />}
                      colorScheme="blue"
                      size="sm"
                      mr={2}
                      onClick={() => addRow(row.id)}
                      isDisabled={!row.endMonth}
                    />
                  )}
                  {index === rows.length - 1 && index !== 0 && (
                    <IconButton
                      aria-label="Remove"
                      icon={<FaCircleMinus />}
                      colorScheme="red"
                      size="sm"
                      onClick={() => removeRow(row.id)}
                    />
                  )}
                </GridItem>
              </Grid>
            ))}
          </Box>
        </>
      )
    );
  };

  return (
    <Box
      position="fixed"
      top="10vh"
      ml="2vw"
      mr="2vw"
      left="250px"
      width="calc(96vw - 250px)"
      height="85vh"
      p={6}
      overflowY="auto"
      bg="white"
    >
      <fieldset disabled={!pagePermissions?.isEditable}>
        <Text mb="10px" color="blue">{t('features.InvestmentScreening.QuantityAndPrice.label.projectName')} :
          {projectData?.revisedOpportunityNameByRevision ? projectData.revisedOpportunityNameByRevision : projectData?.opportunityName}
        </Text>

        {/* 事業の継続性の有無（合理化・更新のみ表示） */}
        {(projectData?.purpose === PurposeType.Rationalization.code || projectData?.purpose === PurposeType.UpdateAndOther.code) && (
          <Box
            maxW="79.5vw"
            mx="auto"
            borderWidth="1px"
            borderRadius="md"
            boxShadow="md"
            p={4}
            mb={6}
          >
            <Flex align="center" gap={4}>
              <Text fontWeight="bold">{t('features.InvestmentScreening.QuantityAndPrice.field.businessContinuity')}</Text>
              <RadioGroup value={businessContinuity} onChange={setBusinessContinuity} isDisabled={!pagePermissions?.isEditable}>
                <Stack direction="row">
                  <Radio value={RADIO_VALUES.PART_INPUT}>{t('common.option.available')}</Radio>
                  <Radio value={RADIO_VALUES.PART_NOT_INPUT}>{t('common.option.unavailable')}</Radio>
                </Stack>
              </RadioGroup>
            </Flex>
          </Box>
        )}

        {isSimpleMode && (
          <Box
            maxW="79.5vw"
            mx="auto"
            borderWidth="1px"
            borderRadius="md"
            boxShadow="md"
            p={6}
            mb={6}
            bg="gray.50"
          >
            <Text color="gray.600" fontSize="md" textAlign="center">
              {t('features.InvestmentScreening.QuantityAndPrice.message.simpleModeNote')}
            </Text>
          </Box>
        )}

        {!isSimpleMode && quantityAndPriceTables.map((table, tableIndex) => {
          const currencyName = selectedCurrency.find((item) => item.tableId === table.id)?.name ?? "";
          return (
            <Box
              key={table.id}
              maxW="79.5vw"
              mx="auto"
              borderWidth="1px"
              borderRadius="md"
              boxShadow="md"
              height="fit-content"
              mb={6}
            >
              <Box
                maxWidth="100%"
                p={4}
              > {/* 150+padding2*8 */}
                {/* Part No and Year Buttons */}
                <Flex justify="space-between" align="center" mb={4}>
                  <HStack>
                    <FormControl isInvalid={!!errors[`partNoTables-${table.id}-partNo`]}>
                      <Select
                        placeholder="Part No."
                        value={table.partNo}
                        onChange={(e) => handlePartNoChange(table.id, e.target.value)}
                        width="500px"
                      >
                        {partNoOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Select>
                      {errors[`partNoTables-${table.id}-partNo`] && <FormErrorMessage>{errors[`partNoTables-${table.id}-partNo`]}</FormErrorMessage>}
                    </FormControl>
                    <HelpIcon width="600px">
                      {t('features.InvestmentScreening.QuantityAndPrice.help.partNoQuantityInputHelp')}
                      {`\n\n`}
                      {t('features.InvestmentScreening.QuantityAndPrice.help.partNoQuantityExclusionHelp')}
                      {`\n\n`}
                      {t('features.InvestmentScreening.QuantityAndPrice.help.partNoQuantitySyncHelp')}
                    </HelpIcon>
                  </HStack>
                  <Flex gap={2}>
                    <IconButton
                      aria-label="Remove year"
                      icon={<FaCircleMinus />}
                      onClick={() => removeYear(table.id, TABLE_TYPES.PART_NO_TABLES)}
                      colorScheme="blue"
                      isDisabled={true}
                    />
                    <IconButton
                      aria-label="Add year"
                      icon={<FaPlusCircle />}
                      onClick={() => addYear(table.id, TABLE_TYPES.PART_NO_TABLES)}
                      colorScheme="blue"
                      isDisabled={true}
                    />
                  </Flex>
                </Flex>

                {/* 新製品/既存製品比率（汎用設備の場合のみ表示） */}
                {isGeneralPurpose && (
                  <Box mb={4} width="500px">
                    <FormControl isInvalid={!!errors[`newProductRatio-${table.id}`]} isRequired>
                      <Grid templateColumns="200px 150px 50px 150px" alignItems="center" gap={3}>
                        <GridItem>
                          <Text fontWeight="bold">{t('features.InvestmentScreening.QuantityAndPrice.field.newProductRatio')}</Text>
                        </GridItem>
                        <GridItem>
                          <Flex align="center" gap={2}>
                            <Text whiteSpace="nowrap">{t('features.InvestmentScreening.QuantityAndPrice.label.newProduct')}</Text>
                            <CustomNumberInput
                              value={table.newProductRatio ?? null}
                              onChange={() => { }}
                              onBlur={(value) => updateNewProductRatio(table.id, value)}
                              max={100}
                              min={0}
                              decimalPlaces={0}
                              w="100px"
                              emptyValue={0}
                            />
                            <Text>%</Text>
                          </Flex>
                        </GridItem>
                        <GridItem />
                        <GridItem>
                          <Flex align="center" gap={2}>
                            <Text whiteSpace="nowrap">{t('features.InvestmentScreening.QuantityAndPrice.label.existingProduct')}</Text>
                            <Text fontWeight="bold">
                              {table.newProductRatio !== null && table.newProductRatio !== undefined
                                ? `${100 - table.newProductRatio}%`
                                : "-%"}
                            </Text>
                          </Flex>
                        </GridItem>
                      </Grid>
                      {errors[`newProductRatio-${table.id}`] && (
                        <FormErrorMessage>{errors[`newProductRatio-${table.id}`]}</FormErrorMessage>
                      )}
                    </FormControl>
                  </Box>
                )}

                <Box mb={4} width="500px">
                  <Grid templateColumns="344px 150px 80px" mb={2} alignItems="center" gap={3}>
                    <GridItem>
                      <Text>
                        <Flex align="center" gap={2}>
                          {LABEL_CURRENCY}
                          <CurrencyList currencyData={currencyData} />
                          <HelpIcon>
                            <Text>
                              {t('features.InvestmentScreening.QuantityAndPrice.help.multipleCurrencyLine1')}<br />
                              {t('features.InvestmentScreening.QuantityAndPrice.help.multipleCurrencyLine2')}<br />
                              {t('features.InvestmentScreening.QuantityAndPrice.help.multipleCurrencyLine3')}<br />
                              {t('features.InvestmentScreening.QuantityAndPrice.help.multipleCurrencyLine4')}
                            </Text>
                          </HelpIcon>
                        </Flex>
                      </Text>
                    </GridItem>
                    <GridItem>
                      <Text>{t('features.InvestmentScreening.QuantityAndPrice.field.ratio')}</Text>
                    </GridItem>
                    <GridItem />
                  </Grid>
                  <FormControl isInvalid={!!errors[`ratio-${table.id}`] || !!errors[`currencyData-${table.id}`]}>
                    {table.currencyAndRatio.map((currency, index) => (
                      <Grid templateColumns="344px 150px 40px 80px" mb={2} alignItems="center" gap={3}>
                        <GridItem>
                          <FormControl isInvalid={!!errors[`currencyData-${table.id}`]}>
                            <Select
                              placeholder={LABEL_CURRENCY}
                              value={currency.id}
                              onChange={(e) => (updateCurrencyRow(index, table.id, "id", e.target.value))}
                            >
                              {currencyOptions.map((option) => {
                                const isSelectedInOtherRow = table.currencyAndRatio.some(
                                  (c, i) => i !== index && c.id === option.value
                                );
                                return (
                                  <option
                                    key={option.value}
                                    value={option.value}
                                    disabled={isSelectedInOtherRow}
                                  >
                                    {option.label}
                                  </option>
                                );
                              })}
                            </Select>
                          </FormControl>
                        </GridItem>
                        <FormControl isInvalid={!!errors[`ratio-${table.id}`]}>
                          <GridItem>
                            <CustomNumberInput
                              value={currency.ratio}
                              onChange={() => { }}
                              onBlur={(value) => updateCurrencyRow(index, table.id, "ratio", value)}
                              max={100}
                              min={0}
                              decimalPlaces={0}
                            />
                          </GridItem>
                        </FormControl>
                        <Text>%</Text>
                        <GridItem>
                          {index === table.currencyAndRatio.length - 1 && (
                            <IconButton
                              aria-label="Add"
                              icon={<FaPlusCircle />}
                              colorScheme="blue"
                              size="sm"
                              mr={2}
                              onClick={() => addCurrencyRow(table.id)}
                            />
                          )}
                          {table.currencyAndRatio.length > 1 && (
                            <IconButton
                              aria-label="Remove"
                              icon={<FaCircleMinus />}
                              colorScheme="red"
                              size="sm"
                              onClick={() => removeCurrencyRow(index, table.id, currency.id)}
                            />
                          )}
                        </GridItem>
                      </Grid>
                    ))}
                    {errors[`currencyData-${table.id}`] && <FormErrorMessage>{errors[`currencyData-${table.id}`]}</FormErrorMessage>}
                    {errors[`ratio-${table.id}`] && <FormErrorMessage>{errors[`ratio-${table.id}`]}</FormErrorMessage>}
                  </FormControl>
                </Box>
                <Flex align="center" mb={4}>
                  <Text mr={4}>{t('features.InvestmentScreening.QuantityAndPrice.field.moldInput')}</Text>
                  <RadioGroup
                    onChange={(value) => handleRadioChange(table.id, value, TABLE_TYPES.MOLD_TABLES)}
                    value={table.moldRadioValue}
                  >
                    <Stack direction="row">
                      <Radio value={RADIO_VALUES.PART_INPUT}>{t('common.option.available')}</Radio>
                      <Radio value={RADIO_VALUES.PART_NOT_INPUT}>{t('common.option.unavailable')}</Radio>
                    </Stack>
                  </RadioGroup>
                  <Box ml={2} display="flex" alignItems="center">
                    <HelpIcon>
                      <Text>
                        {t('features.InvestmentScreening.QuantityAndPrice.help.moldRecoveryLine1')}<br />
                        {t('features.InvestmentScreening.QuantityAndPrice.help.moldRecoveryLine2', { label: LABEL_PRICE })}<br />
                        {t('features.InvestmentScreening.QuantityAndPrice.help.moldRecoveryLine3')}
                      </Text>
                    </HelpIcon>
                  </Box>
                </Flex>

                <Flex>
                  <Text mr={4}>{t('features.InvestmentScreening.QuantityAndPrice.field.devCostInput')}</Text>
                  <RadioGroup
                    onChange={(value) => handleRadioChange(table.id, value, TABLE_TYPES.DEVELOPMENT_COST_TABLES)}
                    value={table.developmentRadioValue}
                    mb={4}
                  >
                    <Stack direction="row">
                      <Radio value={RADIO_VALUES.PART_INPUT}>{t('common.option.available')}</Radio>
                      <Radio value={RADIO_VALUES.PART_NOT_INPUT}>{t('common.option.unavailable')}</Radio>
                    </Stack>
                  </RadioGroup>
                </Flex>
                {/* 入力行 */}
                {renderSection(LABEL_PRICE, setPriceRows, setQuantityAndPriceTables, priceRows, table, currencyName)}
                <HStack mb={4}>
                  <HStack mr={2} >
                    <ButtonGroup spacing={4} isAttached  >
                      <ReadOnlyExemptButton colorScheme={(inputModes.find(item => item.id === table.id)?.mode ?? "manual") === "manual" ? "green" : "gray"}
                        isLoading={loadingIds.has(table.id)} onClick={(e) => {
                          setInputModes(prev => {
                            if (prev.find(item => item.id === table.id)) {
                              return prev.map(item =>
                                item.id === table.id ? { ...item, mode: "manual" } : item
                              );
                            } else {
                              return [...prev, { id: table.id, mode: "manual" }];
                            }
                          });
                          handleMaualClick(table.id, e);
                        }}>
                        {t('features.InvestmentScreening.QuantityAndPrice.field.inputOnScreen')}
                      </ReadOnlyExemptButton>
                      <ReadOnlyExemptButton colorScheme={inputModes.find(item => item.id === table.id)?.mode === "google" ? "green" : "gray"}
                        isDisabled={!table.partNo || priceRows.find((item) => item.id === table.id)?.rows.length === 0}
                        isLoading={loadingIds.has(table.id)} onClick={(e) => {
                          setInputModes(prev => {
                            if (prev.find(item => item.id === table.id)) {
                              return prev.map(item =>
                                item.id === table.id ? { ...item, mode: "google" } : item
                              );
                            } else {
                              return [...prev, { id: table.id, mode: "google" }];
                            }
                          });
                          handleGoogleSpreadClick(e, table);
                        }}>
                        GoogleSpread
                      </ReadOnlyExemptButton>
                    </ButtonGroup>
                  </HStack>
                  <Button colorScheme="blue" onClick={(e) => handleSyncData(e, table)}
                    disabled={(inputModes.find(item => item.id === table.id)?.mode ?? "manual") === "manual"}
                    isLoading={loadingIds.has(table.id)}>
                    {t('features.InvestmentScreening.QuantityAndPrice.field.dataLink')}
                  </Button>
                  <HelpIcon >
                    {t('features.InvestmentScreening.QuantityAndPrice.help.setSalesInputHelp')}
                  </HelpIcon>
                </HStack>
                <Box
                  id={table.id}
                  ref={(el) => (quantityAndPriceTableRef.current[tableIndex] = el)}
                  maxWidth="100%"
                  overflowX="auto"
                >
                  <Grid
                    templateColumns={`150px repeat(${getDynamicColumnCount(table, projectData?.projectStartDate)}, 150px)`} // 各列の幅を固定
                    gap={4}
                  >
                    {/* ヘッダー */}
                    <GridItem>
                      <Text fontWeight="bold">{t('features.InvestmentScreening.QuantityAndPrice.field.item')}</Text>
                    </GridItem>
                    {generateYearMonthCells(table, projectData?.projectStartDate, baseStartYear)}

                    {/* 数量行 */}
                    <GridItem>
                      <Text>{LABEL_QUANTITY}　({t('features.InvestmentScreening.QuantityAndPrice.field.quantityUnit')})</Text>
                    </GridItem>
                    {generateGridItems(table, projectData?.projectStartDate, (monthData, year, monthKey, index, isDisabled) => (
                      <GridItem key={`quantity-${table.id}-${year}-${monthKey}`}>
                        <FormControl isInvalid={!!errors[`partNoTables-${table.id}-${year}-${monthKey}-quantity`]}>
                          <CustomNumberInput
                            value={monthData?.quantity || 0}
                            onChange={(value) =>
                              handleInputChange(table.id, year, monthKey, "quantity", value, setQuantityAndPriceTables)
                            }
                            max={9999999999}
                            min={0}
                            decimalPlaces={0}
                            isDisabled={isDisabled}
                          />
                          {errors[`partNoTables-${table.id}-${year}-${monthKey}-quantity`] && (
                            <FormErrorMessage>{errors[`partNoTables-${table.id}-${year}-${monthKey}-quantity`]}</FormErrorMessage>
                          )}
                        </FormControl>
                      </GridItem>
                    ))}

                    {/**
                {table.years.flatMap((year) => {
                  const months = table.data[year]?.months || {};
                  return Object.keys(months).map((monthKey) => {
                    const monthData = months[Number(monthKey)];
                    return (
                      <GridItem key={`quantity-${table.id}-${year}-${monthKey}`}>
                        <FormControl isInvalid={!!errors[`partNoTables-${table.id}-${year}-${monthKey}-quantity`]}>
                          <CustomNumberInput
                            value={monthData?.quantity || 0}
                            onChange={(value) => handleInputChange(table.id, year, Number(monthKey), "quantity", value)}
                            max={9999999999}
                            min={0}
                            decimalPlaces={0}
                          />
                          {errors[`partNoTables-${table.id}-${year}-${monthKey}-quantity`] && <FormErrorMessage>{errors[`partNoTables-${table.id}-${year}-${monthKey}-quantity`]}</FormErrorMessage>}
                        </FormControl>
                      </GridItem>
                    );
                  });
                })}
*/}
                    {/* 単価行 */}
                    <GridItem>
                      <Text>{LABEL_PRICE}　({currencyName || "--"})</Text>
                    </GridItem>
                    {/*
                {table.years.flatMap((year, yearIndex) => {
                  const months = table.data[year]?.months || {};
                  return Object.keys(months).map((monthKey, monthIndex) => {
                    const monthData = months[Number(monthKey)];
                    return (
                      <GridItem key={`price-${table.id}-${yearIndex}-${monthKey}`}>
                        <FormControl isInvalid={!!errors[`partNoTables-${table.id}-${yearIndex}-${monthIndex}-price`]}>
                          <CustomNumberInput
                            value={monthData?.price || 0}
                            onChange={(value) => handleInputChange(table.id, year, Number(monthKey), "price", value)}
                            max={9999999999}
                            min={0}
                            decimalPlaces={4}
                          />
                          {errors[`partNoTables-${table.id}-${yearIndex}-${monthIndex}-price`] && <FormErrorMessage>{errors[`partNoTables-${table.id}-${yearIndex}-${monthIndex}-price`]}</FormErrorMessage>}
                        </FormControl>
                      </GridItem>
                    );
                  });
                })}
*/}
                    {generateGridItems(table, projectData?.projectStartDate, (monthData, year, monthKey, index, isDisabled) => (
                      <GridItem key={`price-${table.id}-${year}-${monthKey}`}>
                        <FormControl isInvalid={!!errors[`partNoTables-${table.id}-${year}-${monthKey}-price`]}>
                          <CustomNumberInput
                            value={monthData?.price || 0} // 単価値
                            onChange={(value) =>
                              handleInputChange(table.id, year, monthKey, "price", value, setQuantityAndPriceTables)
                            }
                            max={9999999999}
                            min={0}
                            decimalPlaces={4}
                            isDisabled={isDisabled}
                          />
                          {errors[`partNoTables-${table.id}-${year}-${monthKey}-price`] && (
                            <FormErrorMessage>{errors[`partNoTables-${table.id}-${year}-${monthKey}-price`]}</FormErrorMessage>
                          )}
                        </FormControl>
                      </GridItem>
                    ))}
                    {/* 金額行 */}
                    <GridItem>
                      <Text>{t('features.InvestmentScreening.QuantityAndPrice.field.productSales')}　({currencyName || "--"})</Text>
                    </GridItem>
                    {/*
                {table.years.flatMap((year) => {
                  const months = table.data[year]?.months || {};
                  return Object.keys(months).map((monthKey) => {
                    const monthData = months[Number(monthKey)];
                    const sales = new Decimal(monthData?.quantity || 0).times(new Decimal(monthData?.price || 0));
                    return (
                      <GridItem key={`sales-${table.id}-${year}-${monthKey}`}>
                        <Text textAlign="right" mr={4}>{formatNumber(sales, selectedCurrency.name)}</Text>
                      </GridItem>
                    );
                  });
                })}
*/}
                    {generateGridItems(table, projectData?.projectStartDate, (monthData, year, monthKey) => {
                      const sales = new Decimal(monthData?.quantity || 0).times(new Decimal(monthData?.price || 0));
                      return (
                        <GridItem key={`sales-${table.id}-${year}-${monthKey}`}>
                          <Text textAlign="right" mr={4}>{formatNumber(sales, currencyName, 3)}</Text>
                        </GridItem>
                      );
                    })}
                  </Grid>
                </Box>
              </Box>
              <Box pl={4}>
                <Flex>
                  <Checkbox
                    size="lg"
                    colorScheme="green"
                    isChecked={table.isSetSalesInput ?? false}
                    onChange={(e) => handleSetSalesInputChange(table.id, e.target.checked)}
                  >
                    <Text fontSize="sm" fontWeight="medium">
                      {t('features.InvestmentScreening.QuantityAndPrice.field.setSalesInputCheckbox')}
                    </Text>
                  </Checkbox>
                </Flex>
              </Box>
              <Box p={4}>
                <Flex>
                  <Text fontWeight="bold">
                    {t('features.InvestmentScreening.QuantityAndPrice.field.quantityTotal')}:{" "}
                    {formatNumber(
                      calculateTotalQuantity(table.years, table.data),
                      currencyName
                    )}
                  </Text>
                </Flex>
                <Flex mt={4}>
                  {(() => {
                    const { totalSales, weightedAveragePrice } = calculateTotalSales(table.years, table.data);
                    return (
                      <>
                        <Text fontWeight="bold">
                          {t('features.InvestmentScreening.QuantityAndPrice.field.totalAmount')}:{" "}
                          {formatNumber(totalSales, currencyName, 3)} {currencyName}
                        </Text>
                      </>
                    );
                  })()}
                </Flex>
              </Box>

              {/* PartNoに紐づく金型回収テーブル */}
              {table.moldRadioValue === RADIO_VALUES.PART_INPUT && (
                <Box p={4}>
                  <Flex align="center">
                    <Text fontWeight="bold">{t('features.InvestmentScreening.QuantityAndPrice.field.moldRecovery')}</Text>
                    <Box mt={4} mb={4} ml={4}>
                      <Button leftIcon={<FaPlusCircle />} colorScheme="blue" onClick={addPartNoMoldTable(table.id)} mr="20px">
                        {t('features.InvestmentScreening.QuantityAndPrice.field.moldRecovery')}
                      </Button>
                    </Box>
                  </Flex>
                  {(() => {
                    let totalMoldPrice = new Decimal(0);
                    return (
                      <>
                        {table.partNoMoldTables && Array.isArray(table.partNoMoldTables) && table.partNoMoldTables.map((partNoMoldTable, moldTableIndex) => {
                          const totalPrice = calculateTotalPrice(partNoMoldTable.years, partNoMoldTable.data);
                          totalMoldPrice = totalMoldPrice.plus(totalPrice);
                          return (
                            <Box
                              key={partNoMoldTable.id}
                              mx="auto"
                              height="fit-content"
                              mb={6}
                            >
                              {/* 金型名入力 */}
                              <Box mt={4}>
                                <Flex>
                                  <FormControl isInvalid={!!errors[`partNoTables-${table.id}-${partNoMoldTable.id}-moldName`]} flex="0">
                                    <Input
                                      placeholder={t('features.InvestmentScreening.QuantityAndPrice.field.moldName')}
                                      value={partNoMoldTable.moldName}
                                      onChange={(e) => handlePartNoMoldNameChange(table.id, partNoMoldTable.id, e.target.value)}
                                      width="500px"
                                      maxLength={50}
                                    />
                                    {errors[`partNoTables-${table.id}-${partNoMoldTable.id}-moldName`] && (
                                      <FormErrorMessage>
                                        {errors[`partNoTables-${table.id}-${partNoMoldTable.id}-moldName`]}
                                      </FormErrorMessage>
                                    )}
                                  </FormControl>
                                  <Box mb={4} ml={4}>
                                    <IconButton
                                      aria-label="Delete PartNo-Mold Data"
                                      icon={<RiDeleteBin6Fill />}
                                      colorScheme="red"
                                      onClick={() => deletePartNoMoldTable(table.id, partNoMoldTable.id)}
                                    />
                                  </Box>
                                </Flex>

                                {/* 範囲入力 */}
                                {renderPartNoSection(setPartNoMoldPriceRows, partNoMoldPriceRows, table.id, partNoMoldTable, currencyName)}

                                {/* 月ごとの金額入力 */}
                                <Box
                                  data-group={table.id}
                                  ref={(el) => {
                                    // 初期化
                                    if (!partNoMoldPriceChildTableRef.current[table.id]) {
                                      partNoMoldPriceChildTableRef.current[table.id] = [];
                                    }
                                    const childArray = partNoMoldPriceChildTableRef.current[table.id];
                                    if (el) {
                                      // +押下時：配列を追加
                                      childArray[moldTableIndex] = el;
                                    } else {
                                      // -押下時：配列を削除
                                      childArray.splice(moldTableIndex, 1);
                                    }
                                  }}
                                  maxWidth="100%"
                                  overflowX="auto"
                                >
                                  <Grid templateColumns={`100px repeat(${getDynamicColumnCount(partNoMoldTable, projectData?.projectStartDate)}, 150px)`}
                                    gap={4}
                                  >
                                    <GridItem>
                                      <Text fontWeight="bold">{t('features.InvestmentScreening.QuantityAndPrice.field.item')}</Text>
                                    </GridItem>
                                    {generateYearMonthCells(partNoMoldTable, projectData?.projectStartDate, baseStartYear)}

                                    {/* 金額入力 */}
                                    <GridItem>
                                      <Text>{t('features.InvestmentScreening.QuantityAndPrice.field.amount')}　({currencyName || "--"})</Text>
                                    </GridItem>
                                    {generateGridItems(partNoMoldTable, projectData?.projectStartDate, (monthData, year, monthKey, index, isDisabled) => (
                                      <GridItem
                                        key={`price-${table.id}-${partNoMoldTable.id}-${year}-${monthKey}`}
                                      >
                                        <FormControl isInvalid={!!errors[`partNoTables-${table.id}-${partNoMoldTable.id}-${year}-${monthKey}-price`]}>
                                          <CustomNumberInput
                                            value={monthData?.price || 0} // 金額値
                                            onChange={(value) => handlePartNoMoldInputChange(table.id, partNoMoldTable.id, year, monthKey, value)}
                                            max={9999999999}
                                            min={0}
                                            decimalPlaces={4} // 小数点4桁許容
                                            isDisabled={isDisabled}
                                          />
                                          {errors[`partNoTables-${table.id}-${partNoMoldTable.id}-${year}-${monthKey}-price`] && (
                                            <FormErrorMessage>
                                              {errors[`partNoTables-${table.id}-${partNoMoldTable.id}-${year}-${monthKey}-price`]}
                                            </FormErrorMessage>
                                          )}
                                        </FormControl>
                                      </GridItem>
                                    ))}
                                  </Grid>
                                </Box>
                              </Box>
                            </Box>
                          );
                        })}
                        {/* 合計金額 */}
                        <Box>
                          <Text fontWeight="bold">{t('features.InvestmentScreening.QuantityAndPrice.field.totalAmount')}: {formatNumber(totalMoldPrice, currencyName, 3)} {currencyName}</Text>
                        </Box>
                      </>
                    );
                  })()}
                </Box>
              )
              }

              {/* PartNoに紐づく開発費回収テーブル */}
              {
                table.developmentRadioValue === RADIO_VALUES.PART_INPUT && (
                  <Box p={4}>
                    <Flex align="center">
                      <Text fontWeight="bold">{t('features.InvestmentScreening.QuantityAndPrice.field.devCostRecovery')}</Text>
                      <Box mt={4} mb={4} ml={4}>
                        <Button leftIcon={<FaPlusCircle />} colorScheme="blue" onClick={addPartNoDevelopmentCostTable(table.id)} mr="20px">
                          {t('features.InvestmentScreening.QuantityAndPrice.field.devCostRecovery')}
                        </Button>
                      </Box>
                    </Flex>
                    {(() => {
                      let totalDevelopmentCostPrice = new Decimal(0);
                      return (
                        <>
                          {table.partNoDevelopmentCostTables &&
                            Array.isArray(table.partNoDevelopmentCostTables) &&
                            table.partNoDevelopmentCostTables.map((partNoDevelopmentCostTable, devCostTableIndex) => {
                              const totalPrice = calculateTotalPrice(
                                partNoDevelopmentCostTable.years,
                                partNoDevelopmentCostTable.data
                              );
                              totalDevelopmentCostPrice = totalDevelopmentCostPrice.plus(totalPrice);

                              return (
                                <Box
                                  key={partNoDevelopmentCostTable.id}
                                  mx="auto"
                                  height="fit-content"
                                  mb={6}
                                >
                                  {/* 開発費名入力 */}
                                  <Box mt={4}>
                                    <Flex>
                                      <FormControl isInvalid={!!errors[`partNoTables-${table.id}-${partNoDevelopmentCostTable.id}-developmentCostName`]} flex="0">
                                        <Input
                                          placeholder={t('features.InvestmentScreening.QuantityAndPrice.field.devCostName')}
                                          value={partNoDevelopmentCostTable.developmentCostName}
                                          onChange={(e) => handlePartNoDevelopmentCostNameChange(table.id, partNoDevelopmentCostTable.id, e.target.value)}
                                          width="500px"
                                          maxLength={50}
                                        />
                                        {errors[`partNoTables-${table.id}-${partNoDevelopmentCostTable.id}-developmentCostName`] && (
                                          <FormErrorMessage>
                                            {errors[`partNoTables-${table.id}-${partNoDevelopmentCostTable.id}-developmentCostName`]}
                                          </FormErrorMessage>
                                        )}
                                      </FormControl>
                                      <Box mb={4} ml={4}>
                                        <IconButton
                                          aria-label="Delete PartNo-DevelopmentCost Data"
                                          icon={<RiDeleteBin6Fill />}
                                          colorScheme="red"
                                          onClick={() => deletePartNoDevelopmentCostTable(table.id, partNoDevelopmentCostTable.id)}
                                        />
                                      </Box>
                                    </Flex>

                                    {/* 範囲入力 */}
                                    {renderPartNoSection(setPartNoDevelopmentCostPriceRows, partNoDevelopmentCostPriceRows, table.id, partNoDevelopmentCostTable, currencyName)}


                                    {/* 月ごとの金額入力 */}
                                    <Box
                                      data-group={table.id}
                                      ref={(el) => {
                                        // 初期化
                                        if (!developmentCostChildTableRef.current[table.id]) {
                                          developmentCostChildTableRef.current[table.id] = [];
                                        }

                                        const childArray = developmentCostChildTableRef.current[table.id];
                                        if (el) {
                                          // +押下時：配列を追加
                                          childArray[devCostTableIndex] = el;
                                        } else {
                                          // -押下時：配列を削除
                                          childArray.splice(devCostTableIndex, 1);
                                        }
                                      }}
                                      maxWidth="100%"
                                      overflowX="auto"
                                    >
                                      <Grid templateColumns={`100px repeat(${getDynamicColumnCount(partNoDevelopmentCostTable, projectData?.projectStartDate)}, 150px)`}
                                        gap={4}
                                      >
                                        <GridItem>
                                          <Text fontWeight="bold">{t('features.InvestmentScreening.QuantityAndPrice.field.item')}</Text>
                                        </GridItem>
                                        {generateYearMonthCells(partNoDevelopmentCostTable, projectData?.projectStartDate, baseStartYear)}

                                        {/* 金額入力行 */}
                                        <GridItem>
                                          <Text>{t('features.InvestmentScreening.QuantityAndPrice.field.amount')}　({currencyName || "--"})</Text>
                                        </GridItem>
                                        {generateGridItems(partNoDevelopmentCostTable, projectData?.projectStartDate, (monthData, year, monthKey, index, isDisabled) => (
                                          <GridItem key={`price-${table.id}-${partNoDevelopmentCostTable.id}-${year}-${monthKey}`}>
                                            <FormControl isInvalid={!!errors[`partNoTables-${table.id}-${partNoDevelopmentCostTable.id}-${year}-${monthKey}-price`]}>
                                              <CustomNumberInput
                                                value={monthData?.price || 0}
                                                onChange={(value) =>
                                                  handlePartNoDevelopmentCostInputChange(
                                                    table.id,
                                                    partNoDevelopmentCostTable.id,
                                                    year,
                                                    monthKey,
                                                    value
                                                  )
                                                }
                                                max={9999999999}
                                                min={0}
                                                decimalPlaces={4}
                                                isDisabled={isDisabled}
                                              />
                                              {errors[`partNoTables-${table.id}-${partNoDevelopmentCostTable.id}-${year}-${monthKey}-price`] && (
                                                <FormErrorMessage>
                                                  {errors[`partNoTables-${table.id}-${partNoDevelopmentCostTable.id}-${year}-${monthKey}-price`]}
                                                </FormErrorMessage>
                                              )}
                                            </FormControl>
                                          </GridItem>
                                        ))}
                                      </Grid>
                                    </Box>
                                  </Box>
                                </Box>
                              );
                            })}

                          {/* 合計金額表示 */}
                          <Box>
                            <Text fontWeight="bold">
                              {t('features.InvestmentScreening.QuantityAndPrice.field.totalAmount')}: {formatNumber(totalDevelopmentCostPrice, currencyName, 3)} {currencyName}
                            </Text>
                          </Box>
                        </>
                      );
                    })()}
                  </Box>
                )
              }

              {/* 総合計金額 金型、開発費がある場合に表示 */}
              {
                (table.moldRadioValue === RADIO_VALUES.PART_INPUT || table.developmentRadioValue === RADIO_VALUES.PART_INPUT) && (
                  <Flex ml={4}>
                    <Text fontWeight="bold">
                      {t('features.InvestmentScreening.QuantityAndPrice.field.grandTotal')}:{" "}
                      {formatNumber(calculateGrandTotal(table), currencyName, 3)} {currencyName}
                    </Text>
                  </Flex>
                )
              }
              <Flex justify="flex-end" p={4} maxWidth="100%" >
                <IconButton
                  aria-label="Delete Table"
                  icon={<RiDeleteBin6Fill />}
                  colorScheme="red"
                  onClick={() => deleteTable(table.id, TABLE_TYPES.PART_NO_TABLES)}
                />
              </Flex>
            </Box>
          )
        })}

        {/* 金型回収テーブル */}
        {!isSimpleMode &&
          moldTables.map((table, tableIndex) => {
            const totalPrice = calculateTotalPrice(table.years, table.data);
            const currencyName = currencyData.find(currency => currency.currencyId === table.currencyId)?.currencyName;

            return (
              <Box
                key={table.id}
                id={table.id}
                maxW="79.5vw"
                mx="auto"
                borderWidth="1px"
                borderRadius="md"
                boxShadow="md"
                height="fit-content"
                mb={6}
              >
                <Box maxWidth="100%" p={4}>

                  <Flex justify="space-between" align="center">
                    <Text fontWeight="bold">{t('features.InvestmentScreening.QuantityAndPrice.field.moldRecovery')}</Text>
                    <Flex gap={2}>
                      <IconButton
                        aria-label="Remove year"
                        icon={<FaCircleMinus />}
                        onClick={() => removeYear(table.id, TABLE_TYPES.MOLD_TABLES)}
                        colorScheme="blue"
                        isDisabled={true}
                      />
                      <IconButton
                        aria-label="Add year"
                        icon={<FaPlusCircle />}
                        onClick={() => addYear(table.id, TABLE_TYPES.MOLD_TABLES)}
                        colorScheme="blue"
                        isDisabled={true}
                      />
                    </Flex>
                  </Flex>

                  <Box mb={4}>
                    <FormControl isInvalid={!!errors[`moldTables-${table.id}-moldName`]}>
                      <Input
                        placeholder={t('features.InvestmentScreening.QuantityAndPrice.field.moldName')}
                        value={table.moldName}
                        onChange={(e) => handleMoldNameChange(table.id, e.target.value)}
                        width="500px"
                        maxLength={50}
                      />
                      {errors[`moldTables-${table.id}-moldName`] && <FormErrorMessage>{errors[`moldTables-${table.id}-moldName`]}</FormErrorMessage>}
                    </FormControl>
                  </Box>

                  <Box mb={4}>
                    <Flex align="center" >
                      <Text width="220px" whiteSpace="nowrap">
                        <Flex align="center" gap={2}>
                          {LABEL_CURRENCY}
                          <CurrencyList currencyData={currencyData} />
                        </Flex>
                      </Text>
                      <FormControl isInvalid={!!errors[`moldCurrencyData-${table.id}`]}>
                        <Select
                          placeholder={LABEL_CURRENCY}
                          value={table.currencyId}
                          onChange={(e) => {
                            setMoldTables(prev =>
                              prev.map(t =>
                                t.id === table.id
                                  ? { ...t, currencyId: e.target.value }
                                  : t
                              )
                            );
                          }}
                          width="410px"
                        >
                          {currencyOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Select>
                        {errors[`moldCurrencyData-${table.id}`] && <FormErrorMessage>{errors[`moldCurrencyData-${table.id}`]}</FormErrorMessage>}
                      </FormControl>
                    </Flex>
                  </Box>

                  {/* 範囲入力 */}
                  {renderSection(t('features.InvestmentScreening.QuantityAndPrice.field.amount'), setMoldPriceRows, setMoldTables, moldPriceRows, table, currencyName)}
                  <Box
                    id={table.id}
                    ref={(el) => (moldTableRef.current[tableIndex] = el)}
                    maxWidth="100%"
                    overflowX="auto"
                  >
                    <Grid
                      templateColumns={`100px repeat(${getDynamicColumnCount(table, projectData?.projectStartDate)}, 150px)`}
                      gap={4}
                    >
                      <GridItem>
                        <Text fontWeight="bold">{t('features.InvestmentScreening.QuantityAndPrice.field.item')}</Text>
                      </GridItem>
                      {generateYearMonthCells(table, projectData?.projectStartDate, baseStartYear)}

                      {/* 金額行 */}
                      <GridItem>
                        <Text>{t('features.InvestmentScreening.QuantityAndPrice.field.amount')}　({currencyName || "--"})</Text>
                      </GridItem>
                      {generateGridItems(table, projectData?.projectStartDate, (monthData, year, monthKey, index, isDisabled) => (
                        <GridItem key={`price-${table.id}-${year}-${monthKey}`}>
                          <FormControl
                            isInvalid={!!errors[`moldTables-${table.id}-${year}-${monthKey}-price`]}
                          >
                            <CustomNumberInput
                              value={monthData?.price || 0}
                              onChange={(value) =>
                                handleInputChange(table.id, year, monthKey, "price", value, setMoldTables)
                              }
                              max={9999999999}
                              min={0}
                              decimalPlaces={4} // 小数点4桁許容
                              isDisabled={isDisabled}
                            />
                            {errors[`moldTables-${table.id}-${year}-${monthKey}-price`] && (
                              <FormErrorMessage>
                                {errors[`moldTables-${table.id}-${year}-${monthKey}-price`]}
                              </FormErrorMessage>
                            )}
                          </FormControl>
                        </GridItem>
                      ))}
                    </Grid>
                  </Box>
                </Box>
                {/* 合計金額 */}
                <Box p={4}>
                  <Text fontWeight="bold">
                    {t('features.InvestmentScreening.QuantityAndPrice.field.totalAmount')}: {formatNumber(totalPrice, currencyName!, 3)} {currencyName}
                  </Text>
                </Box>
                <Flex justify="flex-end" p={4} maxWidth="100%">
                  <IconButton
                    aria-label="Delete Mold Table"
                    icon={<RiDeleteBin6Fill />}
                    colorScheme="red"
                    onClick={() => deleteTable(table.id, TABLE_TYPES.MOLD_TABLES)}
                  />
                </Flex>
              </Box>
            );
          })
        }

        {/* 開発費回収テーブル */}
        {!isSimpleMode &&
          developmentCostTables.map((table, tableIndex) => {
            const totalPrice = calculateTotalPrice(table.years, table.data);
            const currencyName = currencyData.find(currency => currency.currencyId === table.currencyId)?.currencyName;

            return (
              <Box
                key={table.id}
                id={table.id}
                maxW="79.5vw"
                mx="auto"
                borderWidth="1px"
                borderRadius="md"
                boxShadow="md"
                height="fit-content"
                mb={6}
              >
                <Box maxWidth="100%" p={4}>
                  <Flex justify="space-between" align="center">
                    <Flex align="center" gap={2}>
                      <Text fontWeight="bold">{t('features.InvestmentScreening.QuantityAndPrice.field.devCostRecovery')}</Text>
                      <HelpIcon>
                        {t('features.InvestmentScreening.QuantityAndPrice.help.devCostRecoveryHelp')}
                      </HelpIcon>
                    </Flex>
                    <Flex gap={2}>
                      <IconButton
                        aria-label="Remove year"
                        icon={<FaCircleMinus />}
                        onClick={() => removeYear(table.id, TABLE_TYPES.DEVELOPMENT_COST_TABLES)}
                        colorScheme="blue"
                        isDisabled={true}
                      />
                      <IconButton
                        aria-label="Add year"
                        icon={<FaPlusCircle />}
                        onClick={() => addYear(table.id, TABLE_TYPES.DEVELOPMENT_COST_TABLES)}
                        colorScheme="blue"
                        isDisabled={true}
                      />
                    </Flex>
                  </Flex>
                  <Box mb={4}>
                    <FormControl isInvalid={!!errors[`developmentCostTables-${table.id}-developmentCostName`]}>
                      <Input
                        placeholder={t('features.InvestmentScreening.QuantityAndPrice.field.devCostName')}
                        value={table.developmentCostName}
                        onChange={(e) => handleDevelopmentCostNameChange(table.id, e.target.value)}
                        width="500px"
                        maxLength={50}
                      />
                      {errors[`developmentCostTables-${table.id}-developmentCostName`] && <FormErrorMessage>{errors[`developmentCostTables-${table.id}-developmentCostName`]}</FormErrorMessage>}
                    </FormControl>
                  </Box>

                  <Box mb={4}>
                    <Flex align="center" >
                      <Text width="220px" whiteSpace="nowrap">
                        <Flex align="center" gap={2}>
                          {LABEL_CURRENCY}
                          <CurrencyList currencyData={currencyData} />
                        </Flex>
                      </Text>
                      <FormControl isInvalid={!!errors[`developmentCostCurrencyData-${table.id}`]}>
                        <Select
                          placeholder={LABEL_CURRENCY}
                          value={table.currencyId}
                          onChange={(e) => {
                            setDevelopmentCostTables(prev =>
                              prev.map(t =>
                                t.id === table.id
                                  ? { ...t, currencyId: e.target.value }
                                  : t
                              )
                            );
                          }}
                          width="410px"
                        >
                          {currencyOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Select>
                        {errors[`developmentCostCurrencyData-${table.id}`] && <FormErrorMessage>{errors[`developmentCostCurrencyData-${table.id}`]}</FormErrorMessage>}
                      </FormControl>
                    </Flex>
                  </Box>

                  {/* 範囲入力 */}
                  {renderSection(t('features.InvestmentScreening.QuantityAndPrice.field.amount'), setDevelopmentCostPriceRows, setDevelopmentCostTables, developmentCostPriceRows, table, currencyName)}
                  <Box
                    id={table.id}
                    ref={(el) => (developmentCostTableRef.current[tableIndex] = el)}
                    maxWidth="100%"
                    overflowX="auto"
                  >
                    <Grid
                      templateColumns={`100px repeat(${getDynamicColumnCount(table, projectData?.projectStartDate)}, 150px)`}
                      gap={4}
                    >
                      <GridItem>
                        <Text fontWeight="bold">{t('features.InvestmentScreening.QuantityAndPrice.field.item')}</Text>
                      </GridItem>
                      {generateYearMonthCells(table, projectData?.projectStartDate, baseStartYear)}

                      {/* 金額行 */}
                      <GridItem>
                        <Text>{t('features.InvestmentScreening.QuantityAndPrice.field.amount')}　({currencyName || "--"})</Text>
                      </GridItem>
                      {generateGridItems(table, projectData?.projectStartDate, (monthData, year, monthKey, index, isDisabled) => (
                        <GridItem key={`price-${table.id}-${year}-${monthKey}`}>
                          <FormControl isInvalid={!!errors[`developmentCostTables-${table.id}-${year}-${monthKey}-price`]}>
                            <CustomNumberInput
                              value={monthData?.price || 0}
                              onChange={(value) =>
                                handleInputChange(table.id, year, monthKey, "price", value, setDevelopmentCostTables)
                              }
                              max={9999999999}
                              min={0}
                              decimalPlaces={4}
                              isDisabled={isDisabled}
                            />
                            {errors[`developmentCostTables-${table.id}-${year}-${monthKey}-price`] && (
                              <FormErrorMessage>
                                {errors[`developmentCostTables-${table.id}-${year}-${monthKey}-price`]}
                              </FormErrorMessage>
                            )}
                          </FormControl>
                        </GridItem>
                      ))}
                    </Grid>
                  </Box>
                </Box>
                {/* 合計金額 */}
                <Box p={4}>
                  <Text fontWeight="bold">{t('features.InvestmentScreening.QuantityAndPrice.field.totalAmount')}: {formatNumber(totalPrice, currencyName!, 3)} {currencyName}</Text>
                </Box>
                <Flex justify="flex-end" p={4} maxWidth="100%">
                  <IconButton
                    aria-label="Delete Development Cost Table"
                    icon={<RiDeleteBin6Fill />}
                    colorScheme="red"
                    onClick={() => deleteTable(table.id, TABLE_TYPES.DEVELOPMENT_COST_TABLES)}
                  />
                </Flex>
              </Box>
            );
          })
        }

        {/* 設備費回収テーブル */}
        {!isSimpleMode &&
          equipmentCostTables.map((table, tableIndex) => {
            const totalPrice = calculateTotalPrice(table.years, table.data);
            const currencyName = currencyData.find(currency => currency.currencyId === table.currencyId)?.currencyName;

            return (
              <Box
                key={table.id}
                id={table.id}
                maxW="79.5vw"
                mx="auto"
                borderWidth="1px"
                borderRadius="md"
                boxShadow="md"
                height="fit-content"
                mb={6}
              >
                <Box maxWidth="100%" p={4}>
                  <Flex justify="space-between" align="center">
                    <Flex align="center" gap={2}>
                      <Text fontWeight="bold">{t('features.InvestmentScreening.QuantityAndPrice.field.equipCostRecovery')}</Text>
                      <HelpIcon>
                        {t('features.InvestmentScreening.QuantityAndPrice.help.equipCostRecoveryHelp')}
                      </HelpIcon>
                    </Flex>
                    <Flex gap={2}>
                      <IconButton
                        aria-label="Remove year"
                        icon={<FaCircleMinus />}
                        onClick={() => removeYear(table.id, TABLE_TYPES.EQUIPMENT_COST_TABLES)}
                        colorScheme="blue"
                        isDisabled={true}
                      />
                      <IconButton
                        aria-label="Add year"
                        icon={<FaPlusCircle />}
                        onClick={() => addYear(table.id, TABLE_TYPES.EQUIPMENT_COST_TABLES)}
                        colorScheme="blue"
                        isDisabled={true}
                      />
                    </Flex>
                  </Flex>
                  <Box mb={4}>
                    <FormControl isInvalid={!!errors[`equipmentCostTables-${table.id}-equipmentCostName`]}>
                      <Input
                        placeholder={t('features.InvestmentScreening.QuantityAndPrice.field.equipCostName')}
                        value={table.equipmentCostName}
                        onChange={(e) => handleEquipmentCostNameChange(table.id, e.target.value)}
                        width="500px"
                        maxLength={50}
                      />
                      {errors[`equipmentCostTables-${table.id}-equipmentCostName`] && <FormErrorMessage>{errors[`equipmentCostTables-${table.id}-equipmentCostName`]}</FormErrorMessage>}
                    </FormControl>
                  </Box>

                  <Box mb={4}>
                    <Flex align="center" >
                      <Text width="220px" whiteSpace="nowrap">
                        <Flex align="center" gap={2}>
                          {LABEL_CURRENCY}
                          <CurrencyList currencyData={currencyData} />
                        </Flex>
                      </Text>
                      <FormControl isInvalid={!!errors[`equipmentCostCurrencyData-${table.id}`]}>
                        <Select
                          placeholder={LABEL_CURRENCY}
                          value={table.currencyId}
                          onChange={(e) => {
                            setEquipmentCostTables(prev =>
                              prev.map(t =>
                                t.id === table.id
                                  ? { ...t, currencyId: e.target.value }
                                  : t
                              )
                            );
                          }}
                          width="410px"
                        >
                          {currencyOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Select>
                        {errors[`equipmentCostCurrencyData-${table.id}`] && <FormErrorMessage>{errors[`equipmentCostCurrencyData-${table.id}`]}</FormErrorMessage>}
                      </FormControl>
                    </Flex>
                  </Box>

                  {/* 範囲入力 */}
                  {renderSection(t('features.InvestmentScreening.QuantityAndPrice.field.amount'), setEquipmentCostPriceRows, setEquipmentCostTables, equipmentCostPriceRows, table, currencyName)}
                  <Box
                    id={table.id}
                    ref={(el) => (equipmentCostTableRef.current[tableIndex] = el)}
                    maxWidth="100%"
                    overflowX="auto"
                  >
                    <Grid
                      templateColumns={`100px repeat(${getDynamicColumnCount(table, projectData?.projectStartDate)}, 150px)`}
                      gap={4}
                    >
                      <GridItem>
                        <Text fontWeight="bold">{t('features.InvestmentScreening.QuantityAndPrice.field.item')}</Text>
                      </GridItem>
                      {generateYearMonthCells(table, projectData?.projectStartDate, baseStartYear)}

                      {/* 金額行 */}
                      <GridItem>
                        <Text>{t('features.InvestmentScreening.QuantityAndPrice.field.amount')}　({currencyName || "--"})</Text>
                      </GridItem>
                      {generateGridItems(table, projectData?.projectStartDate, (monthData, year, monthKey, index, isDisabled) => (
                        <GridItem key={`price-${table.id}-${year}-${monthKey}`}>
                          <FormControl isInvalid={!!errors[`equipmentCostTables-${table.id}-${year}-${monthKey}-price`]}>
                            <CustomNumberInput
                              value={monthData?.price || 0}
                              onChange={(value) =>
                                handleInputChange(table.id, year, monthKey, "price", value, setEquipmentCostTables)
                              }
                              max={9999999999}
                              min={0}
                              decimalPlaces={4}
                              isDisabled={isDisabled}
                            />
                            {errors[`equipmentCostTables-${table.id}-${year}-${monthKey}-price`] && (
                              <FormErrorMessage>
                                {errors[`equipmentCostTables-${table.id}-${year}-${monthKey}-price`]}
                              </FormErrorMessage>
                            )}
                          </FormControl>
                        </GridItem>
                      ))}
                    </Grid>
                  </Box>
                </Box>
                {/* 合計金額 */}
                <Box p={4}>
                  <Text fontWeight="bold">{t('features.InvestmentScreening.QuantityAndPrice.field.totalAmount')}: {formatNumber(totalPrice, currencyName!, 3)} {currencyName}</Text>
                </Box>
                <Flex justify="flex-end" p={4} maxWidth="100%">
                  <IconButton
                    aria-label="Delete Equipment Cost Table"
                    icon={<RiDeleteBin6Fill />}
                    colorScheme="red"
                    onClick={() => deleteTable(table.id, TABLE_TYPES.EQUIPMENT_COST_TABLES)}
                  />
                </Flex>
              </Box>
            );
          })
        }
      </fieldset >
      {/* ここに自由記入欄を設ける */}
      {projectId && !isSimpleMode &&
        <Box
          maxW="79.5vw"
          mx="auto"
        >
          <Text>
            {t('features.InvestmentScreening.QuantityAndPrice.field.freeNotes')}
          </Text>
          <Flex align="center">
            <Textarea
              name="specificationAndPerformance"
              value={quantityPriceFreeText}
              onChange={(e) => setQuantityPriceFreeText(e.target.value)}
              placeholder={t('features.InvestmentScreening.QuantityAndPrice.field.freeNotes')}
              resize="none"
              width="100%"
              height={120}
            />
          </Flex>
        </Box>
      }
      {/* +Part No. ボタン */}
      {!isSimpleMode &&
        <fieldset disabled={!pagePermissions?.isEditable}>
          <Flex justify="flex-end" mt={6}>
            <HelpIcon>
              <Text>
                {t('features.InvestmentScreening.QuantityAndPrice.help.moldRecoveryProjectLine1')}
                {t('features.InvestmentScreening.QuantityAndPrice.help.moldRecoveryProjectLine2')}

                {t('features.InvestmentScreening.QuantityAndPrice.help.devCostRecoveryProjectLine1')}
                {t('features.InvestmentScreening.QuantityAndPrice.help.devCostRecoveryProjectLine2')}

                {t('features.InvestmentScreening.QuantityAndPrice.help.equipCostRecoveryProjectLine1')}
                {t('features.InvestmentScreening.QuantityAndPrice.help.equipCostRecoveryProjectLine2')}
                {t('features.InvestmentScreening.QuantityAndPrice.help.prototypeCostRecoveryNote')}
              </Text>
            </HelpIcon>
            <Button leftIcon={<FaPlusCircle />} colorScheme="blue" onClick={addQuantityAndPriceTable} ml="10px">
              Part No.
            </Button>
            <Button leftIcon={<FaPlusCircle />} colorScheme="blue" onClick={addMoldTable} ml="10px">
              {t('features.InvestmentScreening.QuantityAndPrice.field.moldRecoveryProject')}
            </Button>
            <Button leftIcon={<FaPlusCircle />} colorScheme="blue" onClick={addDevelopmentCostTable} ml="10px">
              {t('features.InvestmentScreening.QuantityAndPrice.field.devCostRecoveryProject')}
            </Button>
            <Button leftIcon={<FaPlusCircle />} colorScheme="blue" onClick={addEquipmentCostTable} ml="10px">
              {t('features.InvestmentScreening.QuantityAndPrice.field.equipCostRecoveryProject')}
            </Button>
          </Flex>
        </fieldset>
      }

      {/* 事業部確認、営業担当者確認ボタン */}
      < Flex justify="flex-end" mt={6} >
        <Box>
          <Button
            ml="10px"
            colorScheme="blue"
            name="companyDivision"
            onClick={(e) => handleModalOpen(t('features.InvestmentScreening.QuantityAndPrice.button.companyDivisionConfirm'), e)}
            isDisabled={!pagePermissions?.eabledCompanyDivision}
            ref={refs["companyDivision"]}
          >
            {t('features.InvestmentScreening.QuantityAndPrice.button.companyDivisionConfirm')}
          </Button>
          <ConfirmorList confirmorInfo={names["companyDivision"]} width={buttonWidths["companyDivision"]} />
        </Box>
        <Box>
          <Button
            ml="10px"
            colorScheme="blue"
            name="salesDivision"
            onClick={(e) => handleModalOpen(t('features.InvestmentScreening.QuantityAndPrice.button.salesDivisionConfirm'), e)}
            isDisabled={!pagePermissions?.eabledSalesDivision}
            ref={refs["salesDivision"]}
          >
            {t('features.InvestmentScreening.QuantityAndPrice.button.salesDivisionConfirm')}
          </Button>
          <ConfirmorList confirmorInfo={names["salesDivision"]} width={buttonWidths["salesDivision"]} />
        </Box>
      </Flex>
      {/* モーダル */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{modalTitle}</ModalHeader>
          <ModalBody overflowY="auto" maxHeight="60vh">
            {isSimpleMode ? (
              <Box>
                <Text mb={4}>{t('common.message.simpleModeNoConfirmContent')}</Text>
                {(projectData?.purpose === PurposeType.Rationalization.code || projectData?.purpose === PurposeType.UpdateAndOther.code) && (
                  <Box p={2}>
                    <Text fontWeight="bold">{t('features.InvestmentScreening.QuantityAndPrice.field.businessContinuity')}</Text>
                    <Text>
                      {businessContinuity === RADIO_VALUES.PART_INPUT
                        ? t('common.option.available')
                        : t('common.option.unavailable')}
                    </Text>
                  </Box>
                )}
              </Box>
            ) : (<>
              {/* PartNoごとの年間売上 */}
              <Text fontWeight="bold" mb={2}>{t('features.InvestmentScreening.QuantityAndPrice.field.annualSalesByPartNo')}</Text>
              {partNoTotals.map((item, index) => {
                const currencyName = selectedCurrency.find((c) => c.partNo === item.partNo)?.name ?? "";
                return (
                  <Box key={index} mb={4}>
                    <Text fontWeight="bold">{item.partNo || t('features.InvestmentScreening.QuantityAndPrice.field.notEntered')}</Text>
                    <Grid templateColumns="1fr 1fr" gap={1}>
                      <GridItem>
                        <Text>{t('features.InvestmentScreening.QuantityAndPrice.field.productSales')}</Text>
                      </GridItem>
                      <GridItem>
                        <Text textAlign="right" mr={12}>{formatNumber(item.salesTotal, currencyName, 3)} {currencyName}</Text>
                      </GridItem>
                      {item.moldTotal.greaterThan(0) && (
                        <>
                          <GridItem>
                            <Text>{t('features.InvestmentScreening.QuantityAndPrice.field.mold')}</Text>
                          </GridItem>
                          <GridItem>
                            <Text textAlign="right" mr={12}>{formatNumber(item.moldTotal, currencyName, 3)} {currencyName}</Text>
                          </GridItem>
                        </>
                      )}
                      {item.developmentCostTotal.greaterThan(0) && (
                        <>
                          <GridItem>
                            <Text>{t('features.InvestmentScreening.QuantityAndPrice.field.devCost')}</Text>
                          </GridItem>
                          <GridItem>
                            <Text textAlign="right" mr={12}>{formatNumber(item.developmentCostTotal, currencyName, 3)} {currencyName}</Text>
                          </GridItem>
                        </>
                      )}
                    </Grid>
                  </Box>
                )
              })}

              {/* 金型と開発費回収の合計 */}
              {moldTotals.length > 0 && (
                <>
                  <Text fontWeight="bold" mb={2}>{t('features.InvestmentScreening.QuantityAndPrice.field.moldRecovery')}</Text>
                  <Grid templateColumns="1fr 1fr" mb={4}>
                    {moldTotals.map((mold) => (
                      <React.Fragment key={mold.id}>
                        <GridItem>
                          <Text>{mold.name}</Text>
                        </GridItem>
                        <GridItem>
                          <Text textAlign="right" mr={12}>
                            {formatNumber(mold.total, currencyData.find(c => c.currencyId === mold.currencyId)?.currencyName ?? "", 3)} {currencyData.find(c => c.currencyId === mold.currencyId)?.currencyName ?? ""}
                          </Text>
                        </GridItem>
                      </React.Fragment>
                    ))}
                  </Grid>
                </>
              )}
              {developmentCostTotals.length > 0 && (
                <>
                  <Text fontWeight="bold" mb={2}>{t('features.InvestmentScreening.QuantityAndPrice.field.devCostRecovery')}</Text>
                  <Grid templateColumns="1fr 1fr" mb={4}>
                    {developmentCostTotals.map((developmentCost) => (
                      <React.Fragment key={developmentCost.id}>
                        <GridItem>
                          <Text>{developmentCost.name}</Text>
                        </GridItem>
                        <GridItem>
                          <Text textAlign="right" mr={12}>
                            {formatNumber(developmentCost.total, currencyData.find(c => c.currencyId === developmentCost.currencyId)?.currencyName ?? "", 3)} {currencyData.find(c => c.currencyId === developmentCost.currencyId)?.currencyName ?? ""}
                          </Text>
                        </GridItem>
                      </React.Fragment>
                    ))}
                  </Grid>
                </>
              )}
              {equipmentCostTotals.length > 0 && (
                <>
                  <Text fontWeight="bold" mb={2}>{t('features.InvestmentScreening.QuantityAndPrice.field.equipCostRecovery')}</Text>
                  <Grid templateColumns="1fr 1fr" mb={4}>
                    {equipmentCostTotals.map((equipmentCost) => (
                      <React.Fragment key={equipmentCost.id}>
                        <GridItem>
                          <Text>{equipmentCost.name}</Text>
                        </GridItem>
                        <GridItem>
                          <Text textAlign="right" mr={12}>
                            {formatNumber(equipmentCost.total, currencyData.find(c => c.currencyId === equipmentCost.currencyId)?.currencyName ?? "", 3)} {currencyData.find(c => c.currencyId === equipmentCost.currencyId)?.currencyName ?? ""}
                          </Text>
                        </GridItem>
                      </React.Fragment>
                    ))}
                  </Grid>
                </>
              )}
              {/* プロジェクト合計 */}
              {/* <Grid templateColumns="1fr 1fr" gap={1} mb={4}>
              <GridItem>
                <Text fontWeight="bold" mr={4}>プロジェクト合計</Text>
              </GridItem>
              <GridItem>
                <Text fontSize="lg" textAlign="right" mr={12}>{formatNumber(grandTotal, selectedCurrency[0]?.name)} {selectedCurrency[0]?.name}</Text>
              </GridItem>
            </Grid> */}
            </>)}
          </ModalBody>
          <ModalFooter position="sticky" bottom="0" bg="white" zIndex="1">
            <Button colorScheme="blue" mr={3} onClick={(e) => handleConfirm(e)}>
              {t('common.button.confirmAction')}
            </Button>
            <Button colorScheme="gray" onClick={onClose}>
              {t('common.button.cancel')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal >
    </Box >
  );
});

export default QuantityAndPrice;
